import { areEqualEnums, dateOnly_FROM_date, DateTime, dateTime, enumByIdentifier, enums, enumsWith, enumWith, equalsToEnum, error, items, money_FROM_number, optionalDateTime, stringifyReplacer, unexpectedCodePath, WHEN_WITHIN, year_FROM_date } from "@/fx/data-helpers";
import { CLP } from "./claims-process";
import { Id } from "@/fx/Id";
import { validator } from "@/fx/validation";
import { addDays, differenceInBusinessDays } from "date-fns";
import assert from "assert";

import { FILE_NAME_FORMAT, fileIdentifier, fileInfo, FileInfo, sanitizedStringForFilename } from "@/common/file-info";
import { CMD } from "./claims-commands";
import { isDefined } from "@/specs/validation";

import { RLT } from "./role-types";
import { SYS } from "./system";
import { demoClaim } from "./claims-stage-testing";
import { section } from "@/fx/forms/mudroom";
import { ReminderStyle } from "@/ui/controls/form";
import { type App as App_type, App as App_, app as app_, apps as apps_, currentApp as currentApp_, currentAppInfo as currentAppInfo_ } from "./claims-apps";
import bag from "@/../public/images/bag.svg";
import can from "@/../public/images/can.svg";
import bottle from "@/../public/images/bottle.svg";
import freeze_pop from "@/../public/images/freeze_pop.svg";


export namespace CLM {

  export const EXTENSION_ONE_DEFAULTS: ClaimExtensionOne = {
    __stageTesting_variant: undefined,
    __stageTesting_stage:   undefined,

    legalStage:             1,

    destructorType:         undefined,
    destructionCompanyName: "",
    streetAddress:          "",
    city:                   "",
    state:                  "",
    zipCode:                "",
    destructionMethod:      "",

    totalAlcoholicCases: "",
    totalBarrels:        "",
    totalTaxAmount:      "",
    productionLocations: [],

    dateFaxedToTTB: "",
    noticeOfIntent: undefined,
    faxCoverSheet:  undefined,

    ttbApprovalDeadline:    "",
    ttbApprovalDate:        "",
    ttbApproval:            undefined,
    approvedForDestruction: false,

    destructionApprovalDate: "",
    destructionApproval:     undefined,

    earliestDestructionDate: "",
    casesDestroyed:          "",
    reviewNotes:             "",
    // affidavits:           [],
    affidavit1:              undefined,
    affidavit2:              undefined,
    affidavit3:              undefined,
    affidavit4:              undefined,
    affidavit5:              undefined,

    affidavitComplete: false,
  };

  export const injectedClaim = (ctx: () => ClaimCleanupContext) => (
    claim: RawClaim
  ): RawClaim => {
    assert(claim !== undefined);
    assert(claim.extensionOne === undefined);

    const demo    = () => demoClaim(ctx)(claim);
    const regular = () => ({
      ...claim,
      extensionOne: EXTENSION_ONE_DEFAULTS,
    });

    return claim.id === "1" ? demo() : regular();
  }

// #region App

  export type  App            = App_type;
  export const App            = App_;
  export const apps           = apps_;
  export const app            = app_;
  export const currentApp     = currentApp_;
  export const currentAppInfo = currentAppInfo_;

// #endregion
// #region Experience

  export type Experience = {
    identifier: string;
    name:       string;
  }
  export const Experience = enums({
    NOVICE: { identifier: "NOVICE", name: "Novice" } as Experience,
    PRO:    { identifier: "PRO",    name: "Pro"    } as Experience,
  })
  export const experiences = items<Experience>(Experience);
  export const experience  = enumByIdentifier(experiences);

// #endregion

export const DEFAULT_CREDIT_PERCENTAGE = 50;

// #region Claim Type

export type ClaimType = {
  identifier: string;
  name:       string;
}
export const ClaimType = enums({
  DESTRUCTION: { identifier: "DESTRUCTION", name: "Destruction" } as ClaimType,
  OVERAGE:     { identifier: "OVERAGE",     name: "Overage"     } as ClaimType,
  SHORTAGE:    { identifier: "SHORTAGE",    name: "Shortage"    } as ClaimType,
})
export const claimTypes = items<ClaimType>(ClaimType);
export const claimType  = enumByIdentifier(claimTypes);

// #endregion
// #region SAP Order Timing

export type SapOrderTiming = {
  identifier: string;
  name:       string;
}
export const SapOrderTiming = enums({
  AT_CREATION:      { identifier: "AT_CREATION",      name: "At Creation"      } as SapOrderTiming,
  AFTER_ACCEPTANCE: { identifier: "AFTER_ACCEPTANCE", name: "After Acceptance" } as SapOrderTiming,
  BEFORE_CREDIT:    { identifier: "BEFORE_CREDIT",    name: "Before Credit"    } as SapOrderTiming,
})
export const sapOrderTimings = items<SapOrderTiming>(SapOrderTiming);
export const sapOrderTiming  = enumByIdentifier(sapOrderTimings);

// #endregion
// #region Claim Reason

export type ClaimReason = {
  identifier:  string;
  name:        string;
  claimType:   ClaimType;
  description: string;
}
export const ClaimReason = enums({
  OUT_OF_CODE:   { identifier: "OUT_OF_CODE",    name: "Out-of-Code",    claimType: ClaimType.DESTRUCTION, description: "Out-of-code product"     } as ClaimReason,
  DAMAGED:       { identifier: "DAMAGED",        name: "Damaged",        claimType: ClaimType.DESTRUCTION, description: "Product is/was damaged"  } as ClaimReason,
  QUALITY_ALERT: { identifier: "QUALITY_ALERT",  name: "Quality Alert",  claimType: ClaimType.DESTRUCTION, description: "A product quality alert" } as ClaimReason,
  MISSHIPMENT:   { identifier: "MISSHIPMENT",    name: "Misshipment",    claimType: ClaimType.DESTRUCTION, description: "A product misshipment"   } as ClaimReason,
  SHORT_CODED:   { identifier: "SHORT_CODED",    name: "Short-coded",    claimType: ClaimType.DESTRUCTION, description: "Short-coded product"     } as ClaimReason,
  OVERSHIPMENT:  { identifier: "OVERSHIPMENT",   name: "Over-shipment",  claimType: ClaimType.OVERAGE,     description: "An over-shipment"        } as ClaimReason,
  UNDERSHIPMENT: { identifier: "UNDERSHIPMENT",  name: "Under-shipment", claimType: ClaimType.SHORTAGE,    description: "An under-shipment"       } as ClaimReason,
})
export const claimReasons = items<ClaimReason>(ClaimReason);
export const claimReason  = enumByIdentifier(claimReasons);

// #endregion
// #region Beverage Type

  export type BeverageType = {
    identifier:  string;
    name:        string;
    productType: string;
    abbreviation?: string;
  }
  function beverageType_(identifier: string, name: string, abbreviation?: string): BeverageType {
    return {
      identifier,
      name,
      productType: name + " Cases",
    };
  }
  export const BeverageType = enums({
    BEER_FMB:      beverageType_("BEER_FMB",      "Beer/FMB"     ),
    CIDER:         beverageType_("CIDER",         "Cider"	       ),
    SPIRITS:       beverageType_("SPIRITS",       "Spirits"      ),
    NON_ALCOHOLIC: beverageType_("NON_ALCOHOLIC", "Non-Alcoholic"),
  })
  export const beverageTypes      = items<BeverageType>(BeverageType);
  export const beverageType       = enumByIdentifier(beverageTypes);
  export const beverageType_WITH  = enumWith(beverageTypes);
  export const beverageTypes_WITH = enumsWith(beverageTypes);

// #endregion
// #region Container

export type Container = {
  identifier: string;
  name:       string;
  icon:       string;
  order:      number;
}
export const Container = enums({
  BOTTLES:      { identifier: "BOTTLES",      name: "Bottles"      , icon: bottle    , order: 1 } as Container,
  CANS:         { identifier: "CANS",         name: "Cans"         , icon: can       , order: 2 } as Container,
  BAG:          { identifier: "BAG",          name: "Bag"          , icon: bag       , order: 3 } as Container,
  FREEZE_POPS:  { identifier: "FREEZE_POPS",  name: "Freeze Pops"  , icon: freeze_pop, order: 4 } as Container,
})
export const containers      = items<Container>(Container);
export const container       = enumByIdentifier(containers);
export const container_WITH  = enumWith(containers);
export const containers_WITH = enumsWith(containers);

// #endregion
// #region Destruction Reason

export type DestructionReason = {
  identifier:              string;
  name:                    string;
  defaultCreditPercentage: number;
}
export const DestructionReason = enums({
  OUT_OF_CODE:      { identifier: "OUT_OF_CODE",      name: "Out of Code",      defaultCreditPercentage: 50  } as DestructionReason,
  OUT_OF_CODE_CLUB: { identifier: "OUT_OF_CODE_CLUB", name: "Out of Code Club", defaultCreditPercentage: 50 } as DestructionReason,
})
export const destructionReasons = items<DestructionReason>(DestructionReason);
export const destructionReason  = enumByIdentifier(destructionReasons);

export const DEFAULT_DESTRUCTION_REASON = DestructionReason.OUT_OF_CODE;

// #endregion
// #region Claim Verification Method

export type ClaimVerificationMethod = {
  identifier: string;
  name:       string;
}
export const ClaimVerificationMethod = enums({
  IN_PERSON:         { identifier: "IN_PERSON",         name: "In Person"         } as ClaimVerificationMethod,
  BY_PHOTO_OR_VIDEO: { identifier: "BY_PHOTO_OR_VIDEO", name: "By Photo or Video" } as ClaimVerificationMethod,
})
export const claimVerificationMethods = items<ClaimVerificationMethod>(ClaimVerificationMethod);
export const claimVerificationMethod  = enumByIdentifier(claimVerificationMethods);

// #endregion
// #region Manager Level

export type ManagerLevel = {
  identifier:         string;
  name:               string;
  firstLevelApprover: boolean;
  upperLevelApprover: boolean;
}
export const ManagerLevel = enums({
  MARKET_MANAGER:                                    { identifier: "MARKET_MANAGER",                                    name: "Market Manager"                                   , firstLevelApprover: true,  upperLevelApprover: false } as ManagerLevel,
  DISTRICT_MANAGER:                                  { identifier: "DISTRICT_MANAGER",                                  name: "District Manager"                                 , firstLevelApprover: true,  upperLevelApprover: false } as ManagerLevel,
  AREA_MANAGER:                                      { identifier: "AREA_MANAGER",                                      name: "Area Manager"                                     , firstLevelApprover: true,  upperLevelApprover: false } as ManagerLevel,
  REGIONAL_MANAGER:                                  { identifier: "REGIONAL_MANAGER",                                  name: "Regional Manager"                                 , firstLevelApprover: true,  upperLevelApprover: true  } as ManagerLevel,
  DIVISION_DIRECTOR:                                 { identifier: "DIVISION_DIRECTOR",                                 name: "Division Director"                                , firstLevelApprover: true,  upperLevelApprover: true  } as ManagerLevel,
  CHIEF_SALES_OFFICER:                               { identifier: "CHIEF_SALES_OFFICER",                               name: "Chief Sales Officer"                              , firstLevelApprover: false, upperLevelApprover: true  } as ManagerLevel,
  DIVISIONAL_ANALYST:                                { identifier: "DIVISIONAL_ANALYST",                                name: "Divisional Analyst"                               , firstLevelApprover: true,  upperLevelApprover: false } as ManagerLevel,
  CUSTOMER_AND_SUPPLY_LOGISTICS_MANAGEMENT_DIRECTOR: { identifier: "CUSTOMER_AND_SUPPLY_LOGISTICS_MANAGEMENT_DIRECTOR", name: "Customer and Supply Logistics Management Director", firstLevelApprover: false, upperLevelApprover: true  } as ManagerLevel,
})
export const managerLevels = items<ManagerLevel>(ManagerLevel);
export const managerLevel  = enumByIdentifier(managerLevels);

// #region Destruction Company Type

export type DestructionCompanyType = {
  identifier: string;
  name:       string;
  alias:      string;
}
export const DestructionCompanyType = enums({
  WHOLESALER:  { identifier: "WHOLESALER",   name: "Wholesaler",                  alias: "Onsite (by distributor)"  } as DestructionCompanyType,
  THIRD_PARTY: { identifier: "THIRD_PARTY",  name: "Another company (3rd party)",                                   } as DestructionCompanyType,
})
export const destructionCompanyTypes = items<DestructionCompanyType>(DestructionCompanyType);
export const destructionCompanyType  = enumByIdentifier(destructionCompanyTypes);

// #endregion
// #region Production Location

export type ProductionLocation = {
  identifier: string;
  name:       string;
}
export const ProductionLocation = enums({
  "BR-PA-SAM-15000": { identifier: "BR-PA-SAM-15000", name: "American Craft Brewery LLC, Breinigsville, PA  (BR-PA-SAM-15000)" } as ProductionLocation,
  "BR-OH-SAM-1":     { identifier: "BR-OH-SAM-1",     name: "American Craft Brewery LLC, Cincinnati, OH (BR-OH-SAM-1)"         } as ProductionLocation,
  "BR-PA-BOS-15000": { identifier: "BR-PA-BOS-15000", name: "American Craft Brewery LLC, Latrobe, PA (BR-PA-BOS-15000)"        } as ProductionLocation,
  "BR-TN-21054":     { identifier: "BR-TN-21054",     name: "American Craft Brewery LLC, Memphis, TN (BR-TN-21054)"            } as ProductionLocation,
  "BR-CA-21278":     { identifier: "BR-CA-21278",     name: "American Craft Brewery LLC, Irwindale, CA (BR-CA-21278)"          } as ProductionLocation,
  "BR-DE-21015":     { identifier: "BR-DE-21015",     name: "Dogfish Head Craft Brewery LLC, Milton, DE (BR-DE-21015)"         } as ProductionLocation,
  "BR-CA-AME-15003": { identifier: "BR-CA-AME-15003", name: "American Craft Brewery LLC, Los Angeles, CA (BR-CA-AME-15003)"    } as ProductionLocation,
  "BR-AZ-20145":     { identifier: "BR-AZ-20145",     name: "RAUCH North America, Inc. (BR-AZ-20145)"                          } as ProductionLocation,
})
export const productionLocations = items<ProductionLocation>(ProductionLocation);
export const productionLocation  = enumByIdentifier(productionLocations);

// #endregion
// #region Aod Issue

export type AodIssue = {
  identifier: string;
  name:       string;
}
export const AodIssue = enums({
  "INVALID_NOI":                  { identifier: "INVALID_NOI",                  name: "The claim’s NOI Number is missing or incorrect."       } as AodIssue,
  "INVALID_DESTRUCTION_COMPANY":  { identifier: "INVALID_DESTRUCTION_COMPANY",  name: "The destruction company does not match the NOI."       } as AodIssue,
  "INVALID_DESTRUCTION_LOCATION": { identifier: "INVALID_DESTRUCTION_LOCATION", name: "The destruction location does not match the NOI."      } as AodIssue,
  "INVALID_SIGNATURES":           { identifier: "INVALID_SIGNATURES",           name: "Signatures do not align with the destruction company." } as AodIssue,
  "OTHER":                        { identifier: "OTHER",                        name: "Other (see Affidavit Review Notes)."                   } as AodIssue,
})
export const aodIssues = items<AodIssue>(AodIssue);
export const aodIssue  = enumByIdentifier(aodIssues);

// #endregion
// #region State

export type State = {
  identifier: string;
  name:       string;
}
export const State = enums({
  AL: { identifier: "AL", name: "Alabama"        } as State,
  AK: { identifier: "AK", name: "Alaska"         } as State,
  AZ: { identifier: "AZ", name: "Arizona"        } as State,
  AR: { identifier: "AR", name: "Arkansas"       } as State,
  CA: { identifier: "CA", name: "California"     } as State,
  CO: { identifier: "CO", name: "Colorado"       } as State,
  CT: { identifier: "CT", name: "Connecticut"    } as State,
  DE: { identifier: "DE", name: "Delaware"       } as State,
  FL: { identifier: "FL", name: "Florida"        } as State,
  GA: { identifier: "GA", name: "Georgia"        } as State,
  HI: { identifier: "HI", name: "Hawaii"         } as State,
  ID: { identifier: "ID", name: "Idaho"          } as State,
  IL: { identifier: "IL", name: "Illinois"       } as State,
  IN: { identifier: "IN", name: "Indiana"        } as State,
  IA: { identifier: "IA", name: "Iowa"           } as State,
  KS: { identifier: "KS", name: "Kansas"         } as State,
  KY: { identifier: "KY", name: "Kentucky"       } as State,
  LA: { identifier: "LA", name: "Louisiana"      } as State,
  ME: { identifier: "ME", name: "Maine"          } as State,
  MD: { identifier: "MD", name: "Maryland"       } as State,
  MA: { identifier: "MA", name: "Massachusetts"  } as State,
  MI: { identifier: "MI", name: "Michigan"       } as State,
  MN: { identifier: "MN", name: "Minnesota"      } as State,
  MS: { identifier: "MS", name: "Mississippi"    } as State,
  MO: { identifier: "MO", name: "Missouri"       } as State,
  MT: { identifier: "MT", name: "Montana"        } as State,
  NE: { identifier: "NE", name: "Nebraska"       } as State,
  NV: { identifier: "NV", name: "Nevada"         } as State,
  NH: { identifier: "NH", name: "New Hampshire"  } as State,
  NJ: { identifier: "NJ", name: "New Jersey"     } as State,
  NM: { identifier: "NM", name: "New Mexico"     } as State,
  NY: { identifier: "NY", name: "New York"       } as State,
  NC: { identifier: "NC", name: "North Carolina" } as State,
  ND: { identifier: "ND", name: "North Dakota"   } as State,
  OH: { identifier: "OH", name: "Ohio"           } as State,
  OK: { identifier: "OK", name: "Oklahoma"       } as State,
  OR: { identifier: "OR", name: "Oregon"         } as State,
  PA: { identifier: "PA", name: "Pennsylvania"   } as State,
  RI: { identifier: "RI", name: "Rhode Island"   } as State,
  SC: { identifier: "SC", name: "South Carolina" } as State,
  SD: { identifier: "SD", name: "South Dakota"   } as State,
  TN: { identifier: "TN", name: "Tennessee"      } as State,
  TX: { identifier: "TX", name: "Texas"          } as State,
  UT: { identifier: "UT", name: "Utah"           } as State,
  VT: { identifier: "VT", name: "Vermont"        } as State,
  VA: { identifier: "VA", name: "Virginia"       } as State,
  WA: { identifier: "WA", name: "Washington"     } as State,
  WV: { identifier: "WV", name: "West Virginia"  } as State,
  WI: { identifier: "WI", name: "Wisconsin"      } as State,
  WY: { identifier: "WY", name: "Wyoming"        } as State,
})
export const states = items<State>(State);
export const state  = enumByIdentifier(states);

// #endregion

// #region Tax Credit Eligibility

export type TaxCreditEligibility = {
  identifier:    string;
  name:          string;
  style?:        ReminderStyle;
}
export const TaxCreditEligibility = enums({
  PENDING:                    { identifier: "PENDING"                   , name: "Tax credit eligibility pending."                              , style: "info"    } as TaxCreditEligibility,
  TAX_RETURN_CREDIT_ELIGIBLE: { identifier: "TAX_RETURN_CREDIT_ELIGIBLE", name: "Claim is eligible for excise tax return credit."              , style: "info"    } as TaxCreditEligibility,
  MAY_BE_ELIGIBLE:            { identifier: "MAY_BE_ELIGIBLE"           , name: "Claim may be eligible for tax credit pending further review." , style: "warning" } as TaxCreditEligibility,
  NOT_ELIGIBLE:               { identifier: "NOT_ELIGIBLE"              , name: "Claim is not eligible for tax credit."                                           } as TaxCreditEligibility,
})
export const taxCreditEligibilities = items<TaxCreditEligibility>(TaxCreditEligibility);
export const taxCreditEligibility   = enumByIdentifier(taxCreditEligibilities);

// #endregion

// #region Tax Credit Eligibility Risks

export type TaxCreditEligibilityRisk = {
  identifier: string;
  name:      string;
}
export const TaxCreditEligibilityRisk = enums({
  DESTROYED_BEFORE_TTB_SUBMISSION:                    { identifier: "DESTROYED_BEFORE_TTB_SUBMISSION",                    name: "Wholesaler destroyed product before TTB submission."                                             } as TaxCreditEligibilityRisk,
  DESTROYED_BEFORE_TTB_APPROVAL:                      { identifier: "DESTROYED_BEFORE_TTB_APPROVAL"  ,                    name: "Wholesaler destroyed product before TTB approval."                                               } as TaxCreditEligibilityRisk,
  DESTROYED_BEFORE_TTB_APPROVAL_DEADLINE:             { identifier: "DESTROYED_BEFORE_TTB_APPROVAL_DEADLINE"  ,           name: "Wholesaler destroyed product before TTB approval deadline."                                      } as TaxCreditEligibilityRisk,
  NOI_SUBMISSION_SKIPPED:                             { identifier: "NOI_SUBMISSION_SKIPPED"         ,                    name: "NOI submission to TTB was skipped."                                                              } as TaxCreditEligibilityRisk,
  LATE_WHOLESALER_CREDIT:                             { identifier: "LATE_WHOLESALER_CREDIT"         ,                    name: "Wholesaler credited more than 30 days after destruction."                                        } as TaxCreditEligibilityRisk,
  INVALID_AODS:                                       { identifier: "INVALID_AODS"                   ,                    name: "AODs are incomplete and/or contain conflicting information."                                     } as TaxCreditEligibilityRisk,
  TTB_SKIPPED_DESTROYED_BEFORE_TTB_APPROVAL_DEADLINE: { identifier: "TTB_SKIPPED_DESTROYED_BEFORE_TTB_APPROVAL_DEADLINE", name: "Skipped waiting for TTB approval and wholesaler destroyed product before TTB approval deadline." } as TaxCreditEligibilityRisk,
})
export const taxCreditEligibilityRisks = items<TaxCreditEligibilityRisk>(TaxCreditEligibilityRisk);
export const taxCreditEligibilityRisk  = enumByIdentifier(taxCreditEligibilityRisks);

// #endregion

// #region Key Moments

export type KeyMoment = {
  identifier: string;
  title:      string;
}
export const KeyMoment = enums({
  SUBMITTED:                      { identifier: "SUBMITTED"                     , title: "Submitted"                      } as KeyMoment,
  SENT_BACK:                      { identifier: "SENT_BACK"                     , title: "Sent Back"                      } as KeyMoment,
  ACCEPTED:                       { identifier: "ACCEPTED"                      , title: "Accepted"                       } as KeyMoment,
  CLAIM_APPROVAL_CONFIRMED:       { identifier: "CLAIM_APPROVAL_CONFIRMED"      , title: "Claim Approval Confirmed"       } as KeyMoment,
  SAP_ORDER_CREATED:              { identifier: "SAP_ORDER_CREATED"             , title: "SAP Order Created"              } as KeyMoment,
  FIRST_LEVEL_APPROVED:           { identifier: "FIRST_LEVEL_APPROVED"          , title: "First Level Approved"           } as KeyMoment,
  TTB_SUBMISSION_CONFIRMED:       { identifier: "TTB_SUBMISSION_CONFIRMED"      , title: "TTB Submission Confirmed"       } as KeyMoment,
  DESTRUCTION_APPROVAL_CONFIRMED: { identifier: "DESTRUCTION_APPROVAL_CONFIRMED", title: "Destruction Approval Confirmed" } as KeyMoment,
  DESTRUCTION_CONFIRMED:          { identifier: "DESTRUCTION_CONFIRMED"         , title: "Destruction Confirmed"          } as KeyMoment,
  SAP_ENTRY_CONFIRMED:            { identifier: "SAP_ENTRY_CONFIRMED"           , title: "SAP Entry Confirmed"            } as KeyMoment,
  CLOSED:                         { identifier: "CLOSED"                        , title: "Closed"                         } as KeyMoment,
  DISCARD_KEY_MOMENT:             { identifier: "DISCARD_KEY_MOMENT"            , title: "Discard Key Moment"             } as KeyMoment
})
export const keyMoments = items<KeyMoment>(KeyMoment);
export const keyMoment  = enumByIdentifier(keyMoments);

// #endregion

// #region IssueType

export type IssueType = {
  identifier: string;
  name:       string;
}
export const IssueType = enums({
  APP__CLAIM_INFORMATION_ISSUE: { identifier: "APP__CLAIM_INFORMATION_ISSUE", name: "Claim information issue in this app" } as IssueType,
  SAP__ACCOUNTING_DATA_ERROR:   { identifier: "SAP__ACCOUNTING_DATA_ERROR",   name: "Accounting data error in SAP"        } as IssueType,
  SAP__CLAIM_DATA_ENTRY_ERROR:  { identifier: "SAP__CLAIM_DATA_ENTRY_ERROR",  name: "Claim data entry error in SAP"       } as IssueType,
})
export const issueTypes = items<IssueType>(IssueType);
export const issueType  = enumByIdentifier(issueTypes);

// #endregion

// #region UserGroup

export type UserGroup = {
  identifier: string;
  name:       string;
  roles:      CLP.ActorType[];
}
export const UserGroup = enums({
  BBC__WHOLESALER_CREDIT_ACCOUNTING: { identifier: "BBC__WHOLESALER_CREDIT_ACCOUNTING", name: "Accounting",   roles: [CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT, CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT_ASSISTANT, CLP.ActorType.ACCOUNTS_RECEIVABLE_SUPERVISOR] } as UserGroup,
  BBC__LOGISTICS:                    { identifier: "BBC__LOGISTICS",                    name: "Logistics" ,   roles: [CLP.ActorType.RETURNS_COORDINATOR                                                                                                             ] } as UserGroup,
  BBC__APPROVERS:                    { identifier: "BBC__APPROVERS",                    name: "Approvers" ,   roles: [CLP.ActorType.BBC_APPROVER                                                                                                                    ] } as UserGroup,
  DISTRIBUTORS:                      { identifier: "DISTRIBUTORS",                      name: "Distributors", roles: [CLP.ActorType.DISTRIBUTOR                                                                                                                     ] } as UserGroup,
})
export const userGroups = items<UserGroup>(UserGroup);
export const userGroup  = enumByIdentifier(userGroups);

// #endregion

// #region Brand Family
export type BrandFamily = {
  identifier: string,
  name:       string
};
const brandFamily_ = (identifier: string): BrandFamily => {
  return { identifier, name: identifier };
}
export function brandFamilies_FROM_products({ products}: { products: CLM.Product[] }): BrandFamily[] {
  const featuredBrandFamilies = new Set(products.filter(_ => _.featured).map(_ => _.brandFamily));
  return [...new Set(products.map(_ => _.brandFamily))].sort().sort(
    (a, b) => (featuredBrandFamilies.has(b) ? 1 : 0) - (featuredBrandFamilies.has(a) ? 1 : 0)
  ).map(identifier => brandFamily_(identifier));
}
export const productWithBrandFamily = (...families: BrandFamily[]) => (product: CLM.Product) => {
  return families.some(_ => _.identifier === product.brandFamily);
};

// #endregion

// #region Container

export function containers_FROM_products({ products}: { products: CLM.Product[] }): Container[] {
  return [...new Set(products.map(_ => _.packaging_container))].sort((a, b) => a.order - b.order);
}
export const productWithContainer = (...containers: Container[]) => (product: CLM.Product) => {
  return containers.some(_ => _.identifier === product.packaging_container.identifier);
};

// #endregion

// #region Package

export type Package = {
  identifier: string,
  name:       string
};
const package_ = (identifier: string): Package => {
  return { identifier, name: identifier };
}
export const packages_FROM_products = ({ products}: { products: CLM.Product[] }): Package[] => {
  return [...new Set(products.map(_ => _.packaging_type))].sort().map(
    identifier => package_(identifier)
  );
}
export const productWithPackage = (...packages: Package[]) => (product: CLM.Product) => {
  return packages.some(_ => _.identifier === product.packaging_type);
}

// #endregion
// #region Configuration

export type ClaimCreatorsConfig = {
  claimType:     CLM.ClaimType;
  claimReason:   CLM.ClaimReason;
  itemized:      boolean;
  claimCreators: RLT.RoleType[];
}
const claimCreatorsConfigValidator = validator((_: ClaimCreatorsConfig) => {
  const { FOR } = objectValidator(_);
  return ({
      a1: FOR(_ => _.claimType    ).ALWAYS_IS_DEFINED,
      a2: FOR(_ => _.claimReason  ).ALWAYS_IS_DEFINED,
      a3: FOR(_ => _.claimReason  ).WHEN_DEFINED_THEN(claimReason => claimReason.claimType === _.claimType),

      b1: FOR(_ => _.itemized     ).ALWAYS_IS_DEFINED,

      c1: FOR(_ => _.claimCreators).ALWAYS_IS_DEFINED,
      c2: FOR(_ => _.claimCreators).IS_NON_EMPTY_ARRAY(),
  })
});
const claimCreatorsConfigErrors   = (_: ClaimCreatorsConfig) => claimCreatorsConfigValidator.validationErrors(_);
const isValidClaimCreatorsConfig  = (_: ClaimCreatorsConfig) => claimCreatorsConfigValidator.isValid(_);
const claimCreatorsConfigsValidator = validator((_: ClaimCreatorsConfig[]) => {
  const { FOR } = arrayValidator(_, _ => _.claimType, _ => _.claimReason);
  return ({
      a1: FOR(_ => _.itemized     ).IS_UNIQUE(),

      b1: FOR(_ => _.claimCreators).HAS_UNIQUE(),
  })
});
const claimCreatorsConfigsErrors   = (_: ClaimCreatorsConfig[]) => claimCreatorsConfigsValidator.validationErrors(_);
const validateClaimCreatorsConfigs = (_: ClaimCreatorsConfig[]) => {
  const objectValidationErrors = _
    .filter(_ => !isValidClaimCreatorsConfig(_))
    .map   (_ => ({ config: _, errors: claimCreatorsConfigErrors(_) }));
  assert(objectValidationErrors.length === 0, `Object errors found in defined claim creators configurations with following error codes. ${JSON.stringify(objectValidationErrors, stringifyReplacer, 2)}`);
  const arrayValidationErrors = claimCreatorsConfigsErrors(_);
  assert(arrayValidationErrors.length === 0, `Array errors found in defined claim creators configurations with following error codes. ${JSON.stringify(arrayValidationErrors, stringifyReplacer, 2)}`);
  return _;
}

export type SapOrderTimingConfig = {
  claimType:      CLM.ClaimType;
  claimReason:    CLM.ClaimReason;
  itemized:       boolean;
  sapOrderTiming: CLM.SapOrderTiming;
}
const sapOrderTimingConfigValidator = validator((_: SapOrderTimingConfig) => {
  const { FOR } = objectValidator(_);
  return ({
      a1: FOR(_ => _.claimType     ).ALWAYS_IS_DEFINED,
      a2: FOR(_ => _.claimReason   ).ALWAYS_IS_DEFINED,
      a3: FOR(_ => _.claimReason   ).WHEN_DEFINED_THEN(claimReason => claimReason.claimType === _.claimType),

      b1: FOR(_ => _.itemized      ).ALWAYS_IS_DEFINED,

      c1: FOR(_ => _.sapOrderTiming).ALWAYS_IS_DEFINED,
  })
});
const sapOrderTimingConfigErrors   = (_: SapOrderTimingConfig) => sapOrderTimingConfigValidator.validationErrors(_);
const isValidSapOrderTimingConfig  = (_: SapOrderTimingConfig) => sapOrderTimingConfigValidator.isValid(_);
const sapOrderTimingConfigsValidator = validator((_: SapOrderTimingConfig[]) => {
  const { KEYS_ARE_UNIQUE } = arrayValidator(_, _ => _.claimType, _ => _.claimReason, _ => _.itemized);
  return ({
      a1: KEYS_ARE_UNIQUE(),
  })
});
const sapOrderTimingConfigsErrors   = (_: SapOrderTimingConfig[]) => sapOrderTimingConfigsValidator.validationErrors(_);
const validateSapOrderTimingConfigs = (_: SapOrderTimingConfig[]) => {
  const objectValidationErrors = _
    .filter(_ => !isValidSapOrderTimingConfig(_))
    .map   (_ => ({ config: _, errors: sapOrderTimingConfigErrors(_) }));
  assert(objectValidationErrors.length === 0, `Object errors found in defined SAP order timing configurations with following error codes. ${JSON.stringify(objectValidationErrors, stringifyReplacer, 2)}`);
  const arrayValidationErrors = sapOrderTimingConfigsErrors(_);
  assert(arrayValidationErrors.length === 0, `Array errors found in defined SAP order timing configurations with following error codes. ${JSON.stringify(arrayValidationErrors, stringifyReplacer, 2)}`);
  return _;
}

export type InvolvesTaxCreditConfig = {
  claimType:         CLM.ClaimType;
  claimReason:       CLM.ClaimReason;
  involvesTaxCredit: boolean;
}
const involvesTaxCreditConfigValidator = validator((_: InvolvesTaxCreditConfig) => {
  const { FOR } = objectValidator(_);
  return ({
      a1: FOR(_ => _.claimType        ).ALWAYS_IS_DEFINED,
      a2: FOR(_ => _.claimReason      ).ALWAYS_IS_DEFINED,
      a3: FOR(_ => _.claimReason      ).WHEN_DEFINED_THEN(claimReason => claimReason.claimType === _.claimType),

      b1: FOR(_ => _.involvesTaxCredit).ALWAYS_IS_DEFINED,
  })
});
const involvesTaxCreditConfigErrors   = (_: InvolvesTaxCreditConfig) => involvesTaxCreditConfigValidator.validationErrors(_);
const isValidInvolvesTaxCreditConfig  = (_: InvolvesTaxCreditConfig) => involvesTaxCreditConfigValidator.isValid(_);
const involvesTaxCreditConfigsValidator = validator((_: InvolvesTaxCreditConfig[]) => {
  const { KEYS_ARE_UNIQUE } = arrayValidator(_, _ => _.claimType, _ => _.claimReason);
  return ({
      a1: KEYS_ARE_UNIQUE(),
  })
});
const involvesTaxCreditConfigsErrors   = (_: InvolvesTaxCreditConfig[]) => involvesTaxCreditConfigsValidator.validationErrors(_);
const validateInvolvesTaxCreditConfigs = (_: InvolvesTaxCreditConfig[]) => {
  const objectValidationErrors = _
    .filter(_ => !isValidInvolvesTaxCreditConfig(_))
    .map   (_ => ({ config: _, errors: involvesTaxCreditConfigErrors(_) }));
  assert(objectValidationErrors.length === 0, `Object errors found in defined involves-tax-credit configurations with following error codes. ${JSON.stringify(objectValidationErrors, stringifyReplacer, 2)}`);
  const arrayValidationErrors = involvesTaxCreditConfigsErrors(_);
  assert(arrayValidationErrors.length === 0, `Array errors found in defined involves-tax-credit configurations with following error codes. ${JSON.stringify(arrayValidationErrors, stringifyReplacer, 2)}`);
  return _;
}

export type ClaimConfig = {
  sapOrderTiming:    CLM.SapOrderTiming;
  involvesTaxCredit: boolean;
}
export type SupportedClaimType = {
  claimType:         CLM.ClaimType;
  claimReason:       CLM.ClaimReason;
  itemized:          boolean;
  involvesTaxCredit: boolean;
  sapOrderTiming:    CLM.SapOrderTiming;
}

export const validateConfig = ({ configuration_claimCreators, configuration_sapOrderTiming, configuration_involvesTaxCredit }: {
  configuration_claimCreators:     ClaimCreatorsConfig[];
  configuration_sapOrderTiming:    SapOrderTimingConfig[];
  configuration_involvesTaxCredit: InvolvesTaxCreditConfig[];
}) => {
  validateClaimCreatorsConfigs    (configuration_claimCreators    );
  validateSapOrderTimingConfigs   (configuration_sapOrderTiming   );
  validateInvolvesTaxCreditConfigs(configuration_involvesTaxCredit);

  const missingSapOrderTiming = configuration_claimCreators
    .filter(_ =>
      !configuration_sapOrderTiming.some(sap =>
        sap.claimType   === _.claimType   &&
        sap.claimReason === _.claimReason &&
        sap.itemized    === _.itemized));
  assert(missingSapOrderTiming.length === 0, `Missing SAP order timing entries for the following claim-creators combinations. ${JSON.stringify(missingSapOrderTiming, stringifyReplacer, 2)}`);

  const missingTaxCredit = configuration_claimCreators
    .filter(_ =>
      !configuration_involvesTaxCredit.some(tax =>
        tax.claimType   === _.claimType &&
        tax.claimReason === _.claimReason));
  assert(missingTaxCredit.length === 0, `Missing involves-tax-credit entries for the following claim-creators combinations. ${JSON.stringify(missingTaxCredit, stringifyReplacer, 2)}`);

  const sapOrderTimingMissingClaimCreators = configuration_sapOrderTiming
    .filter(_ =>
      !configuration_claimCreators.some(creator =>
        creator.claimType   === _.claimType   &&
        creator.claimReason === _.claimReason &&
        creator.itemized    === _.itemized));
  assert(sapOrderTimingMissingClaimCreators.length === 0, `Missing claim-creators entries for the following SAP order timing combinations. ${JSON.stringify(sapOrderTimingMissingClaimCreators, stringifyReplacer, 2)}`);

  const atCreationWithNonBbcCreator = configuration_sapOrderTiming
    .filter(_ => _.sapOrderTiming === SapOrderTiming.AT_CREATION)
    .filter(_ =>
      configuration_claimCreators.some(creator =>
        creator.claimType   === _.claimType   &&
        creator.claimReason === _.claimReason &&
        creator.itemized    === _.itemized    &&
        creator.claimCreators.some(role => role.isBbcEmployee !== true)));
  assert(atCreationWithNonBbcCreator.length === 0, `AT_CREATION SAP order timing with non-BBC creators for the following combinations. ${JSON.stringify(atCreationWithNonBbcCreator, stringifyReplacer, 2)}`);

  return { configuration_claimCreators, configuration_sapOrderTiming, configuration_involvesTaxCredit };
}

// #endregion

  export type UserId                    = Id & { __resource: "CLM:user"                             };
  export type WholesalerId              = Id & { __resource: "CLM:wholesaler"                       };
  export type ClaimId                   = Id & { __resource: "CLM:claim"                            };
  export type UpperLevelClaimApprovalId = Id & { __resource: "CLM:claim/upper-level-claim-approval" };
  export type IssueId                   = Id & { __resource: "CLM:claim/issue"                      };
  export type DistributorId             = Id & { __resource: "CLM:distributor"                      };
  export type DistributorLocationId     = Id & { __resource: "CLM:distributor/distributor-location" };
  export type DistributorEmployeeId     = Id & { __resource: "CLM:distributor/distributor-employee" };
  export type BbcEmployeeId             = Id & { __resource: "CLM:bbc-employee"                     };
  export type ProductId                 = Id & { __resource: "CLM:product"                          };
  export type ProductLineItemId         = Id & { __resource: "CLM:product-line-item"                };

  export const toSystemUserId = (_: UserId) => (_ as Id) as SYS.UserId;

  export type DistributorIdentifier         = CLM.DistributorId         | CLM.Distributor                                            | string;
  export type DistributorLocationIdentifier = CLM.DistributorLocationId | CLM.DistributorLocation                                    | string;
  export type DistributorEmployeeIdentifier = CLM.DistributorEmployeeId | CLM.DistributorEmployee | CLM.DistributorEmployeeReference | string;
  export type BbcEmployeeIdentifier         = CLM.BbcEmployeeId         | CLM.BbcEmployee         | CLM.BbcEmployeeReference         | string;

  // export type ResourceIdentifier = CLM.ClaimIdentifier | CLM.DistributorIdentifier | CLM.DistributorLocationIdentifier | CLM.DistributorEmployeeIdentifier | CLM.BbcEmployeeIdentifier;

  export type Identifier<TResourceId extends Id> = TResourceId | { id: TResourceId } | string;

  export const toResourceId = <TResourceId extends Id>(_ : Identifier<TResourceId>): TResourceId => {
    const candidate = toOptionalResourceId<TResourceId>(_);
    assert(candidate !== undefined, `Expected a valid resource identifier but got: ${JSON.stringify(_)} (type: ${typeof _})`);
    return candidate;
  }
  export const toOptionalResourceId = <TResourceId extends Id>(_? : Identifier<TResourceId>): TResourceId | undefined => {
    if(typeof _ === "string") {
      return Id.from(_) as TResourceId;
    }
    if(_ instanceof Id) {
      return _ as TResourceId;
    }
    if(_?.id) {
      assert(_?.id instanceof Id, `Expected a valid resource identifier but got: ${JSON.stringify(_)} (type: ${typeof _})`);
      return _?.id;
    }
    return undefined;
  }
  export const toRawResourceId         = <TResourceId extends Id>(_  : Identifier<TResourceId>) => toResourceId        <TResourceId>(_) .asInt.toString();
  export const toOptionalRawResourceId = <TResourceId extends Id>(_? : Identifier<TResourceId>) => toOptionalResourceId<TResourceId>(_)?.asInt.toString();

// #region User

  export function dueDateAlert_inBusinessDays(user: User) {
    return user.experimentalSettings?.dueDateAlert_inBusinessDays ??
      DEFAULT_EXPERIMENTAL_SETTINGS.dueDateAlert_inBusinessDays;
  }

  export const DEFAULT_EXPERIMENTAL_SETTINGS = {
    showOverdue:                  false,
    hideDraftsWhenEmpty:          false,
    dueDateAlert_inBusinessDays:  undefined,
  };

  export type GlobalSettings = {
  }
  export type UserSettings = {
    experience: Experience;
    persona:    CLP.ActorType;
  }
  export type UserExperimentalSettings = {
    showOverdue:                  boolean;
    hideDraftsWhenEmpty:          boolean;
    dueDateAlert_inBusinessDays?: number;
  }
  export type User = {
    id:                    UserId;
    firstName:             string;
    lastName:              string;
    name:                  string;
    email:                 string;
    roleType:              RLT.RoleType;
    settings:              UserSettings;
    experimentalSettings?: UserExperimentalSettings;
    testingTags?:          SYS.TestingTag[];
    impersonating?:        User;
  }

  export type RawUser = {
    id:                    string;
    firstName:             string;
    lastName:              string;
    name:                  string;
    email:                 string;
    roleType:              string;
    testingTags?:          string;

    impersonating_id?:        string;
    impersonating_firstName?: string;
    impersonating_lastName?:  string;
    impersonating_name?:      string;
    impersonating_email?:     string;
    impersonating_roleType?:  string;
  }

  export const userRoleHelpers = ({ currentUser }: { currentUser: User }) => ({
    whenRC:      currentUser.settings.persona === CLP.ActorType.RETURNS_COORDINATOR,
    whenDSS:     currentUser.settings.persona === CLP.ActorType.DIVISION_SUPPORT_SPECIALIST,
    whenCOS:     currentUser.settings.persona === CLP.ActorType.CUSTOMER_OPERATIONS_SPECIALIST,
    whenWCA:     currentUser.settings.persona === CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT,
    whenWCA_AS:  currentUser.settings.persona === CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT_ASSISTANT,
    whenARS:     currentUser.settings.persona === CLP.ActorType.ACCOUNTS_RECEIVABLE_SUPERVISOR,
    whenBBC_APR: currentUser.settings.persona === CLP.ActorType.BBC_APPROVER,
    whenDIS:     currentUser.settings.persona === CLP.ActorType.DISTRIBUTOR,

    whenAdmin: [RLT.RoleType.Admin, RLT.RoleType.DevAdmin].includes(currentUser.roleType)
  });

  export const user = () => (
    user: User
  ) => {
    user.experimentalSettings = DEFAULT_EXPERIMENTAL_SETTINGS;

    if(user.roleType == RLT.RoleType.RC) {
      user.settings = { experience: Experience.NOVICE, persona: CLP.ActorType.RETURNS_COORDINATOR };
    }
    else if(user.roleType == RLT.RoleType.WCA_AS) {
      user.settings = { experience: Experience.NOVICE, persona: CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT_ASSISTANT };
    }
    else if(user.roleType == RLT.RoleType.ARS) {
      user.settings = { experience: Experience.NOVICE, persona: CLP.ActorType.ACCOUNTS_RECEIVABLE_SUPERVISOR };
    }
    else if(user.roleType == RLT.RoleType.WCA) {
      user.settings = { experience: Experience.NOVICE, persona: CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT };
    }
    else if(user.roleType == RLT.RoleType.Admin || user.roleType == RLT.RoleType.DevAdmin ) {
      user.settings = { experience: Experience.PRO, persona: CLP.ActorType.RETURNS_COORDINATOR };
    }
    else if(user.roleType == RLT.RoleType.BBC_APR) {
      user.settings = { experience: Experience.PRO, persona: CLP.ActorType.BBC_APPROVER };
    }
    else if(user.roleType == RLT.RoleType.DIS) {
      user.settings = { experience: Experience.NOVICE, persona: CLP.ActorType.DISTRIBUTOR };
    }
    else if(user.roleType == RLT.RoleType.DSS) {
      user.settings = { experience: Experience.NOVICE, persona: CLP.ActorType.DIVISION_SUPPORT_SPECIALIST };
    }
    else if(user.roleType == RLT.RoleType.COS) {
      user.settings = { experience: Experience.NOVICE, persona: CLP.ActorType.CUSTOMER_OPERATIONS_SPECIALIST };
    }
    else {
      throw new Error("User is not in a valid role cannot determine settings.");
    }

    return user;
  }

  export const toRawUser = (_: User): RawUser => ({
    id:                   _.id.asInt.toString(),
    firstName:            _.firstName,
    lastName:             _.lastName,
    name:                 _.name,
    email:                _.email,
    roleType:             _.roleType.identifier,
    testingTags:          _.testingTags?.map(_ => _.identifier).join(','),

    impersonating_id:        _.impersonating?.id.asInt.toString(),
    impersonating_firstName: _.impersonating?.firstName,
    impersonating_lastName:  _.impersonating?.lastName,
    impersonating_name:      _.impersonating?.name,
    impersonating_email:     _.impersonating?.email,
    impersonating_roleType:  _.impersonating?.roleType.identifier,
  });

  export const fromRawUser = () => (_: RawUser): User => (user())({
    id:                   Id.from(BigInt(_.id)) as UserId,
    firstName:            _.firstName,
    lastName:             _.lastName,
    name:                 _.name,
    email:                _.email,
    roleType:             RLT.roleType(_.roleType),
    settings:             {
                            experience: Experience.NOVICE,
                            persona:    CLP.ActorType.RETURNS_COORDINATOR,
                          },
    experimentalSettings: DEFAULT_EXPERIMENTAL_SETTINGS,
    testingTags:          _.testingTags?.split(',').map(_ => SYS.testingTag(_)),
    impersonating:        _.impersonating_id && _.impersonating_firstName && _.impersonating_lastName && _.impersonating_name && _.impersonating_email && _.impersonating_roleType &&
                          fromRawUser()({
                            id:        _.impersonating_id,
                            firstName: _.impersonating_firstName,
                            lastName:  _.impersonating_lastName,
                            name:      _.impersonating_name,
                            email:     _.impersonating_email,
                            roleType:  _.impersonating_roleType,
                          }) || undefined,
  });

  const userValidator = validator((_: User) => ({
    a1: !_.roleType.controlledDistributorAccess || IS_bbcEmployee({ user: _ }),
  }));

  export const userErrors    = (_: User) => userValidator.validationErrors(_);
  export const isValidUser   = (_: User) => userValidator.isValid(_);
  export const validateUsers = (_: User[]) => {
    const validationErrors = _
      .filter(_ => !isValidUser(_))
      .map(_ => ({ user: _, errors: userErrors(_) }));

    assert(
      validationErrors.length === 0,
      `Errors found in defined Users with following error codes. ${JSON.stringify(validationErrors, stringifyReplacer, 2)}`
    );
  };
  export const validateRawUsers = (rawUsers: RawUser[]) => {
    const users = rawUsers.map(fromRawUser());
    validateUsers(users);
  };

// #endregion
// #region Wholesaler

  export type Wholesaler = {
    id:    WholesalerId;
    sapId: string;
    name:  string;
  }

// #endregion

// #region Claim - Claim Number
const MIN_PREFIX =  0;
const MAX_PREFIX = 99;
const BASELINE_YEAR = 2000;
const MIN_CLAIM_YEAR = BASELINE_YEAR + MIN_PREFIX;
const MAX_CLAIM_YEAR = BASELINE_YEAR + MAX_PREFIX;
export const claimNumberPrefix = ({ commandTime, year, prefixOverride }: {
  commandTime?:    Date,
  year?:           number,
  prefixOverride?: number,
}): number => {
  assert(commandTime !== undefined || year !== undefined, 'Either commandTime or year must be provided');
  assert(commandTime === undefined || year === undefined, 'Only one of commandTime or year can be provided');

  if (commandTime !== undefined) {
    const year = year_FROM_date(commandTime);
    return claimNumberPrefix({ year, prefixOverride });
  }

  assert(year !== undefined, 'year must be provided if commandTime is not');
  assert(year >= MIN_CLAIM_YEAR && year <= MAX_CLAIM_YEAR, `Year must be between ${MIN_CLAIM_YEAR} and ${MAX_CLAIM_YEAR}`);
  return prefixOverride ?? year - BASELINE_YEAR;
}

export const claimNumberPrefix_asString = ({ prefix }: {
  prefix: number,
}): string => {
  assert(prefix >= MIN_PREFIX && prefix <= MAX_PREFIX, `Prefix must be between ${MIN_PREFIX} and ${MAX_PREFIX}`);
  assert(Number.isInteger(prefix), 'Prefix must be an integer');
  return prefix.toString().padStart(2, '0');
}

export const claimNumberFormatted = ({ year, sequence, prefixOverride }: {
  year:            number,
  sequence:        number,
  prefixOverride?: number,
}) => {
  assert(year >= MIN_CLAIM_YEAR && year <= MAX_CLAIM_YEAR, `Year must be between ${MIN_CLAIM_YEAR} and ${MAX_CLAIM_YEAR}`)
  assert(sequence >= 1 && sequence <= MAX_CLAIM_SEQUENCE_NUMBER, `Sequence must be between 1 and ${MAX_CLAIM_SEQUENCE_NUMBER}`)
  assert(prefixOverride === undefined || (prefixOverride >= MIN_PREFIX && prefixOverride <= MAX_PREFIX), `Prefix must be between ${MIN_PREFIX} and ${MAX_PREFIX}`)

  const prefix = claimNumberPrefix({ year, prefixOverride });

  const paddedPrefix   = prefix  .toString().padStart(2, '0');
  const paddedSequence = sequence.toString().padStart(4, '0');

  return `C${paddedPrefix}-${paddedSequence}`;
}
const MAX_CLAIM_SEQUENCE_NUMBER   = 999999;
const CLAIM_NUMBER_FORMAT_PATTERN = /^C(\d{2})-(\d{4,6})$/;

export const claimNumberParts = ({ claimNumber }: {
  claimNumber: string,
}) => {
  assert(claimNumber.match(CLAIM_NUMBER_FORMAT_PATTERN), `Claim number must match the pattern ${CLAIM_NUMBER_FORMAT_PATTERN}`);

  const [_, prefixAsString, sequenceAsString] = claimNumber.match(CLAIM_NUMBER_FORMAT_PATTERN) as RegExpMatchArray;

  const prefix   = parseInt(prefixAsString  );
  const sequence = parseInt(sequenceAsString);
  const year     = BASELINE_YEAR + prefix;

  assert(prefix   >= MIN_PREFIX     && prefix   <= MAX_PREFIX,                `Prefix must be between ${MIN_PREFIX} and ${MAX_PREFIX}`);
  assert(sequence >= 1              && sequence <= MAX_CLAIM_SEQUENCE_NUMBER, `Sequence must be between 1 and ${MAX_CLAIM_SEQUENCE_NUMBER}`);
  assert(year     >= MIN_CLAIM_YEAR && year     <= MAX_CLAIM_YEAR,            `Year must be between ${MIN_CLAIM_YEAR} and ${MAX_CLAIM_YEAR}`);

  return { year, prefix, sequence };
}

export const nextClaimNumber = ({ lastClaimNumberInCurrentGroup, now, prefixOverride, sequenceOverride }: {
  lastClaimNumberInCurrentGroup: string | undefined,
  now:                           Date,
  prefixOverride?:               number,
  sequenceOverride?:             number,
}) => {
  assert(prefixOverride   === undefined || (prefixOverride   >= MIN_PREFIX && prefixOverride   <= MAX_PREFIX               ), `Prefix must be between ${MIN_PREFIX} and ${MAX_PREFIX}`)
  assert(sequenceOverride === undefined || (sequenceOverride >= 1          && sequenceOverride <= MAX_CLAIM_SEQUENCE_NUMBER), `Sequence must be between 1 and ${MAX_CLAIM_SEQUENCE_NUMBER}`)

  const currentYear = year_FROM_date(now);

  if (sequenceOverride              !== undefined) return claimNumberFormatted({ year: currentYear, sequence: sequenceOverride, prefixOverride });
  if (lastClaimNumberInCurrentGroup === undefined) return claimNumberFormatted({ year: currentYear, sequence: 1,                prefixOverride });

  assert(lastClaimNumberInCurrentGroup.match(CLAIM_NUMBER_FORMAT_PATTERN));

  const { year, prefix, sequence } = claimNumberParts({ claimNumber: lastClaimNumberInCurrentGroup });

  if (prefixOverride !== undefined) {
    assert(prefix === prefixOverride, `Prefix must match the override value ${prefixOverride}`);
  }
  else {
    assert(year === currentYear, `Year must match the current year ${currentYear}`);
  }

  return claimNumberFormatted({ year: currentYear, sequence: sequence + 1, prefixOverride });
}
// #endregion
// #region Claim - Noi

const noiFormatted = (year: number, sequence: number) => {
  assert(year >= MIN_CLAIM_YEAR && year <= MAX_CLAIM_YEAR, `Year must be between ${MIN_CLAIM_YEAR} and ${MAX_CLAIM_YEAR}`)
  assert(sequence >= 1 && sequence <= 999, 'Sequence must be between 1 and 999')
  return `${year.toString().slice(2, 4)}-${sequence.toString().padStart(3, '0')}D`;
}

export const nextNoi = ({ lastNoi, now }: {
  lastNoi: string | undefined,
  now:     Date,
}) => {
  const currentYear       = year_FROM_date(now);
  const firstNoiOfTheYear = noiFormatted(currentYear, 1);

  if (lastNoi === undefined) return firstNoiOfTheYear;

  assert(lastNoi.match(/^\d{2}-\d{2,3}D$/));

  const [_, yearAsString, sequenceAsString] = lastNoi.match(/^(\d{2})-(\d{2,3})D$/) as RegExpMatchArray;

  const year = 2000 + parseInt(yearAsString);
  assert(year >= MIN_CLAIM_YEAR && year <= MAX_CLAIM_YEAR, `Year must be between ${MIN_CLAIM_YEAR} and ${MAX_CLAIM_YEAR}`)
  assert(year <= currentYear, 'Year must be less than or equal to current year')

  if (year !== currentYear) return firstNoiOfTheYear;

  const sequence = parseInt(sequenceAsString);
  assert(sequence >= 1 && sequence <= 999, 'Sequence must be between 1 and 999')

  return noiFormatted(year, sequence + 1);
}

// #endregion
// #region Claim - Process Fields

export type ClaimProcessFields = {
  stage:              CLP.Stage;
  phase:              CLP.Phase;
  deliverable?:       CLP.Deliverable;

  bbcStatus:          CLP.BbcStatus;
  distributorStatus?: CLP.DistributorStatus;
  cancelationReason?: string;
  denialReason?:      string;

  baseline_persona?:  CLP.ActorType;
  baseline_action?:   CLP.BaselineAction;
  baseline_dueDate?:  Date;
}
export type RawClaimProcessFields = {
  stage:              string;
  phase:              string;
  deliverable?:       string;

  bbcStatus:          string;
  distributorStatus?: string;
  cancelationReason?: string;
  denialReason?:      string;

  baseline_persona?:  string;
  baseline_action?:   string;
  baseline_dueDate?:  string;
}
export function toRawClaimProcessFields(_: ClaimProcessFields): RawClaimProcessFields {
  return {
    ..._,

    stage:            _.stage       .identifier,
    phase:            _.phase       .identifier,
    deliverable:      _.deliverable?.identifier,

    bbcStatus:         _.bbcStatus.identifier,
    distributorStatus: _.distributorStatus?.identifier,

    baseline_persona: _.baseline_persona?.identifier,
    baseline_action:  _.baseline_action ?.identifier,

    baseline_dueDate: _.baseline_dueDate?.toISOString(),
  };
}
export function fromRawClaimProcessFields(_: RawClaimProcessFields): ClaimProcessFields {
  return {
    ..._,

    stage:            CLP.stage       (_.stage),
    phase:            CLP.phase       (_.phase),
    deliverable:      _.deliverable !== undefined && CLP.deliverable (_.deliverable) || undefined,

    bbcStatus:         CLP.bbcStatus      (_.bbcStatus),
    distributorStatus: _.distributorStatus !== undefined && CLP.distributorStatus (_.distributorStatus) || undefined,

    baseline_persona: _.baseline_persona !== undefined && CLP.actorType(_.baseline_persona)                         || undefined,
    baseline_action:  _.baseline_action  !== undefined && CLP.action   (_.baseline_action ) as CLP.BaselineAction   || undefined,

    baseline_dueDate: _.baseline_dueDate !== undefined && new Date(_.baseline_dueDate) || undefined,
  };
}

export const CLAIM_PROCESS_FIELDS_RAW_CLEAN: Partial<RawClaimProcessFields> = {
  stage:                   undefined,
  phase:                   undefined,
  deliverable:             undefined,

  bbcStatus:               undefined,
  distributorStatus:       undefined,
  cancelationReason:       undefined,
  denialReason:            undefined,

  baseline_persona:        undefined,
  baseline_action:         undefined,
  baseline_dueDate:        undefined,
}
export const claim_processFieldsRaw_WITH =  (cleanupContext: () => ClaimCleanupContext) => (_: RawClaimProcessFields): RawClaimProcessFields => {
  return {
    ...CLAIM_PROCESS_FIELDS_RAW_CLEAN,
    ..._,
  };
}

export const claimProcessFields_FROM = ({ claim }: {
  claim: Claim
}): ClaimProcessFields => ({
  stage:                   claim.stage,
  phase:                   claim.phase,
  deliverable:             claim.deliverable,

  bbcStatus:               claim.bbcStatus,
  distributorStatus:       claim.distributorStatus,
  cancelationReason:       claim.cancelationReason,
  denialReason:            claim.denialReason,

  baseline_persona:        claim.baseline?.persona,
  baseline_action:         claim.baseline?.action,
  baseline_dueDate:        claim.baseline?.dueDate,
});
const coreProcessFields_FOR = ({ action, dueDate }:{
  action:   CLP.BaselineAction,
  dueDate?: Date,
}): Omit<ClaimProcessFields, "bbcStatus"> => {

  return ({
    stage:            action.deliverable.phase.stage,
    phase:            action.deliverable.phase,
    deliverable:      action.deliverable,

    baseline_persona: CLP.action_assignedTo(action),
    baseline_action:  action,
    baseline_dueDate: dueDate,

    distributorStatus: action.deliverable.phase.stage.distributorStatus,
  });
}

export const timeEstimates = CLP.timeExpectations;

export const TtbTimeConstraints = {
  WHOLESALER_CREDIT:  30,
}

export const claimProcessFields_FOR = {
  newDraft:    (): ClaimProcessFields => claimProcessFields_FOR.claimCreation   .step2(),
  newDraft_v2: (): ClaimProcessFields => claimProcessFields_FOR.claimCreation_v2.step2(),
  claimCreation: {
    step2: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action:  CLP.Action.ENTER_WHOLESALER_POINT_OF_CONTACT,
      }),
      bbcStatus: CLP.BbcStatus.DRAFT,
    }),
    step3: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action:  CLP.Action.ENTER_CLAIM_APPROVAL_DETAILS,
      }),
      bbcStatus: CLP.BbcStatus.DRAFT,
    }),
    step4: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.UPLOAD_CLAIM_FORM,
      }),
      bbcStatus: CLP.BbcStatus.DRAFT,
    }),
    stepFinal: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action:  CLP.Action.REVIEW_AND_OPEN_CLAIM,
      }),
      bbcStatus: CLP.BbcStatus.DRAFT,
    }),
  },
  claimCreation_v2: {
    step2: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action:  CLP.Action.SELECT_PRODUCTS,
      }),
      bbcStatus:         CLP.BbcStatus.DRAFT,
      distributorStatus: CLP.DistributorStatus.DRAFT,
    }),
    step3: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action:  CLP.Action.ENTER_PRODUCT_DETAILS,
      }),
      bbcStatus:         CLP.BbcStatus.DRAFT,
      distributorStatus: CLP.DistributorStatus.DRAFT,
    }),
    step4: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action:  CLP.Action.SPECIFY_DESTRUCTION_INTENT,
      }),
      bbcStatus:         CLP.BbcStatus.DRAFT,
      distributorStatus: CLP.DistributorStatus.DRAFT,
    }),
    step5: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action:  CLP.Action.VERIFY_POINT_OF_CONTACT,
      }),
      bbcStatus:         CLP.BbcStatus.DRAFT,
      distributorStatus: CLP.DistributorStatus.DRAFT,
    }),
    step6: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action:  CLP.Action.CONFIRM_AND_SUBMIT,
      }),
      bbcStatus:         CLP.BbcStatus.DRAFT,
      distributorStatus: CLP.DistributorStatus.DRAFT,
    }),
    submit: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action:  CLP.Action.APPROVE_CLAIM,
      }),
      bbcStatus: CLP.BbcStatus.SUBMITTED,
    }),
  },
  submitted: {
    approve: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action:  CLP.Action.CONFIRM_CLAIM_ACCEPTANCE,
      }),
      bbcStatus: CLP.BbcStatus.SUBMITTED,
    }),
    confirmAcceptance: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action:  CLP.Action.RECORD_SAP_ORDER_NUMBER,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_NEW_SAP_ORDER,
    }),
    recordSapOrderNumber: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action:  CLP.Action.CONFIRM_SAP_ORDER_CREATED,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_NEW_SAP_ORDER,
    }),
    confirmSapOrderCreated: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action:  CLP.Action.CONFIRM_CLAIM_APPROVALS,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_CLAIM_APPROVAL,
    }),
    return:  (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action:  CLP.Action.CONFIRM_AND_SUBMIT,
      }),
      bbcStatus:         CLP.BbcStatus.DRAFT,
      distributorStatus: CLP.DistributorStatus.DRAFT,
    }),
  },
  claimApproval: {
    sendClaimApprovalRequests: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.SEND_CLAIM_APPROVAL_REQUESTS,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_CLAIM_APPROVAL,
    }),
    waitingForClaimApprovals: ({ firstDeadline }: { firstDeadline: Date}): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.WAITING_FOR_CLAIM_APPROVALS,
        dueDate: firstDeadline,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_CLAIM_APPROVAL,
    }),
    confirmClaimApprovals: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.CONFIRM_CLAIM_APPROVALS,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_CLAIM_APPROVAL,
    }),
  },
  ttbApproval: {
    prepareTtbSubmission: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.PREPARE_TTB_SUBMISSION,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_TTB_APPROVAL,
    }),
    sendTtbSubmission: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.SEND_TTB_SUBMISSION,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_TTB_APPROVAL,
    }),
    waitingForTtbApprovals: ({ ttbApprovalDeadline }: { ttbApprovalDeadline: Date}): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.WAITING_FOR_TTB_APPROVAL,
        dueDate: ttbApprovalDeadline,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_TTB_APPROVAL,
    }),
    confirmTtbApprovals: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.CONFIRM_TTB_APPROVAL,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_TTB_APPROVAL,
    }),
  },
  destruction: {
    sendApprovalToDestroy: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.SEND_APPROVAL_TO_DESTROY,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_APPROVAL_TO_DESTROY,
    }),
    confirmDestructionApprovals: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.CONFIRM_APPROVAL_TO_DESTROY,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_APPROVAL_TO_DESTROY,
    }),
    waitingForAods: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.WAITING_FOR_AODS,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_DESTRUCTION,
    }),
    confirmDestruction: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.CONFIRM_DESTRUCTION,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_DESTRUCTION,
    }),
  },
  wholesalerCreditApproval: {
    confirmEnteredInSAP: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.CONFIRM_CLAIM_ENTERED_IN_SAP,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_WHOLESALER_CREDIT,
    }),
    recordCreditTotals: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.RECORD_CREDIT_DETAILS,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_WHOLESALER_CREDIT,
    }),
    approveClaimForCredit: (): ClaimProcessFields => ({
      ...coreProcessFields_FOR({
        action: CLP.Action.CONFIRM_WHOLESALER_CREDITED,
      }),
      bbcStatus: CLP.BbcStatus.PENDING_WHOLESALER_CREDIT,
    }),
  },
  archived: {
    approvedForWholesalerCredit: (): ClaimProcessFields => ({
      bbcStatus:   CLP.BbcStatus.APPROVED_FOR_WHOLESALER_CREDIT,
      stage:             CLP.Stage.CLOSED,
      phase:             CLP.Phase.ARCHIVED,
      distributorStatus: CLP.DistributorStatus.CREDITED,
    }),
    wholesalerCredited: (): ClaimProcessFields => ({
      bbcStatus:         CLP.BbcStatus.WHOLESALER_CREDITED,
      stage:             CLP.Stage.CLOSED,
      phase:             CLP.Phase.ARCHIVED,
      distributorStatus: CLP.DistributorStatus.CREDITED,
    }),
    canceled: ({ cancelationReason }: { cancelationReason: string }): ClaimProcessFields => ({
      stage:    CLP.Stage.CLOSED,
      phase:    CLP.Phase.ARCHIVED,
      bbcStatus:   CLP.BbcStatus.CANCELED,
      cancelationReason,
      distributorStatus: CLP.DistributorStatus.CANCELED,
    }),
    denied: ({ denialReason }: { denialReason: string }): ClaimProcessFields => ({
      stage:  CLP.Stage.CLOSED,
      phase:  CLP.Phase.ARCHIVED,
      bbcStatus: CLP.BbcStatus.DENIED,
      denialReason,
      distributorStatus: CLP.DistributorStatus.DENIED,
    }),
  }
}

export const claimProcessFields_expected = ({ claim, ulcas, now }: {
  claim: Claim,
  ulcas: UpperLevelClaimApproval[],
  now:   Date,
}): ClaimProcessFields => {

  assert(claim.phase === CLP.Phase.CLAIM_APPROVAL);

  if (ulcas.length === 0) {
    return claimProcessFields_FOR.claimApproval.confirmClaimApprovals();
  }
  if (ulcas.every(ulca_approvalState_isApproved)) {
    return claimProcessFields_FOR.claimApproval.confirmClaimApprovals();
  }
  if (!ulcas.every(ulca_approvalState_isRequested)) {
    return claimProcessFields_FOR.claimApproval.sendClaimApprovalRequests();
  }

  const deadlines = ulcas
    .filter(_ => ulca_approvalState_isRequested(_) && !ulca_approvalState_isApproved(_))
    .map(_ => _.approvalDeadline ?? unexpectedCodePath("Approval deadline is missing."))
    .toSorted((a, b) => a.getTime() - b.getTime());

  assert(deadlines.length > 0);
  const firstDeadline = deadlines[0];

  return claimProcessFields_FOR.claimApproval.waitingForClaimApprovals({ firstDeadline });
}

export function claimProcessFields_areSame({ from, to }: {
  from: ClaimProcessFields,
  to:   ClaimProcessFields,
}): boolean {
  return areEqualEnums(from.stage,       to.stage      )
      && areEqualEnums(from.phase,       to.phase      )
      && areEqualEnums(from.deliverable, to.deliverable)

      && areEqualEnums(from.bbcStatus,         to.bbcStatus        )
      && areEqualEnums(from.distributorStatus, to.distributorStatus)

      && from.cancelationReason === to.cancelationReason
      && from.denialReason      === to.denialReason

      && areEqualEnums(from.baseline_persona,        to.baseline_persona)
      && areEqualEnums(from.baseline_action,         to.baseline_action )
      && from.baseline_dueDate?.toISOString() === to.baseline_dueDate?.toISOString();
}
export const claimProcessFields_isSupportedTransition = ({ from, to, command }: {
  from:    ClaimProcessFields,
  to:      ClaimProcessFields,
  command: CMD.Command,
}) => {
  return from.phase === CLP.Phase.CLAIM_APPROVAL
      && to  .phase === CLP.Phase.CLAIM_APPROVAL;
}

export type ClaimKeyMomentFields = {
  title?:              string;
  description?:        string;
  data?:               string;
  keyMoment:           KeyMoment;
  keyMoments_discard?: KeyMoment[];
}

export const claimRecordKeyMoment_FOR = {
  distributor:
  {
    submitted: (): ClaimKeyMomentFields => ({
      keyMoment: KeyMoment.SUBMITTED,
    }),
  },
  approver: {
    firstLevelApproved: (): ClaimKeyMomentFields => ({
      keyMoment: KeyMoment.FIRST_LEVEL_APPROVED,
    }),
    returned: (): ClaimKeyMomentFields => ({
      keyMoment:          KeyMoment.SENT_BACK,
      keyMoments_discard: [KeyMoment.FIRST_LEVEL_APPROVED, KeyMoment.SUBMITTED],
    }),
  },
  returnsCoordinator: {
    accepted: (): ClaimKeyMomentFields => ({
      keyMoment: KeyMoment.ACCEPTED,
    }),
    returned: (): ClaimKeyMomentFields => ({
      keyMoment:          KeyMoment.SENT_BACK,
      keyMoments_discard: [KeyMoment.ACCEPTED, KeyMoment.FIRST_LEVEL_APPROVED, KeyMoment.SUBMITTED],
    }),
    sapOrderCreated: (): ClaimKeyMomentFields => ({
      keyMoment: KeyMoment.SAP_ORDER_CREATED,
    }),
  },
  salesApproval: {
    claimApprovalConfirmed: (): ClaimKeyMomentFields => ({
      keyMoment: KeyMoment.CLAIM_APPROVAL_CONFIRMED,
    }),
  },
  ttb: {
    submissionConfirmed: (): ClaimKeyMomentFields => ({
      keyMoment: KeyMoment.TTB_SUBMISSION_CONFIRMED,
    }),
  },
  sap: {
    entryConfirmed: (): ClaimKeyMomentFields => ({
      keyMoment: KeyMoment.SAP_ENTRY_CONFIRMED,
    }),
  },
  destruction: {
    approvalConfirmed: (): ClaimKeyMomentFields => ({
      keyMoment: KeyMoment.DESTRUCTION_APPROVAL_CONFIRMED,
    }),
    confirmed: (): ClaimKeyMomentFields => ({
      keyMoment: KeyMoment.DESTRUCTION_CONFIRMED,
    }),
  },
  archived: {
    closed: (): ClaimKeyMomentFields => ({
      keyMoment: KeyMoment.CLOSED,
    }),
  },
}

export function expected_ttbApprovalDeadline({ claim }: {
  claim: Claim
}) {
  // CLIFF: Wrapping into dateOnly_FROM_date to make sure that daylight savings time is not an issue
  // TODO: Remove this when daylight savings time is no longer an issue
  return claim.ttb_dateFaxedToTTB === undefined ? undefined : dateOnly_FROM_date(
    addDays(claim.ttb_dateFaxedToTTB, CLM.timeEstimates.TTB_APPROVAL)
  );
}

export function isShareable({ claim }: {
  claim: Claim
}) {
  return claim.schemaVersion === CLP.SchemaVersion.V1 && claim.stage === CLP.Stage.OPEN
      || claim.schemaVersion === CLP.SchemaVersion.V2;
}

export function isDenied({ claim }: {
  claim: Claim
}) {
  return claim.bbcStatus === CLP.BbcStatus.DENIED;
}
export function isCanceled({ claim }: {
  claim: Claim
}) {
  return claim.bbcStatus === CLP.BbcStatus.CANCELED;
}

export function isClaimApprovalRequired({ claim }: {
  claim: Claim
}) {
  if (claim.ulca_claimApprover_levels !== undefined) {
    assert(claim.ulca_claimApprover_levels.length > 0);
    return true;
  }
  return false;
}
export function isClaimApprovalConfirmed({ claim }: {
  claim: Claim
}){
  return claim.keyMoments.claimApprovalConfirmed !== undefined;
}
export function isNoiRequired({ claim, nextProcessFields }: {
  claim:             Claim,
  nextProcessFields: ClaimProcessFields,
}) {
  return claim.beverageType === BeverageType.BEER_FMB
      && ![CLP.Phase.CLAIM_SUBMISSION, CLP.Phase.CLAIM_CREATION, CLP.Phase.CLAIM_INTAKE, CLP.Phase.SAP_ORDER_CREATION, CLP.Phase.CLAIM_APPROVAL].includes(nextProcessFields.phase);
}
export function isDestructionIntentComplete({ claim }: {
  claim: Claim
}) {
  const hasAddress             = claim.di_destructionCompany_streetAddress !== undefined
                              && claim.di_destructionCompany_city          !== undefined
                              && claim.di_destructionCompany_state         !== undefined
                              && claim.di_destructionCompany_zipCode       !== undefined;
  const hasDistributorLocation = claim.di_destructionCompany_type === CLM.DestructionCompanyType.WHOLESALER
                              && claim.di_distributorDestructionLocation !== undefined;

  return (hasAddress || hasDistributorLocation)
      && claim.di_destructionCompany_type   !== undefined
      && (claim.di_destructionCompany_name  !== undefined || hasDistributorLocation)
      && claim.di_destructionMethod         !== undefined;
}
export function isDestructionApprovalConfirmed({ claim }: {
  claim: Claim
}) {
  return claim.keyMoments.destructionApprovalConfirmed !== undefined;
}
export function isDestructionConfirmed({ claim }: {
  claim: Claim
}){
  return claim.keyMoments.destructionConfirmed !== undefined;
}

export function isTtbApprovalDueForFollowUp({ claim, newDueDate, now: now_ }: {
  claim:       Claim,
  newDueDate?: Date,
  now?:        Date,
}) {
  const dueDate = newDueDate ?? expected_ttbApprovalDeadline({ claim });
  if (dueDate === undefined) return false;

  const now = now_ ?? new Date();
  return WHEN_WITHIN(CLP.timeExpectations.TTB_FOLLOW_UP)({ dueDate, now });
}
export function isTtbApprovalOverdue({ claim, newDueDate, now: now_ }: {
  claim:       Claim,
  newDueDate?: Date,
  now?:        Date,
}) {
  const dueDate = newDueDate ?? expected_ttbApprovalDeadline({ claim });
  if (dueDate === undefined) return false;

  const now = now_ ?? new Date();
  return WHEN_WITHIN(0)({ dueDate, now });
}

export const isSubmittedToTtb = ({ claim }: {
  claim: Claim
}) => claim.ttb_dateFaxedToTTB   !== undefined
  && claim.ttb_noticeOfIntent_id !== undefined
  && claim.ttb_faxCoverSheet_id  !== undefined
  && claim.deliverable           !== CLP.Deliverable.TTB_SUBMISSION;

export const isSubmissionToTTBConfirmed = ({ claim }: {
  claim: Claim
}) => claim.keyMoments.ttb_submissionConfirmed !== undefined;

export const isApprovedByTtb = ({ claim }: {
  claim: CLM.Claim
}) => claim.ttb_ttbApprovalDate !== undefined
   && claim.ttb_ttbApproval_id  !== undefined
   && claim.ttb_ttbApprovalConfirmed;

export const isApprovedByBbc = ({ claim }: {
    claim: CLM.Claim
  }) => claim.bbc_destructionApprovalDate !== undefined
     && claim.bbc_destructionApproval_id  !== undefined
     && claim.deliverable         !== CLP.Deliverable.APPROVAL_TO_DESTROY;

export function isApprovedByAccounting({ claim }: {
  claim: Claim
}) {
  return ![CLP.Phase.CLAIM_SUBMISSION, CLP.Phase.CLAIM_CREATION, CLP.Phase.CLAIM_INTAKE, CLP.Phase.SAP_ORDER_CREATION, CLP.Phase.CLAIM_APPROVAL, CLP.Phase.TTB_APPROVAL, CLP.Phase.DESTRUCTION, CLP.Phase.WHOLESALER_CREDIT_APPROVAL].includes(claim.phase)
      && !CLM.isCanceled                   ({ claim })
      && !CLM.isDenied                     ({ claim });
}

export const isApprovedForWholesalerCredit = ({ claim }: {
  claim: CLM.Claim
}) => claim.bbcStatus === CLP.BbcStatus.APPROVED_FOR_WHOLESALER_CREDIT;

export function noiForDisplay({ claim }: {
  claim: Claim
}) {
  const textWhenEmpty = !isCanceled({ claim }) && !isDenied({ claim }) ? "pending" : undefined;
  return claim.noi ?? textWhenEmpty;
}

export const isWholesalerCredited = ({ claim }: {
  claim: CLM.Claim
}) => claim.bbcStatus === CLP.BbcStatus.WHOLESALER_CREDITED
   && !CLM.isCanceled({ claim })
   && !CLM.isDenied  ({ claim });

export function isActionable({ claim }: {
  claim: ClaimSummary
}) {
  return claim.current !== undefined;
}
export function hasOpenIssues({ claim }: {
  claim: ClaimSummary,
}) {
  return (claim.openIssueCount ?? 0) > 0;
}
export function hasOpenIssues_FOR_user({ claim, currentUser }: {
  claim:       ClaimSummary,
  currentUser: User
}) {
  const shouldIgnoreOpenIssues = [CLP.ActorType.DISTRIBUTOR, CLP.ActorType.BBC_APPROVER].includes(currentUser.settings.persona);

  if(shouldIgnoreOpenIssues) {
    return false;
  }

  return hasOpenIssues({ claim });
}
export function isProcessingBlocked({ claim }: {
  claim: Claim,
}) {
  return hasOpenIssues({ claim });
}
export function isProcessingBlocked_FOR_user({ claim, currentUser }: {
  claim:       ClaimSummary,
  currentUser: User
}) {
  return hasOpenIssues_FOR_user({ claim, currentUser });
}
export function isDone({ claim }: {
  claim: ClaimSummary
}) {
  return claim.bbcStatus === CLP.BbcStatus.APPROVED_FOR_WHOLESALER_CREDIT
      || claim.bbcStatus === CLP.BbcStatus.WHOLESALER_CREDITED;
}
export function isClosed({ claim }: {
  claim: ClaimSummary
}) {
  return claim.stage === CLP.Stage.CLOSED;
}
export function issuePersonas({ claim }: {
  claim: Claim
}) {
  return [...new Set(CLM.userGroups.flatMap(_ => claim.issuesAssignedTo?.includes(_) ? _.roles : []))];
}
export function hasIssue({ claim }: {
  claim: Claim
}) {
  return (claim.openIssueCount ?? 0) > 0 || (claim.resolvedIssueCount ?? 0) > 0;
}
export function toolTip({ claim }: {
  claim: ClaimSummary
}) {
  return (claim.openIssueCount ?? 0) > 1 ? `${claim.openIssueCount} open issues` : claim.currentIssueType?.name;
}
export function claimVerification({ claim }: {
  claim: Claim
}) {
  return claim.claimVerificationMethod ? true : false;
}
export function hasIncompleteProductLineItems({ claim }: {
  claim:       Claim & ClaimHelpers
}) {
  return (claim.incompleteProductLineItemCount ?? 0) > 0;
}
export const displayHasIncompleteProductLineItems = ({ claim, currentUser }: {
  claim:       Claim & ClaimHelpers,
  currentUser: User
}) => {
  const baselineAction = claim?.baseline_action ? claim.baseline_action : claim.baseline?.action;

  return currentUser.settings.persona === CLP.ActorType.DISTRIBUTOR
      && claim.distributorStatus === CLP.DistributorStatus.DRAFT
      && baselineAction?.type    === "primary"
      && [CLP.Action.SPECIFY_DESTRUCTION_INTENT, CLP.Action.VERIFY_POINT_OF_CONTACT, CLP.Action.CONFIRM_AND_SUBMIT].includes(baselineAction)
      && hasIncompleteProductLineItems({ claim });
}
export function hasGreaterThanFiftyPercentProductLineItems({ productLineItems }: {
  productLineItems: ProductLineItem[],
}) {
  return productLineItems.some(_ => (_.creditPercentage ?? 0) > 50);
}
export function displayHasGreaterThanFiftyPercentProductLineItems({ productLineItems, currentUser }: {
  productLineItems: ProductLineItem[],
  currentUser:      User
}) {
  return currentUser.settings.persona !== CLP.ActorType.DISTRIBUTOR
      && hasGreaterThanFiftyPercentProductLineItems({ productLineItems });
}
export function hasOverriddenCreditPercentages({ productLineItems }: {
  productLineItems: ProductLineItem[]
}) {
  return productLineItems.some(_ => _.creditPercentage !== _.reason.defaultCreditPercentage);
}
export function displayHasOverriddenCreditPercentages({ productLineItems, currentUser }: {
  productLineItems: ProductLineItem[],
  currentUser:      User
}) {
  return currentUser.settings.persona !== CLP.ActorType.DISTRIBUTOR
      && hasOverriddenCreditPercentages({ productLineItems });
}
export function hasModifiedProductInfo({ claim }: {
  claim: Claim
}) {
  if(!claim.bbcApproverNotes) return false;

  return true;
}
export function displayHasModifiedProductInfo({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User
}) {
  if(hasModifiedProductInfo({ claim })) {
    if(currentUser.settings.persona === CLP.ActorType.DISTRIBUTOR) {
      return claim.keyMoments.sentBack !== undefined || (claim.stage !== CLP.Stage.DRAFT && claim.keyMoments.firstLevelApproved !== undefined);
    }
    else {
      return true;
    }
  }

  return false;
}

export const isActionableBy = (user: User) => (action?: CLP.AssignableAction, persona?: CLP.ActorType) => {
  if (action === undefined) return false;

  const currentPersona = user.settings.persona;
  const actionPersona  = action.assignedTo;

  // Temporary fix to overcome the limitation that action can be assigned only to one persona. Will be removed when BOOM-682 is implemented.
  if (([CLP.Action.RECORD_CREDIT_DETAILS, CLP.Action.CONFIRM_WHOLESALER_CREDITED] as CLP.AssignableAction[]).includes(action)) {
    return currentPersona === CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT || currentPersona === CLP.ActorType.ACCOUNTS_RECEIVABLE_SUPERVISOR;
  }
  else if (([CLP.Action.CONFIRM_CLAIM_ENTERED_IN_SAP] as CLP.AssignableAction[]).includes(action)) {
    return currentPersona === CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT || currentPersona === CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT_ASSISTANT || currentPersona === CLP.ActorType.ACCOUNTS_RECEIVABLE_SUPERVISOR;
  }
  else if(action.type === "primary" && [CLP.Action.CONFIRM_ISSUE_RESOLVED, CLP.Action.GET_CLAIM_APPROVAL, CLP.Action.FINISH_PRODUCT_DETAILS].includes(action) && persona) {
    return currentPersona === persona;
  }

  return currentPersona === actionPersona;
}

export const isTestingTagEnabled = ({ testingTag, userTestingTags, claim }: {
  testingTag:       SYS.TestingTag,
  userTestingTags?: string[],
  claim?:           Claim
}) => {
  if(userTestingTags !== undefined) {
    //Leaving first testing tag as a template for future tags (this may get refactored)
    // if (testingTag === SYS.TestingTag.) {
    //   if(claim === undefined || claim.testingTags?.includes(testingTag)) {
    //     return userTestingTags.includes(SYS.TestingTag.TRACK_SAP_DATA_ENTRY.identifier);
    //   }
    // }
  }

  return false;
}

export const isSafeToToggleTestingTag = ({ claim, testingTag }: {
  claim:      Claim,
  testingTag: SYS.TestingTag,
}) => {
  if (!claim.testingTags?.includes(testingTag)) return true;

  switch (testingTag) {
    //Leaving first testing tag as a template for future tags
    case SYS.TestingTag.DO_NOT_USE_TRACK_SAP_DATA_ENTRY:
      // return claim?.baseline?.action !== CLP.Action.CONFIRM_CLAIM_ENTERED_IN_SAP;
      return true; //TODO: Required until we have an actual testing tag, or else clicking the DO NOT USE tag will thrown an exception
    default:
      unexpectedCodePath(`Testing tag ${testingTag.name} is not supported.`);
  }
}


export const credit_taxCreditAvailable = ({ claim }: {
  claim: Claim
}) => {
  if (!isTaxCreditOpportunity({ claim })) return undefined;

  const taxAmount = isTaxCreditRequested({ claim }) ? claim.ttb_tax
                  : isTaxCreditDeferred({ claim }) ? claim.skipTtb_deferredTaxCredit
                  : unexpectedCodePath("Tax credit is not eligible nor deferred.");

  if (claim.aod_casesDestroyed == undefined || claim.casesRequested == undefined || taxAmount === undefined) return undefined;

  assert(claim.casesRequested > 0, "Cases requested must be greater than 0.");

  const isPartialDestruction = claim.aod_casesDestroyed < claim.casesRequested;

  return isPartialDestruction ? money_FROM_number(taxAmount * claim.aod_casesDestroyed / claim.casesRequested)
                              : taxAmount;
}

export const nonAlcoholicCases = ({ claim }: {
  claim: Claim
}) => {
  !claim.ttb_alcoholicCases || !claim.casesRequested || assert(claim.ttb_alcoholicCases <= claim.casesRequested, "TTB alcoholic cases cannot be greater than cases requested.");
  return claim.beverageType === BeverageType.NON_ALCOHOLIC ? claim.casesRequested : claim.ttb_alcoholicCases && claim.casesRequested ? claim.casesRequested - claim.ttb_alcoholicCases : undefined;
}

export const acc_taxCreditEligibilityRisks = ({ claim }: {
  claim: Claim
}) => {
  if (!isTaxCreditOpportunity({ claim })) return [];

  const risks: TaxCreditEligibilityRisk[] = [];

  if(claim.aod_earliestDestructionDate)
  {
    if(claim.ttb_dateFaxedToTTB && claim.aod_earliestDestructionDate < claim.ttb_dateFaxedToTTB) {
      risks.push(TaxCreditEligibilityRisk.DESTROYED_BEFORE_TTB_SUBMISSION);
    }

    const shouldIgnoreApprovalDate = claim.ttb_ttbApprovalDeadline && claim.ttb_ttbApprovalDate && claim.ttb_ttbApprovalDeadline < claim.ttb_ttbApprovalDate;

    if(!shouldIgnoreApprovalDate && claim.ttb_ttbApprovalDate && claim.aod_earliestDestructionDate < claim.ttb_ttbApprovalDate) {
      risks.push(TaxCreditEligibilityRisk.DESTROYED_BEFORE_TTB_APPROVAL);
    }

    if(claim.acc_creditMemoDate && claim.acc_creditMemoDate > addDays(claim.aod_earliestDestructionDate, TtbTimeConstraints.WHOLESALER_CREDIT)) {
      risks.push(TaxCreditEligibilityRisk.LATE_WHOLESALER_CREDIT);
    }
  }

  const shouldIgnoreDeadline = claim.ttb_ttbApprovalDeadline && claim.ttb_ttbApprovalDate && claim.ttb_ttbApprovalDeadline >= claim.ttb_ttbApprovalDate;

  if(claim.skipTtb) {
    if(!shouldIgnoreDeadline && claim.ttb_ttbApprovalDeadline && claim.aod_earliestDestructionDate && claim.ttb_ttbApprovalDeadline > claim.aod_earliestDestructionDate) {
      risks.push(TaxCreditEligibilityRisk.TTB_SKIPPED_DESTROYED_BEFORE_TTB_APPROVAL_DEADLINE);
    }
    else if(!isSubmissionToTTBConfirmed({ claim }))
    {
      risks.push(TaxCreditEligibilityRisk.NOI_SUBMISSION_SKIPPED);
    }
  }
  else if(!shouldIgnoreDeadline && claim.ttb_ttbApprovalDeadline && claim.aod_earliestDestructionDate && claim.ttb_ttbApprovalDeadline > claim.aod_earliestDestructionDate) {
    risks.push(TaxCreditEligibilityRisk.DESTROYED_BEFORE_TTB_APPROVAL_DEADLINE);
  }

  if(claim.aod_hasIssues)
  {
    risks.push(TaxCreditEligibilityRisk.INVALID_AODS);
  }

  return risks;
}

export const acc_taxCreditEligibility = ({ claim }: {
  claim: Claim
}) => {
  const closed = isClosed({ claim });

  if (!isTaxCreditOpportunity({ claim }) || closed && !isDone({ claim })) return TaxCreditEligibility.NOT_ELIGIBLE;

  if(acc_taxCreditEligibilityRisks({ claim }).length) {
    return TaxCreditEligibility.MAY_BE_ELIGIBLE;
  }
  else if(closed)
  {
    return TaxCreditEligibility.TAX_RETURN_CREDIT_ELIGIBLE;
  }

  return TaxCreditEligibility.PENDING;
}

export function isAlcoholicClaimType({ claim }: {
  claim: Claim
}) {
  return claim.beverageType ? claim.beverageType !== BeverageType.NON_ALCOHOLIC : undefined;
}

export function isTaxCreditOpportunity({ claim }: {
  claim: Claim
}) {
  return claim.beverageType === BeverageType.BEER_FMB;
}
export function isTaxCreditRequested({ claim }: {
  claim: Claim
}) {
  return isTaxCreditOpportunity({ claim }) && (isSubmissionToTTBConfirmed({ claim }) || !claim.skipTtb);
}
export function isTaxCreditDeferred({ claim }: {
  claim: Claim
}){
  return isTaxCreditOpportunity({claim}) && !isTaxCreditRequested({claim});
}

export function di_destructionCompany_fullAddress({ claim }: {
  claim: Claim
}) {
  assert(claim.di_destructionCompany_streetAddress, 'Destruction company street address is required');
  assert(claim.di_destructionCompany_city,          'Destruction company city is required'          );
  assert(claim.di_destructionCompany_state,         'Destruction company state is required'         );
  assert(claim.di_destructionCompany_zipCode,       'Destruction company zip code is required'      );

  return `${claim.di_destructionCompany_streetAddress}, ${claim.di_destructionCompany_city}, ${claim.di_destructionCompany_state.identifier} ${claim.di_destructionCompany_zipCode}`;
}
export function OPTIONAL_di_destructionLocation({ claim }: {
  claim: Claim
}) {
  if (claim.di_destructionCompany_type === undefined) return undefined;
  assert(CLM.destructionCompanyTypes.includes(claim.di_destructionCompany_type), 'Destruction company type is not supported');

  if(claim.di_distributorDestructionLocation){
    assert(claim.di_destructionCompany_type === CLM.DestructionCompanyType.WHOLESALER, 'Destruction company type must be Wholesaler when distributor destruction location is provided');
    return distributorLocation_fullName({ distributorLocation: claim.di_distributorDestructionLocation });
  }

  const claim_HAS_thirdPartyDestructionAddress = claim.di_destructionCompany_streetAddress !== undefined
                                              && claim.di_destructionCompany_city          !== undefined
                                              && claim.di_destructionCompany_state         !== undefined
                                              && claim.di_destructionCompany_zipCode       !== undefined;

  return claim_HAS_thirdPartyDestructionAddress ? di_destructionCompany_fullAddress({ claim }) : undefined;
}
export const HAS_di_destructionLocation = ({ claim } : { claim: Claim }) => OPTIONAL_di_destructionLocation({ claim }) !== undefined;
export const di_destructionLocation     = ({ claim } : { claim: Claim }) => OPTIONAL_di_destructionLocation({ claim }) ?? assert(false, 'Destruction location is required');

export function di_destructionCompany_type_label({ claim, currentUser }: {
  claim:       Claim,
  currentUser: CLM.User
}) {
  assert(claim.di_destructionCompany_type);

  return destructionCompany_type_label({ destructionCompanyType: claim.di_destructionCompany_type, currentUser });
}
export function destructionCompany_type_label({ destructionCompanyType, currentUser }: {
  destructionCompanyType: CLM.DestructionCompanyType,
  currentUser: CLM.User
}) {
  return [CLP.ActorType.DISTRIBUTOR, CLP.ActorType.BBC_APPROVER].includes(currentUser.settings.persona) && destructionCompanyType === CLM.DestructionCompanyType.WHOLESALER ? destructionCompanyType.alias : destructionCompanyType.name;
}
export function userFriendly_claimType_label({ claim }: {
  claim: Claim
}) {
  return claim.beverageType ? `${claim.beverageType.name} Cases for ${claim.claimType.name}` : `Claim for ${claim.claimType.name}`;
}
export function userFriendly_beverageTypeDescription_label({ claim, description = "Cases" }: {
  claim:        ClaimSummary,
  description?: string
}) {
  return claim.beverageType ? `${claim.beverageType.name} ${description}` : "";
}

// #endregion
// #region PREDICATES => Claim

export const claimWithSchemaVersion     = (...versions:      CLP.SchemaVersion         []) => (_: CLM.ClaimSummary) => versions     .some(version      => _.schemaVersion                            === version                 );
export const claimWithAction            = (...actions:       CLP.Action                []) => (_: CLM.ClaimSummary) => actions      .some(action       => _.current?.action                          === action                  );
export const claimWithPhase             = (...phases:        CLP.Phase                 []) => (_: CLM.ClaimSummary) => phases       .some(phase        => _.phase                                    === phase                   );
export const claimWithStage             = (...stages:        CLP.Stage                 []) => (_: CLM.ClaimSummary) => stages       .some(stage        => _.stage                                    === stage                   );
export const claimWithBbcStatus         = (...bbcStatuses:   CLP.BbcStatus             []) => (_: CLM.ClaimSummary) => bbcStatuses  .some(bbcStatus    => _.bbcStatus                                === bbcStatus               );
export const claimWithDistributorStatus = (...statuses:      CLP.DistributorStatus     []) => (_: CLM.ClaimSummary) => statuses     .some(status       => _.distributorStatus                        === status                  );
export const claimWithBeverageType      = (...beverageTypes: CLM.BeverageType          []) => (_: CLM.ClaimSummary) => beverageTypes.some(beverageType => _.beverageType                             === beverageType            );
export const claimWithProductType       = (...beverageTypes: CLM.BeverageType          []) => (_: CLM.ClaimSummary) => beverageTypes.some(beverageType => _.beverageType?.productType                === beverageType.productType);
export const claimWithDistributor       = (...distributors:  CLM.RawDistributor        []) => (_: CLM.ClaimSummary) => distributors .some(distributor  => _.distributor        ?.id.asInt.toString() === distributor .id         );
export const claimWithLocation          = (...locations:     CLM.RawDistributorLocation[]) => (_: CLM.ClaimSummary) => locations    .some(location     => _.distributorLocation?.id.asInt.toString() === location    .id         );

export const claimWithStages            = (   stages:        CLP.Stage                 []) => (_: CLM.ClaimSummary) => stages       .some(stage        => claimWithStage      (stage      )(_) );
export const claimWithDistributors      = (   distributors:  CLM.RawDistributor        []) => (_: CLM.ClaimSummary) => distributors .some(distributor  => claimWithDistributor(distributor)(_) );
export const claimWithLocations         = (   locations:     CLM.RawDistributorLocation[]) => (_: CLM.ClaimSummary) => locations    .some(location     => claimWithLocation   (location   )(_) );

export const claimFor_RC     = (_: CLM.ClaimSummary) => CLM.claimWithSchemaVersion(CLP.SchemaVersion.V1                           )(_)
                                                     || CLM.claimWithStage        (CLP.Stage.OPEN,
                                                                                   CLP.Stage.SUBMITTED,
                                                                                   CLP.Stage.CLOSED                               )(_);
export const claimFor_WCA    = (_: CLM.ClaimSummary) => !CLM.claimWithStage       (CLP.Stage.DRAFT                                )(_);
export const claimFor_WCA_AS = (_: CLM.ClaimSummary) => CLM.claimWithPhase        (CLP.Phase.WHOLESALER_CREDIT_APPROVAL           )(_)
                                                     || CLM.claimWithBbcStatus    (CLP.BbcStatus.APPROVED_FOR_WHOLESALER_CREDIT,
                                                                                   CLP.BbcStatus.WHOLESALER_CREDITED              )(_);
export const claimFor_ARS    = (_: CLM.ClaimSummary) => CLM.claimWithBbcStatus    (CLP.BbcStatus.PENDING_WHOLESALER_CREDIT,
                                                                                   CLP.BbcStatus.APPROVED_FOR_WHOLESALER_CREDIT,
                                                                                   CLP.BbcStatus.WHOLESALER_CREDITED              )(_);

export const claimFor_DIS    = (_: CLM.ClaimSummary, { distributors, locations }: {
  distributors: CLM.RawDistributor[], locations: CLM.RawDistributorLocation[]
}) => CLM.claimWithDistributorStatus(...CLP.distributorStatuses)(_) &&
       CLM.claimWithDistributors    (distributors              )(_) &&
       CLM.claimWithLocations       (locations                 )(_);

export const claimFor_BBC_APR = (_: CLM.ClaimSummary, { distributors, locations }: {
  distributors: CLM.RawDistributor[], locations: CLM.RawDistributorLocation[]
}) => CLM.claimWithDistributorStatus(...CLP.distributorStatuses)(_) &&
      CLM.claimWithDistributors     (distributors              )(_) &&
      CLM.claimWithLocations        (locations                 )(_);

// #endregion
// #region Claim - Form Stages

function summary_STAGE({ claim }: { claim: Claim }) {
  if (claim.baseline?.action === CLP.Action.RECORD_SAP_ORDER_NUMBER   ) return 1;
  if (claim.baseline?.action === CLP.Action.CONFIRM_SAP_ORDER_CREATED ) return 1;
  if (claim.bbcStatus === CLP.BbcStatus.APPROVED_FOR_WHOLESALER_CREDIT) return 2;

  return 3;
}
function firstLevelApproval_STAGE({ claim }: {
  claim: Claim
}) {
  assert(claim.stage === CLP.Stage.SUBMITTED, 'Claim stage must be SUBMITTED');
  assert(claim.baseline?.action === CLP.Action.APPROVE_CLAIM, 'Claim baseline action must be APPROVE_CLAIM');

  return 1;
}
function destructionIntent_STAGE({ claim }: {
  claim: Claim
}) {
  assert(claim.stage !== CLP.Stage.DRAFT, 'Claim stage must not be draft');
  assert(!isTaxCreditOpportunity({ claim }), 'Claim is a tax credit opportunity. Destruction Intent stages are reserved for non-tax credit opportunities. Use ttbSubmission_STAGE instead.');

  if (claim.phase === CLP.Phase.SAP_ORDER_CREATION) return 1;
  if (claim.phase === CLP.Phase.CLAIM_APPROVAL    ) return 1;

  if (claim.phase === CLP.Phase.TTB_APPROVAL) unexpectedCodePath();

  if (claim.phase === CLP.Phase.DESTRUCTION) {
    if (claim.baseline?.action === CLP.Action.SEND_APPROVAL_TO_DESTROY   ) return 1;
    if (claim.baseline?.action === CLP.Action.CONFIRM_APPROVAL_TO_DESTROY) return 2;
    if (claim.baseline?.action === CLP.Action.WAITING_FOR_AODS           ) return 3;
    if (claim.baseline?.action === CLP.Action.CONFIRM_DESTRUCTION        ) return 3;
    unexpectedCodePath();
  }

  return 3;
}
function ttbSubmission_STAGE({ claim }: {
  claim: Claim
}) {
  assert(claim.stage !== CLP.Stage.DRAFT, 'Claim stage must not be draft');
  assert(isTaxCreditOpportunity({ claim }), 'Claim is not a tax credit opportunity. TTB Submission stages are reserved for tax credit opportunities. Use destructionIntent_STAGE instead.');
  assert(isTaxCreditRequested({ claim }) || ![CLP.Phase.TTB_APPROVAL].includes(claim.phase), 'Tax credit must be requested if in TTB Approval phase.');

  if (claim.phase === CLP.Phase.SAP_ORDER_CREATION) return 1;
  if (claim.phase === CLP.Phase.CLAIM_APPROVAL    ) return 1;

  if (claim.phase === CLP.Phase.TTB_APPROVAL) {
    if (claim.baseline?.action === CLP.Action.PREPARE_TTB_SUBMISSION  ) return 2;
    if (claim.baseline?.action === CLP.Action.SEND_TTB_SUBMISSION     ) return 3;
    if (claim.baseline?.action === CLP.Action.WAITING_FOR_TTB_APPROVAL) return 4;
    if (claim.baseline?.action === CLP.Action.CONFIRM_TTB_APPROVAL    ) return 4;
    unexpectedCodePath();
  }
  if (claim.phase === CLP.Phase.DESTRUCTION) {
    if (claim.baseline?.action === CLP.Action.SEND_APPROVAL_TO_DESTROY   ) return 5;
    if (claim.baseline?.action === CLP.Action.CONFIRM_APPROVAL_TO_DESTROY) return 6;
    if (claim.baseline?.action === CLP.Action.WAITING_FOR_AODS           ) return 7;
    if (claim.baseline?.action === CLP.Action.CONFIRM_DESTRUCTION        ) return 7;
    unexpectedCodePath();
  }

  return 7;
}
function destructionApprovals_STAGE({ claim }: {
  claim: Claim
}) {
  assert(claim.stage !== CLP.Stage.DRAFT, 'Claim stage must not be draft');

  if (claim.phase === CLP.Phase.SAP_ORDER_CREATION) return 1;
  if (claim.phase === CLP.Phase.CLAIM_APPROVAL    ) return 1;

  if (claim.phase === CLP.Phase.TTB_APPROVAL) {
    if (claim.baseline?.action === CLP.Action.PREPARE_TTB_SUBMISSION  ) return 1;
    if (claim.baseline?.action === CLP.Action.SEND_TTB_SUBMISSION     ) return 1;
    if (claim.baseline?.action === CLP.Action.WAITING_FOR_TTB_APPROVAL) return 2;
    if (claim.baseline?.action === CLP.Action.CONFIRM_TTB_APPROVAL    ) return 3;
    unexpectedCodePath();
  }
  if (claim.phase === CLP.Phase.DESTRUCTION) {
    if (claim.baseline?.action === CLP.Action.SEND_APPROVAL_TO_DESTROY   ) return 4;
    if (claim.baseline?.action === CLP.Action.CONFIRM_APPROVAL_TO_DESTROY) return 5;
    if (claim.baseline?.action === CLP.Action.WAITING_FOR_AODS           ) return 6;
    if (claim.baseline?.action === CLP.Action.CONFIRM_DESTRUCTION        ) return 6;
    unexpectedCodePath();
  }

  return 6;
}
function destruction_STAGE({ claim }: {
  claim: Claim
}) {
  assert(claim.stage !== CLP.Stage.DRAFT, 'Claim stage must not be draft');

  if (claim.phase === CLP.Phase.SAP_ORDER_CREATION) return 1;
  if (claim.phase === CLP.Phase.CLAIM_APPROVAL    ) return 1;
  if (claim.phase === CLP.Phase.TTB_APPROVAL      ) return 1;

  if (claim.phase === CLP.Phase.DESTRUCTION) {
    if (claim.baseline?.action === CLP.Action.SEND_APPROVAL_TO_DESTROY   ) return 1;
    if (claim.baseline?.action === CLP.Action.CONFIRM_APPROVAL_TO_DESTROY) return 1;
    if (claim.baseline?.action === CLP.Action.WAITING_FOR_AODS           ) return 2;
    if (claim.baseline?.action === CLP.Action.CONFIRM_DESTRUCTION        ) return 3;
    unexpectedCodePath();
  }

  return 4;
}

function accounting_STAGE({ claim }: {
  claim: Claim
}) {
  assert(claim.stage !== CLP.Stage.DRAFT, 'Claim stage must not be draft');

  if (claim.phase === CLP.Phase.SAP_ORDER_CREATION        ) return 1;
  if (claim.phase === CLP.Phase.CLAIM_APPROVAL            ) return 1;
  if (claim.phase === CLP.Phase.TTB_APPROVAL              ) return 1;
  if (claim.phase === CLP.Phase.DESTRUCTION               ) return 1;
  if (claim.phase === CLP.Phase.WHOLESALER_CREDIT_APPROVAL) {
    if (claim.baseline?.action === CLP.Action.CONFIRM_CLAIM_ENTERED_IN_SAP) return 2;
    if (claim.baseline?.action === CLP.Action.RECORD_CREDIT_DETAILS       ) return 3;
    if (claim.baseline?.action === CLP.Action.CONFIRM_WHOLESALER_CREDITED ) return 3;
    unexpectedCodePath();
  }

  return 4;
}

function issue_STAGE({ issue }: {
  issue?:     Issue,
  resolving?: boolean
}) {
  if(!issue) return 1;

  return 2;
}

export function isWholesalerDestructionComplete({ claim }: {
  claim: Claim
}) {
  return destruction_STAGE({ claim }) === 4;
}
export function showInstruction_PROMPT_getClaimApproval({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const processingBlocked = isProcessingBlocked_FOR_user({ claim, currentUser });
  const hasAccess         = currentUser.settings.persona === CLP.ActorType.DISTRIBUTOR;
  const appropriateState  = claim.baseline?.action === CLP.Action.APPROVE_CLAIM;

  return !processingBlocked && hasAccess && appropriateState;
}
export function showConfirmation_PROMPT_approveClaim({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const processingBlocked = isProcessingBlocked_FOR_user({ claim, currentUser });
  const hasAccess         = currentUser.settings.persona === CLP.ActorType.BBC_APPROVER;
  const appropriateState  = claim.baseline?.action === CLP.Action.APPROVE_CLAIM;

  return !processingBlocked && hasAccess && appropriateState;
}
export function showConfirmation_PROMPT_acceptClaim({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const processingBlocked = isProcessingBlocked_FOR_user({ claim, currentUser });
  const hasAccess         = currentUser.settings.persona === CLP.ActorType.RETURNS_COORDINATOR;
  const appropriateState  = claim.baseline?.action === CLP.Action.CONFIRM_CLAIM_ACCEPTANCE;

  return !processingBlocked && hasAccess && appropriateState;
}
export function showConfirmation_PROMPT_confirmSapOrderCreated({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const processingBlocked = isProcessingBlocked_FOR_user({ claim, currentUser });
  const hasAccess         = currentUser.settings.persona === CLP.ActorType.RETURNS_COORDINATOR;
  const appropriateState  = claim.baseline?.action === CLP.Action.CONFIRM_SAP_ORDER_CREATED;

  return !processingBlocked && hasAccess && appropriateState;
}
export function showConfirmation_PROMPT_CLAIM_APPROVAL_complete({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const processingBlocked = isProcessingBlocked_FOR_user({ claim, currentUser });
  const hasAccess         = !CLM.isLocked_claimApprovalForm({ claim, currentUser });
  const appropriateState  = CLM.sa_claimState_isConfirm(claim);

  return !processingBlocked && hasAccess && appropriateState;
}
export function showConfirmation_PROMPT_1({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const processingBlocked = isProcessingBlocked_FOR_user({ claim, currentUser });
  const hasAccess         = when_RC_user({ currentUser });
  const appropriateStage  = ttbSubmission_STAGE({ claim }) === 2;

  return !processingBlocked && hasAccess && appropriateStage;
}
export function showConfirmation_PROMPT_2({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const processingBlocked = isProcessingBlocked_FOR_user({ claim, currentUser });
  const hasAccess         = when_RC_user({ currentUser });
  const appropriateStage  = ttbSubmission_STAGE({ claim }) === 3;

  return !processingBlocked && hasAccess && appropriateStage;
}
export function showConfirmation_PROMPT_3({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const processingBlocked = isProcessingBlocked_FOR_user({ claim, currentUser });
  const hasAccess         = when_RC_user({ currentUser });
  const appropriateStage  = destructionApprovals_STAGE({ claim }) === 3
                         || destructionApprovals_STAGE({ claim }) > 3 && !isApprovedByTtb({ claim });

  return !processingBlocked && hasAccess && appropriateStage;
}
export function showConfirmation_PROMPT_4({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const processingBlocked = isProcessingBlocked_FOR_user({ claim, currentUser });
  const hasAccess         = when_RC_user({ currentUser });
  const appropriateStage  = destructionApprovals_STAGE({ claim }) === 5;

  return !processingBlocked && hasAccess && appropriateStage;
}
export function showConfirmation_PROMPT_5({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const processingBlocked = isProcessingBlocked_FOR_user({ claim, currentUser });
  const hasAccess         = when_RC_user({ currentUser });
  const appropriateStage  = destruction_STAGE({ claim }) === 3;

  return !processingBlocked && hasAccess && appropriateStage;
}

export function showConfirmation_PROMPT_ttbFollowUp({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const processingBlocked = isProcessingBlocked_FOR_user({ claim, currentUser });
  const hasAccess         = when_RC_user({ currentUser });
  const appropriateStage  = destructionApprovals_STAGE({ claim }) === 2;
  const dueForFollowUp    = isTtbApprovalDueForFollowUp({ claim });
  const followUpAddressed = claim.ttb_ttbFollowUpConfirmedAt
                         || claim.ttb_skipTtbFollowUp;

  return !processingBlocked && hasAccess && appropriateStage && dueForFollowUp && !followUpAddressed
}
export function showConfirmation_PROMPT_overdueTtbApproval({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const processingBlocked = isProcessingBlocked_FOR_user({ claim, currentUser });
  const hasAccess         = when_RC_user({ currentUser });
  const appropriateStage  = destructionApprovals_STAGE({ claim }) === 2;
  const overdue           = isTtbApprovalOverdue({ claim });

  return !processingBlocked && hasAccess && appropriateStage && overdue;
}
export function showCreditMemoDate({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const sections = accounting_SECTIONS({ claim, currentUser });

  return !sections.section_1.readonly || claim.acc_creditMemoDate !== undefined;
}

export function showButton_MODAL_skipTtbApproval({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const hasAccess         = when_RC_user({ currentUser });
  const appropriateStatus = isTaxCreditRequested({ claim })
                            && !claim.skipTtb
                            && claim.bbcStatus === CLP.BbcStatus.PENDING_TTB_APPROVAL;

  return hasAccess && appropriateStatus;
}

export function showButton_MODAL_issue({ claim, currentUser }: {
  claim:       Claim,
  currentUser: User,
}) {
  const hasAccess         = (when_WCA_user   ({ currentUser })
                          ||  when_WCA_AS_user({ currentUser })
                          ||  when_ARS_user   ({ currentUser }));
  const appropriateStatus = claim.bbcStatus === CLP.BbcStatus.PENDING_WHOLESALER_CREDIT;

  return hasAccess && appropriateStatus;
}

// #endregion
// #region Claim - Form Sections

const section_ = ({ isActive, hasAccess, stage, disabledStages, optionalStages, readonlyStages }: {
  isActive?:      boolean,
  hasAccess:      boolean,
  stage:          number,
  disabledStages: number[],
  optionalStages: number[],
  readonlyStages: number[],
}) => {
  const active = isActive ?? true;
  return section({
    inactive: !active,
    disabled: active && (disabledStages.includes(stage) &&  hasAccess),
    optional: active && (optionalStages.includes(stage) &&  hasAccess),
    readonly: active && (readonlyStages.includes(stage) || !hasAccess),
  });
}
export const summary_SECTIONS = ({ claim, currentUser }: {
  claim:       CLM.Claim,
  currentUser: CLM.User
}) => ({
  section_1: section_({
    hasAccess:      !CLM.isLocked_summaryForm({ claim, currentUser }),
    stage:          summary_STAGE({ claim }),
    disabledStages: [],
    optionalStages: [],
    readonlyStages: [2],
  }),
  section_2: section_({
    hasAccess:      !CLM.isLocked_summaryForm({ claim, currentUser }),
    stage:          summary_STAGE({ claim }),
    disabledStages: [],
    optionalStages: [],
    readonlyStages: [2],
  }),
  section_3: section_({
    hasAccess:      !CLM.isLocked_summaryForm({ claim, currentUser }),
    stage:          summary_STAGE({ claim }),
    disabledStages: [],
    optionalStages: [],
    readonlyStages: [2],
  }),
  section_4: section_({
    hasAccess:      when_RC_user({ currentUser }),
    stage:          summary_STAGE({ claim }),
    disabledStages: [],
    optionalStages: [],
    readonlyStages: [2, 3],
  }),
});
export const firstLevelApproval_SECTIONS = ({ claim, currentUser }: {
  claim:       CLM.Claim,
  currentUser: CLM.User
}) => ({
  section_1: section_({
    hasAccess:      when_BBC_APR_user({ currentUser }),
    stage:          firstLevelApproval_STAGE({ claim }),
    disabledStages: [],
    optionalStages: [],
    readonlyStages: [],
  })
});
export const destructionIntent_SECTIONS = ({ claim, currentUser }: {
  claim:       CLM.Claim,
  currentUser: CLM.User
}) => ({
  section_1: section_({
    hasAccess:      when_RC_user({ currentUser }),
    stage:          destructionIntent_STAGE({ claim }),
    disabledStages: [],
    optionalStages: [1],
    readonlyStages: [3],
  })
});

export const ttbSubmission_SECTIONS = ({ claim, currentUser }: {
  claim:       CLM.Claim,
  currentUser: CLM.User
}) => {
  const skipTtb                = claim.skipTtb;
  const ttbSubmissionConfirmed = isSubmissionToTTBConfirmed({ claim });
  const hasData_IN_section3    = claim.ttb_dateFaxedToTTB    !== undefined
                              || claim.ttb_noticeOfIntent_id !== undefined
                              || claim.ttb_faxCoverSheet_id  !== undefined;
  const ALL = [1, 2, 3, 4, 5, 6, 7];
  return ({
    section_1: section_({
      hasAccess:      when_RC_user({ currentUser }),
      stage:          ttbSubmission_STAGE({ claim }),
      disabledStages: [],
      optionalStages: skipTtb && !ttbSubmissionConfirmed ? [1, 5] : [1, 2],
      readonlyStages: skipTtb && !ttbSubmissionConfirmed ? [7] : [5, 6, 7],
    }),
    section_2: section_({
      isActive:       !skipTtb || (skipTtb && ttbSubmissionConfirmed),
      hasAccess:      when_RC_user({ currentUser }),
      stage:          ttbSubmission_STAGE({ claim }),
      disabledStages: skipTtb ? []  : [1],
      optionalStages: skipTtb ? []  : [2],
      readonlyStages: skipTtb ? ALL : [5, 6, 7],
    }),
    section_3: section_({
      isActive:       !skipTtb || hasData_IN_section3,
      hasAccess:      when_RC_user({ currentUser }),
      stage:          ttbSubmission_STAGE({ claim }),
      disabledStages: skipTtb ? []  : [1, 2],
      optionalStages: skipTtb ? []  : [3],
      readonlyStages: skipTtb ? ALL : [5, 6, 7],
    }),
    section_skipTtb: section_({
      isActive:       skipTtb && !ttbSubmissionConfirmed,
      hasAccess:      when_RC_user({ currentUser }),
      stage:          ttbSubmission_STAGE({ claim }),
      disabledStages: [],
      optionalStages: [],
      readonlyStages: ALL,
    }),
  });
}
export const destructionApprovals_SECTIONS = ({ claim, currentUser }: {
  claim:       CLM.Claim,
  currentUser: CLM.User
}) => {
  const skipTtb                = claim.skipTtb;
  const ttbSubmissionConfirmed = isSubmissionToTTBConfirmed({ claim });
  const hasData_IN_section1    = claim.ttb_skipTtbFollowUp
                              || claim.ttb_ttbFollowUpConfirmedAt        !== undefined
                              || claim.ttb_overdueTtbApprovalConfirmedAt !== undefined
                              || claim.ttb_ttbApprovalDate               !== undefined
                              || claim.ttb_ttbApproval_id                !== undefined;
  const ALL = [1, 2, 3, 4, 5, 6];
  return {
    section_1a: section_({
      isActive:       CLM.isTaxCreditOpportunity({ claim }) && (!skipTtb || ttbSubmissionConfirmed || hasData_IN_section1),
      hasAccess:      when_RC_user({ currentUser }),
      stage:          destructionApprovals_STAGE({ claim }),
      disabledStages: skipTtb ? []  : [1],
      optionalStages: skipTtb ? []  : [2],
      readonlyStages: skipTtb ? ALL : [4, 5, 6],
    }),
    section_1b: section_({
      isActive:       CLM.isTaxCreditOpportunity({ claim }) && (!skipTtb || ttbSubmissionConfirmed || hasData_IN_section1),
      hasAccess:      when_RC_user({ currentUser }),
      stage:          destructionApprovals_STAGE({ claim }),
      disabledStages: skipTtb && !ttbSubmissionConfirmed ? []  : CLM.isApprovedByTtb({ claim }) ? [1]       : [1],
      optionalStages: skipTtb && !ttbSubmissionConfirmed ? []  : CLM.isApprovedByTtb({ claim }) ? [2]       : [2, 4, 5, 6],
      readonlyStages: skipTtb && !ttbSubmissionConfirmed ? ALL : CLM.isApprovedByTtb({ claim }) ? [4, 5, 6] : [],
    }),
    section_2: section_({
      hasAccess:      when_RC_user({ currentUser }),
      stage:          destructionApprovals_STAGE({ claim }),
      disabledStages: [1, 2, 3],
      optionalStages: [4],
      readonlyStages: [6],
    }),
    section_skipTtb: section_({
      isActive:       skipTtb && ttbSubmissionConfirmed,
      hasAccess:      when_RC_user({ currentUser }),
      stage:          destructionApprovals_STAGE({ claim }),
      disabledStages: [],
      optionalStages: [],
      readonlyStages: ALL,
    }),
  };
};
export const destruction_SECTIONS = ({ claim, currentUser }: {
  claim:       CLM.Claim,
  currentUser: CLM.User
}) => ({
  section_1: section_({
    hasAccess:      when_RC_user({ currentUser }),
    stage:          destruction_STAGE({ claim }),
    disabledStages: [1],
    optionalStages: [2],
    readonlyStages: [4],
  }),
});
export const accounting_SECTIONS = ({ claim, currentUser }: {
  claim:       CLM.Claim,
  currentUser: CLM.User
}) => ({
  section_1: section_({
    hasAccess:      when_WCA_user({ currentUser }) || when_ARS_user({ currentUser }),
    stage:          accounting_STAGE({ claim }),
    disabledStages: [1, 2],
    optionalStages: [3],
    readonlyStages: [4],
  }),
  section_creditProcessingNotes: section_({
    hasAccess:      when_WCA_user({ currentUser }) || when_WCA_AS_user({ currentUser }) || when_ARS_user({ currentUser }),
    stage:          accounting_STAGE({ claim }),
    disabledStages: [1],
    optionalStages: when_WCA_AS_user({ currentUser }) ? [2] : [2, 3],
    readonlyStages: when_WCA_AS_user({ currentUser }) ? [3, 4] : [4],
  }),
  section_2: section_({
    hasAccess:      false,
    stage:          1,
    disabledStages: [],
    optionalStages: [],
    readonlyStages: [1],
  }),
});
export const skipTtbApproval_SECTIONS = ({ claim, currentUser }: {
  claim:       CLM.Claim,
  currentUser: CLM.User
}) => ({
  section_1: section_({
    hasAccess:      when_RC_user({ currentUser }),
    stage:          1,
    disabledStages: [],
    optionalStages: [],
    readonlyStages: [],
  }),
  section_2: section_({
    isActive:       !CLM.isSubmissionToTTBConfirmed({ claim }),
    hasAccess:      when_RC_user({ currentUser }),
    stage:          1,
    disabledStages: [],
    optionalStages: [],
    readonlyStages: [],
  }),
});
export const issue_SECTIONS = ({ claim, issue, currentUser, editing, resolving }: {
  claim:       CLM.Claim,
  issue?:      CLM.Issue,
  currentUser: CLM.User,
  editing?:    boolean,
  resolving?:  boolean,
}) => ({
  section_1: section_({
    isActive:       issue === undefined || editing,
    hasAccess:      when_RC_user({ currentUser }) || when_WCA_user({ currentUser }) || when_WCA_AS_user({ currentUser }) || when_ARS_user({ currentUser }),
    stage:          issue_STAGE({ issue }),
    disabledStages: [],
    optionalStages: [],
    readonlyStages: [],
  }),
  section_2: section_({
    isActive:       issue !== undefined && !resolving && !editing,
    hasAccess:      when_RC_user({ currentUser }) || when_WCA_user({ currentUser }) || when_WCA_AS_user({ currentUser }) || when_ARS_user({ currentUser }),
    stage:          issue_STAGE({ issue }),
    disabledStages: [],
    optionalStages: [],
    readonlyStages: [],
  }),
  section_3: section_({
    isActive:       (issue !== undefined && resolving) || (editing && isResolved({ issue })),
    hasAccess:      when_RC_user({ currentUser }) || when_WCA_user({ currentUser }) || when_WCA_AS_user({ currentUser }) || when_ARS_user({ currentUser }),
    stage:          issue_STAGE({ issue }),
    disabledStages: [],
    optionalStages: [],
    readonlyStages: [],
  }),
});

export const claims_distributor_summary_SECTIONS = ({ claim, currentUser }: {
    claim:       CLM.Claim,
    currentUser: CLM.User,
  }) => ({
    section_1: section_({
      isActive:       claim !== undefined,
      hasAccess:      when_RC_user({ currentUser }) || when_WCA_user({ currentUser }) || when_WCA_AS_user({ currentUser }) || when_ARS_user({ currentUser }),
      stage:          1,
      disabledStages: [],
      optionalStages: [],
      readonlyStages: [],
    })
  });

  export const claims_distributor_approvals_SECTIONS = ({ claim, currentUser }: {
    claim:       CLM.Claim,
    currentUser: CLM.User
  }) => ({
    section_1: section_({
      hasAccess:      when_DIS_user({ currentUser }),
      stage:          1,
      disabledStages: [],
      optionalStages: [],
      readonlyStages: [],
    })
  });

  export const claims_distributor_destruction_SECTIONS = ({ claim, currentUser }: {
    claim:       CLM.Claim,
    currentUser: CLM.User
  }) => ({
    section_1: section_({
      hasAccess:      when_DIS_user({ currentUser }),
      stage:          1,
      disabledStages: [],
      optionalStages: [],
      readonlyStages: [],
    })
  });
  export const claims_distributor_credit_SECTIONS = ({ claim, currentUser }: {
    claim:       CLM.Claim,
    currentUser: CLM.User
  }) => ({
    section_1: section_({
      hasAccess:      when_DIS_user({ currentUser }),
      stage:          1,
      disabledStages: [],
      optionalStages: [],
      readonlyStages: [],
    })
  });
  export const claims_distributor_files_SECTIONS = ({ claim, currentUser }: {
    claim:       CLM.Claim,
    currentUser: CLM.User
  }) => ({
    section_1: section_({
      hasAccess:      when_DIS_user({ currentUser }),
      stage:          1,
      disabledStages: [],
      optionalStages: [],
      readonlyStages: [],
    })
  });

export const files_SECTIONS = ({ claim, currentUser }: {
  claim:       CLM.Claim,
  currentUser: CLM.User
}) => ({
  section_1: section_({
    hasAccess:      when_RC_user({ currentUser }) || when_WCA_user({ currentUser }),
    stage:          1,
    disabledStages: [],
    optionalStages: [],
    readonlyStages: [],
  })
});

export const selectProducts_SECTIONS = ({ claim, currentUser }: {
  claim:       CLM.Claim,
  currentUser: CLM.User
}) => ({
  section_1: section_({
    hasAccess:      when_DIS_user({ currentUser }),
    stage:          1,
    disabledStages: [],
    optionalStages: [],
    readonlyStages: [],
  })
});

// #endregion
// #region Claim - Test Data Collection

export const isIncludedInCollection = (collection: SYS.TestDataCollection) => (_: Claim) => {
  switch (collection) {
    case SYS.TestDataCollection.TEST_CLAIMS_1:
    case SYS.TestDataCollection.TEST_CLAIMS_2:
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_1:
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_2:
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_3:
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_4:
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_5:
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_6:
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_7:
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_8:
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_9:
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_10:
    case SYS.TestDataCollection.OLD_TEST_CLAIMS:
      assert(collection.data.prefix !== undefined, `Test data collection ${collection} does not have a prefix defined.`);
      return _.claimNumber.substring(1,3) === CLM.claimNumberPrefix_asString({ prefix: collection.data.prefix });
    default:
      unexpectedCodePath(`Test data collection ${collection} is not supported.`);
  }
}

// #endregion
// #region Claim - Extension One

  export type ClaimExtensionOne = {
    __stageTesting_variant: string | undefined;
    __stageTesting_stage:   number | undefined;

    legalStage:             number;

    destructorType:         string | undefined;
    destructionCompanyName: string;
    streetAddress:          string;
    city:                   string;
    state:                  string;
    zipCode:                string;
    destructionMethod:      string;

    totalAlcoholicCases:    string;
    totalBarrels:           string;
    totalTaxAmount:         string;
    productionLocations:    string[];

    dateFaxedToTTB: string;
    noticeOfIntent: FileInfo | undefined;
    faxCoverSheet:  FileInfo | undefined;

    ttbApprovalDeadline:    string;
    ttbApprovalDate:        string;
    ttbApproval:            FileInfo | undefined;
    approvedForDestruction: boolean;

    destructionApprovalDate: string;
    destructionApproval:     FileInfo | undefined;

    earliestDestructionDate: string;
    casesDestroyed: string;
    reviewNotes:    string;
    // affidavits:  FileInfo[];
    affidavit1:     FileInfo | undefined;
    affidavit2:     FileInfo | undefined;
    affidavit3:     FileInfo | undefined;
    affidavit4:     FileInfo | undefined;
    affidavit5:     FileInfo | undefined;

    affidavitComplete: boolean;
  }
  export type ClaimExtensionOneRaw = {
    __stageTesting_variant: string | undefined;
    __stageTesting_stage:   number | undefined;

    legalStage:             number;

    destructorType:         string | undefined;
    destructionCompanyName: string;
    streetAddress:          string;
    city:                   string;
    state:                  string;
    zipCode:                string;
    destructionMethod:      string;

    totalAlcoholicCases:    string;
    totalBarrels:           string;
    totalTaxAmount:         string;
    productionLocations:    string[];

    dateFaxedToTTB: string;
    noticeOfIntent: FileInfo | undefined;
    faxCoverSheet:  FileInfo | undefined;

    ttbApprovalDeadline:    string;
    ttbApprovalDate:        string;
    ttbApproval:            FileInfo | undefined;
    approvedForDestruction: boolean;

    destructionApprovalDate: string;
    destructionApproval:     FileInfo | undefined;

    earliestDestructionDate: string;
    casesDestroyed:     string;
    reviewNotes:        string;
    // affidavits:      FileInfo[];
    affidavit1:         FileInfo | undefined;
    affidavit2:         FileInfo | undefined;
    affidavit3:         FileInfo | undefined;
    affidavit4:         FileInfo | undefined;
    affidavit5:         FileInfo | undefined;

    affidavitComplete: boolean;
  }
  export const ce1_toRaw = (source: ClaimExtensionOne): ClaimExtensionOneRaw => {
    return {
      ...source
    };
  }
  export const ce1_fromRaw = (source: ClaimExtensionOneRaw): ClaimExtensionOne => {
    return {
      ...source
    };
  }

// #endregion
// #region Claim - Upper Level Claim Approval

export type UpperLevelClaimApproval = {
  id:                   UpperLevelClaimApprovalId;
  claimId:              ClaimId;

  claimApprover_level:  ManagerLevel;
  claimApprover_name?:  string;

  requestedAt?:         Date;
  approvalDeadline?:    Date;
  approvedAt?:          Date;

  approval_id?:         bigint;
  approval_fileName?:   string;
  approval_uploadedAt?: Date;
}
export type RawUpperLevelClaimApproval = Omit<UpperLevelClaimApproval, "id" | "claimId" | "claimApprover_level" | "requestedAt" | "approvalDeadline" | "approvedAt" | "approval_id" | "approval_uploadedAt"> & {
  id:                   string;
  claimId:              string;

  claimApprover_level:  string;

  requestedAt?:         string;
  approvalDeadline?:    string;
  approvedAt?:          string;

  approval_id?:         string;
  approval_uploadedAt?: string;
}

export const ulcaValidator = validator((ulca: UpperLevelClaimApproval) => {
  const { FOR, OBJ, NOW } = objectValidator(ulca);
  return ({
    a1: FOR(_ => _.id     ).ALWAYS_IS_DEFINED,
    a2: FOR(_ => _.claimId).ALWAYS_IS_DEFINED,

    b1: FOR(_ => _.claimApprover_level).ALWAYS_IS_DEFINED,
    b2: FOR(_ => _.claimApprover_level).ALWAYS(isIncludedIn(managerLevels.filter(_ => _.upperLevelApprover))),
    b3: FOR(_ => _.claimApprover_name ).WHEN_DEFINED_THEN(isStandardText),

    c1: FOR(_ => _.requestedAt     ).WHEN_DEFINED_THEN(isBefore            (NOW            )),
    // c2: FOR(_ => _.approvalDeadline).WHEN_DEFINED_THEN(isNotBefore_OPTIONAL(OBJ.requestedAt)),
    // c3: FOR(_ => _.approvedAt      ).WHEN_DEFINED_THEN(isNotBefore_OPTIONAL(OBJ.requestedAt)),
    c4: FOR(_ => _.approvedAt      ).WHEN_DEFINED_THEN(isBefore            (NOW            )),

    // d1: FOR(_ => _.approvalDeadline).WHEN_DEFINED_THEN(isDefined(OBJ.requestedAt)),
    // d2: FOR(_ => _.approvedAt      ).WHEN_DEFINED_THEN(isDefined(OBJ.requestedAt)),

    e1: FOR(_ => _.approval_id        ).WHEN_DEFINED_THEN(isId         ),
    e2: FOR(_ => _.approval_fileName  ).WHEN_DEFINED_THEN(isFileName   ),
    e3: FOR(_ => _.approval_uploadedAt).WHEN_DEFINED_THEN(isBefore(NOW)),

    f4: FOR(_ => _.approval_id        ).WHEN_DEFINED_THEN(isDefined(ulca.approval_fileName  )),
    f5: FOR(_ => _.approval_id        ).WHEN_DEFINED_THEN(isDefined(ulca.approval_uploadedAt)),
    f6: FOR(_ => _.approval_fileName  ).WHEN_DEFINED_THEN(isDefined(ulca.approval_id        )),
    f7: FOR(_ => _.approval_uploadedAt).WHEN_DEFINED_THEN(isDefined(ulca.approval_id        )),
  });
});

export const toRawUlca = (_: UpperLevelClaimApproval): RawUpperLevelClaimApproval => {
  return {
    ..._,
    id:                  _.id     .asInt.toString(),
    claimId:             _.claimId.asInt.toString(),

    claimApprover_level: _.claimApprover_level.identifier,

    requestedAt:         _.requestedAt     ?.toISOString(),
    approvalDeadline:    _.approvalDeadline?.toISOString(),
    approvedAt:          _.approvedAt      ?.toISOString(),

    approval_id:         _.approval_id        ?.toString(),
    approval_uploadedAt: _.approval_uploadedAt?.toISOString(),
  };
}
export const fromRawUlca = (_: RawUpperLevelClaimApproval): UpperLevelClaimApproval => {
  return {
    ..._,
    id:      Id.from(BigInt(_.id     )) as UpperLevelClaimApprovalId,
    claimId: Id.from(BigInt(_.claimId)) as ClaimId,

    claimApprover_level: managerLevel(_.claimApprover_level),

    requestedAt:      _.requestedAt      === undefined ? undefined : new Date(_.requestedAt     ),
    approvalDeadline: _.approvalDeadline === undefined ? undefined : new Date(_.approvalDeadline),
    approvedAt:       _.approvedAt       === undefined ? undefined : new Date(_.approvedAt      ),

    approval_id:         _.approval_id         === undefined ? undefined : BigInt(_.approval_id),
    approval_uploadedAt: _.approval_uploadedAt === undefined ? undefined : new Date(_.approval_uploadedAt),
  };
}

export const ulcaErrors = (_: UpperLevelClaimApproval) => ulcaValidator.validationErrors(_);

export const isValidUlca = (_: UpperLevelClaimApproval) => ulcaValidator.isValid(_);
export const validateUlcas = (ulcas: UpperLevelClaimApproval[]) => {

  const validator = ulcaValidator;

  const validationErrors = ulcas
    .filter(_ => !validator.isValid(_))
    .map(_ => ({ ulca: _, errors: validator.validationErrors(_) }));

  assert(
    validationErrors.length === 0,
    `Errors found in defined Upper Level Claim Approvals with following error codes. ${JSON.stringify(validationErrors, stringifyReplacer, 2)}`
  );
}
export const validateRawUlcas = (rawUlcas: RawUpperLevelClaimApproval[]) => {

  const ulcas = rawUlcas.map(fromRawUlca);

  validateUlcas(ulcas);

  return ulcas;
}

// Claim Approval (SA) - Claim States

export const sa_claimState_isSendRequests = (_: Claim) => _.phase === CLP.Phase.CLAIM_APPROVAL && _.baseline?.action === CLP.Action.SEND_CLAIM_APPROVAL_REQUESTS;
export const sa_claimState_isWaitingFor   = (_: Claim) => _.phase === CLP.Phase.CLAIM_APPROVAL && _.baseline?.action === CLP.Action.WAITING_FOR_CLAIM_APPROVALS && _.assigned?.action === undefined;
export const sa_claimState_isFollowUp     = (_: Claim) => _.phase === CLP.Phase.CLAIM_APPROVAL && _.baseline?.action === CLP.Action.WAITING_FOR_CLAIM_APPROVALS && _.assigned?.action === CLP.Action.FOLLOW_UP_ON_CLAIM_APPROVALS;
export const sa_claimState_isConfirm      = (_: Claim) => _.phase === CLP.Phase.CLAIM_APPROVAL && _.baseline?.action === CLP.Action.CONFIRM_CLAIM_APPROVALS;
export const sa_claimState_isApproved     = (_: Claim) => ![CLP.Phase.CLAIM_SUBMISSION, CLP.Phase.CLAIM_CREATION, CLP.Phase.CLAIM_INTAKE, CLP.Phase.SAP_ORDER_CREATION, CLP.Phase.CLAIM_APPROVAL].includes(_.phase);

// Upper Level Claim Approval (ULCA) - Approval States

export const ulca_approvalState_isRequested = (_: UpperLevelClaimApproval) => {
  return _.claimApprover_level !== undefined
      && _.claimApprover_name  !== undefined
      && _.requestedAt         !== undefined
      && _.approvalDeadline    !== undefined;
}
export const ulca_approvalState_isApproved = (_: UpperLevelClaimApproval) => {
  return ulca_approvalState_isRequested(_)
      && _.approvedAt          !== undefined
      && _.approval_id         !== undefined
      && _.approval_fileName   !== undefined
      && _.approval_uploadedAt !== undefined;
}
export const ulca_approvalState_isOverdue = ({ now }: { now: Date }) => (_: UpperLevelClaimApproval) => {
  return ulca_approvalState_isRequested(_)
      && !ulca_approvalState_isApproved(_)
      && _.approvalDeadline! < now;
}

export const sa_approval_fileInfo = ({ claim }: {
  claim: Claim,
}) => (ulca: UpperLevelClaimApproval): FileInfo | undefined => {
  if (ulca.approval_id === undefined) {
    return undefined;
  }

  const parts = ulca.approval_fileName?.split('.');
  const fileType = parts && parts.length > 1 ? parts.pop() : undefined;

  return fileInfo({
    parentId:   claim.id.asInt,
    childId:    ulca.approval_id,
    name:       CLM.fileName_claimApproval(claim, { ulca, fileType }),
    uploadedAt: optionalDateTime(ulca.approval_uploadedAt),
  });
}

// #endregion

// #region Claim - Issue

export type Issue = {
  id:                       CLM.IssueId;
  claimId:                  CLM.ClaimId;
  issueType:                CLM.IssueType;
  description:              string;
  assignedTo:               CLM.UserGroup;

  reportedAt:               Date;
  reportedBy:               SYS.ActorId;
  reportedByName?:          string;

  confirmedResolvedAt?:     Date;
  confirmedResolvedBy?:     SYS.ActorId;
  confirmedResolvedByName?: string;

  issueResolutionNotes:     string;

  reopenedAt?:              Date;
  reopenedBy?:              SYS.ActorId;
  reopenedByName?:          string;
}

export type RawIssue = Omit<Issue, "id" | "claimId" | "issueType" | "assignedTo" | "reportedAt" | "reportedBy" | "confirmedResolvedAt" | "confirmedResolvedBy" | "reopenedAt" | "reopenedBy"> & {
  id:                       string;
  resource:                 string;
  issueType:                string;
  assignedTo:               string;
  reportedAt:               string;
  reportedBy:               string;
  reportedByName?:          string;
  confirmedResolvedAt?:     string;
  confirmedResolvedBy?:     string;
  confirmedResolvedByName?: string;
  reopenedAt?:              string;
  reopenedBy?:              string;
  reopenedByName?:          string;
}

export const issueValidator = validator((issue: Issue) => {
  const { FOR, NOW } = objectValidator(issue);
  return ({
    a1: FOR(_ => _.id).ALWAYS_IS_DEFINED,

    b1: FOR(_ => _.claimId).ALWAYS_IS_DEFINED,

    c1: FOR(_ => _.issueType).ALWAYS_IS_DEFINED,
    c2: FOR(_ => _.issueType).ALWAYS(isIncludedIn(issueTypes)),

    c3: FOR(_ => _.description).ALWAYS(isStandardText),

    d1: FOR(_ => _.assignedTo).ALWAYS_IS_DEFINED,
    d2: FOR(_ => _.assignedTo).ALWAYS(isIncludedIn(userGroups)),

    e1: FOR(_ => _.reportedAt).ALWAYS_IS_DEFINED,
    e2: FOR(_ => _.reportedAt).ALWAYS(isBefore(NOW)),
    e3: FOR(_ => _.reportedBy).ALWAYS_IS_DEFINED,

    f1: FOR(_ => _.confirmedResolvedAt).WHEN_DEFINED_THEN(isBefore(NOW)),
    f2: FOR(_ => _.confirmedResolvedBy).WHEN_DEFINED_THEN(isDefined(issue.confirmedResolvedAt)),

    g1: FOR(_ => _.issueResolutionNotes).WHEN_DEFINED_THEN(isStandardText),

    h1: FOR(_ => _.reopenedAt).WHEN_DEFINED_THEN(isBefore(NOW)),
    h2: FOR(_ => _.reopenedBy).WHEN_DEFINED_THEN(isDefined(issue.reopenedAt)),
  });
});

export const toRawIssue = (_: Issue): RawIssue => {
  return {
    ..._,
    id:                  _.id.asInt     .toString(),
    resource:            _.claimId.asInt.toString(),

    issueType:           _.issueType.identifier,

    assignedTo:          _.assignedTo.identifier,

    reportedAt:          _.reportedAt          .toISOString(),
    reportedBy:          _.reportedBy.asInt    .toString(),

    confirmedResolvedAt: _.confirmedResolvedAt?.toISOString(),
    confirmedResolvedBy: _.confirmedResolvedBy?.asInt.toString(),

    reopenedAt:          _.reopenedAt          ?.toISOString(),
    reopenedBy:          _.reopenedBy          ?.asInt.toString(),
  };
}

export const fromRawIssue = (_: RawIssue): Issue => {
  return {
    ..._,
    id:                  Id.from(BigInt(_.id     )) as CLM.IssueId,
    claimId:             Id.from(BigInt(_.resource)) as CLM.ClaimId,

    issueType:           issueType(_.issueType),

    assignedTo:          userGroup(_.assignedTo),

    reportedAt:          new Date(_.reportedAt),
    reportedBy:          Id.from (BigInt(_.reportedBy)),

    confirmedResolvedAt: _.confirmedResolvedAt === undefined ? undefined : new Date(_.confirmedResolvedAt),
    confirmedResolvedBy: _.confirmedResolvedBy === undefined ? undefined : Id.from (BigInt(_.confirmedResolvedBy)) as SYS.ActorId,

    reopenedAt:          _.reopenedAt          === undefined ? undefined : new Date(_.reopenedAt),
    reopenedBy:          _.reopenedBy          === undefined ? undefined : Id.from (BigInt(_.reopenedBy)) as SYS.ActorId,
  };
}

export const issueErrors = (_: Issue) => issueValidator.validationErrors(_);

export const isValidIssue = (_: Issue) => issueValidator.isValid(_);
export const validateIssues = (issues: Issue[]) => {

  const validator = issueValidator;

  const validationErrors = issues
    .filter(_ => !validator.isValid(_))
    .map(_ => ({ issue: _, errors: validator.validationErrors(_) }));

  assert(
    validationErrors.length === 0,
    `Errors found in defined Issues with following error codes. ${JSON.stringify(validationErrors, stringifyReplacer, 2)}`
  );
}

export const validateRawIssues = (rawIssues: RawIssue[]) => {

  const issues = rawIssues.map(fromRawIssue);

  validateIssues(issues);

  return issues;
}

export function isResolved({ issue }: { issue?: Issue }) {
  return issue !== undefined && issue.confirmedResolvedAt !== undefined;
}

export function isReopened({ issue }: { issue?: Issue }) {
  return issue !== undefined && issue.reopenedAt !== undefined;
}

// #endregion

// #region Claim - Locked Fields

const when_RC_user      = ({ currentUser }: { currentUser: CLM.User }) => CLP.ActorType.RETURNS_COORDINATOR                    === currentUser.settings.persona;
const when_WCA_user     = ({ currentUser }: { currentUser: CLM.User }) => CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT           === currentUser.settings.persona;
const when_WCA_AS_user  = ({ currentUser }: { currentUser: CLM.User }) => CLP.ActorType.WHOLESALER_CREDIT_ACCOUNTANT_ASSISTANT === currentUser.settings.persona;
const when_ARS_user     = ({ currentUser }: { currentUser: CLM.User }) => CLP.ActorType.ACCOUNTS_RECEIVABLE_SUPERVISOR         === currentUser.settings.persona;
const when_BBC_APR_user = ({ currentUser }: { currentUser: CLM.User }) => CLP.ActorType.BBC_APPROVER                           === currentUser.settings.persona;
const when_DIS_user     = ({ currentUser }: { currentUser: CLM.User }) => CLP.ActorType.DISTRIBUTOR                            === currentUser.settings.persona;

const when_claimApproval_phase             = ({ claim }: { claim: Claim }) => [CLP.Phase.CLAIM_APPROVAL                     ].includes(claim.phase);
const when_destructionActivities_phases    = ({ claim }: { claim: Claim }) => [CLP.Phase.TTB_APPROVAL, CLP.Phase.DESTRUCTION].includes(claim.phase);
const when_wholesalerCreditApproval_phases = ({ claim }: { claim: Claim }) => [CLP.Phase.WHOLESALER_CREDIT_APPROVAL         ].includes(claim.phase);

export function isLocked_summaryForm({ claim, currentUser }: {
  claim:       Claim,
  currentUser: CLM.User
 }) {
  return !when_RC_user({ currentUser })
      && (!when_wholesalerCreditApproval_phases({ claim }) || !when_WCA_user({ currentUser }));
}
export function isLocked_claimApprovalForm({ claim, currentUser }: {
  claim:       Claim,
  currentUser: CLM.User
 }) {
  return !when_claimApproval_phase({ claim }) || !when_RC_user({ currentUser });
}
export function isLocked_destructionIntentForm({ claim, currentUser }: {
  claim:       Claim,
  currentUser: CLM.User
 }) {
  return !when_destructionActivities_phases({ claim }) || !when_RC_user({ currentUser });
}
export function isLocked_destructionForms({ claim, currentUser }: {
  claim:       Claim,
  currentUser: CLM.User
 }) {
  return !when_destructionActivities_phases({ claim }) || !when_RC_user({ currentUser });
}
export function showConfirmation_PROMPT_ACCOUNTING_enteredInSap({ claim, currentUser }: {
  claim:       Claim,
  currentUser: CLM.User
 }) {
  return !isProcessingBlocked_FOR_user({ claim, currentUser })
      && when_wholesalerCreditApproval_phases({ claim })
      && claim?.baseline?.action === CLP.Action.CONFIRM_CLAIM_ENTERED_IN_SAP
      && (when_WCA_user({ currentUser }) || when_WCA_AS_user({ currentUser }) || when_ARS_user({ currentUser }));
}
export function showConfirmation_PROMPT_ACCOUNTING_complete({ claim, currentUser }: {
  claim:       Claim,
  currentUser: CLM.User
 }) {
  return !isProcessingBlocked_FOR_user({ claim, currentUser })
      && when_wholesalerCreditApproval_phases({ claim })
      && claim?.baseline?.action !== CLP.Action.CONFIRM_CLAIM_ENTERED_IN_SAP
      && (when_WCA_user({ currentUser }) || when_ARS_user({ currentUser }));
}

export const isReadonly_casesRequested_AND_claimForm = (claim: Claim) => {
  return ![CLP.Phase.CLAIM_CREATION, CLP.Phase.CLAIM_APPROVAL].includes(claim.phase) || claim.itemized;
}
export const isReadOnly_confirmAndSubmit = (claim: Claim, currentUser: CLM.User) => {
  return hasIncompleteProductLineItems({ claim }) || claim.assigned?.action !== CLP.Action.CONFIRM_AND_SUBMIT || currentUser.roleType !== RLT.RoleType.DIS;
}
export const isDisabled_confirmAndSubmit = (claim: Claim) => {
  const assignableActions: CLP.AssignableAction[] = [
                                                      CLP.Action.SELECT_PRODUCTS,
                                                      CLP.Action.ENTER_PRODUCT_DETAILS,
                                                      CLP.Action.FINISH_PRODUCT_DETAILS,
                                                      CLP.Action.SPECIFY_DESTRUCTION_INTENT,
                                                      CLP.Action.VERIFY_POINT_OF_CONTACT
                                                    ];
  return claim.assigned?.action !== undefined && assignableActions.includes(claim.assigned?.action);
}

// #endregion
// #region Test Claims - File Info

export type TestClaimsFileInfo = {
  id:          Id;
  name?:       string;
  collection:  SYS.TestDataCollection;
  uploadedAt?: DateTime;
};
export type RawTestClaimsFileInfo = Omit<TestClaimsFileInfo, "id" | "collection" | "uploadedAt"> & {
  id:          string;
  name?:       string;
  collection:  string;
  uploadedAt?: string;
};
export const toRawTestClaimsFileInfo = (_: TestClaimsFileInfo): RawTestClaimsFileInfo => {
  return {
    ..._,
    id:         _.id.asInt.toString(),
    collection: _.collection.identifier,
    uploadedAt: _.uploadedAt?.toISOString(),
  };
}
export const fromRawTestClaimsFileInfo = (_: RawTestClaimsFileInfo): TestClaimsFileInfo => {
  return {
    ..._,
    id:         Id.from(BigInt(_.id)) as Id,
    collection: SYS.testDataCollection(_.collection),
    uploadedAt: _.uploadedAt === undefined ? undefined : dateTime(new Date(_.uploadedAt)),
  };
}

const TEST_CLAIMS_IMPORT_FILE_ID_SEED = BigInt(9999990);

export const testClaimsFileInfos = {
  testClaims1:  { id : Id.from(TEST_CLAIMS_IMPORT_FILE_ID_SEED + BigInt(1)),  collection: SYS.TestDataCollection.TEST_CLAIMS_IMPORT_1  },
  testClaims2:  { id : Id.from(TEST_CLAIMS_IMPORT_FILE_ID_SEED + BigInt(2)),  collection: SYS.TestDataCollection.TEST_CLAIMS_IMPORT_2  },
  testClaims3:  { id : Id.from(TEST_CLAIMS_IMPORT_FILE_ID_SEED + BigInt(3)),  collection: SYS.TestDataCollection.TEST_CLAIMS_IMPORT_3  },
  testClaims4:  { id : Id.from(TEST_CLAIMS_IMPORT_FILE_ID_SEED + BigInt(4)),  collection: SYS.TestDataCollection.TEST_CLAIMS_IMPORT_4  },
  testClaims5:  { id : Id.from(TEST_CLAIMS_IMPORT_FILE_ID_SEED + BigInt(5)),  collection: SYS.TestDataCollection.TEST_CLAIMS_IMPORT_5  },
  testClaims6:  { id : Id.from(TEST_CLAIMS_IMPORT_FILE_ID_SEED + BigInt(6)),  collection: SYS.TestDataCollection.TEST_CLAIMS_IMPORT_6  },
  testClaims7:  { id : Id.from(TEST_CLAIMS_IMPORT_FILE_ID_SEED + BigInt(7)),  collection: SYS.TestDataCollection.TEST_CLAIMS_IMPORT_7  },
  testClaims8:  { id : Id.from(TEST_CLAIMS_IMPORT_FILE_ID_SEED + BigInt(8)),  collection: SYS.TestDataCollection.TEST_CLAIMS_IMPORT_8  },
  testClaims9:  { id : Id.from(TEST_CLAIMS_IMPORT_FILE_ID_SEED + BigInt(9)),  collection: SYS.TestDataCollection.TEST_CLAIMS_IMPORT_9  },
  testClaims10: { id : Id.from(TEST_CLAIMS_IMPORT_FILE_ID_SEED + BigInt(10)), collection: SYS.TestDataCollection.TEST_CLAIMS_IMPORT_10 },
}

export const testClaimsFileInfo = (collection: SYS.TestDataCollection, name?: string, uploadedAt?: DateTime): TestClaimsFileInfo => {
  switch (collection) {
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_1:
      return { ...testClaimsFileInfos.testClaims1, name, uploadedAt };
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_2:
      return { ...testClaimsFileInfos.testClaims2, name, uploadedAt };
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_3:
      return { ...testClaimsFileInfos.testClaims3, name, uploadedAt };
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_4:
      return { ...testClaimsFileInfos.testClaims4, name, uploadedAt };
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_5:
      return { ...testClaimsFileInfos.testClaims5, name, uploadedAt };
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_6:
      return { ...testClaimsFileInfos.testClaims6, name, uploadedAt };
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_7:
      return { ...testClaimsFileInfos.testClaims7, name, uploadedAt };
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_8:
      return { ...testClaimsFileInfos.testClaims8, name, uploadedAt };
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_9:
      return { ...testClaimsFileInfos.testClaims9, name, uploadedAt };
    case SYS.TestDataCollection.TEST_CLAIMS_IMPORT_10:
      return { ...testClaimsFileInfos.testClaims10, name, uploadedAt };
    default:
      unexpectedCodePath(`Test data collection ${collection} is not supported.`);
  }
}

export const fileIdentifier_FROM_testClaimsFileInfo = (fileInfo: TestClaimsFileInfo): string => {
  return fileIdentifier({
    parentId:   fileInfo.id.asInt,
    childId:    fileInfo.id.asInt,
  });
}

export const containerPackaging_FROM_product = (product: CLM.Product) => {
  return `${product.packaging_type} ${product.packaging_container.name}`;
}

// #endregion
// #region Products - File Info

export type ProductsFileInfo = {
  id:          Id;
  name?:       string;
  uploadedAt?: DateTime;
};
export type RawProductsFileInfo = Omit<ProductsFileInfo, "id" | "uploadedAt"> & {
  id:          string;
  name?:       string;
  uploadedAt?: string;
};
export const toRawProductsFileInfo = (_: ProductsFileInfo): RawProductsFileInfo => {
  return {
    ..._,
    id:         _.id.asInt.toString(),
    uploadedAt: _.uploadedAt?.toISOString(),
  };
}
export const fromRawProductsFileInfo = (_: RawProductsFileInfo): ProductsFileInfo => {
  return {
    ..._,
    id:         Id.from(BigInt(_.id)) as Id,
    uploadedAt: _.uploadedAt === undefined ? undefined : dateTime(new Date(_.uploadedAt)),
  };
}

const PRODUCTS_FILE_ID = BigInt(999999100);

export const productsFileInfo = (name?: string, uploadedAt?: DateTime): ProductsFileInfo => {
  return {
    id:         Id.from(PRODUCTS_FILE_ID),
    name,
    uploadedAt,
  };
}

export const fileIdentifier_FROM_productsFileInfo = (fileInfo: ProductsFileInfo): string => {
  return fileIdentifier({
    parentId:   fileInfo.id.asInt,
    childId:    fileInfo.id.asInt,
  });
}

// #endregion
// #region Claim

  export const fileName_claimForm_           = ({ claimNumber                               }: { claimNumber: string                                                 }) => `Claim Form ${claimNumber}`;
  export const fileName_claimApproval_       = ({ claimNumber, claimApprover_name, fileType }: { claimNumber: string, claimApprover_name?: string, fileType?: string }) => `Claim Approval ${claimNumber} - ${claimApprover_name || "TBD"}${fileType ? `.${fileType}` : ""}`;
  export const fileName_noi_                 = ({ noi                                       }: { noi?: string                                                        }) => `Notice of Intent ${noi}`;
  export const fileName_faxCoverSheet_       = ({ noi                                       }: { noi?: string                                                        }) => `Fax Cover Sheet ${noi}`;
  export const fileName_ttbApproval_         = ({ noi                                       }: { noi?: string                                                        }) => `TTB Approval ${noi}`;
  export const fileName_destructionApproval_ = ({ claimNumber                               }: { claimNumber: string                                                 }) => `Destruction Approval ${claimNumber}`;
  export const fileName_aod_                 = ({ claimNumber, index                        }: { claimNumber: string, index: number                                  }) => `AOD ${claimNumber} - ${index}`;
  export const fileName_extraFile_           = ({                                           }: {                                                                     }) => undefined; //USE_SELECTED_FILE_NAME - CLIFF: Must use undefined directly to avoid issues with imports in file-info.ts

  export const fileName_claimForm           = (_: Claim                                                                ) => fileName_claimForm_          ({ claimNumber: _.claimNumber                                                                                                                                             });
  export const fileName_claimApproval       = (_: Claim, options?: { ulca: UpperLevelClaimApproval, fileType?: string }) => fileName_claimApproval_      ({ claimNumber: _.claimNumber, claimApprover_name: sanitizedStringForFilename(options?.ulca?.claimApprover_name), fileType: sanitizedStringForFilename(options?.fileType) });
  export const fileName_noi                 = (_: Claim                                                                ) => fileName_noi_                ({ noi: _.noi                                                                                                                                                             });
  export const fileName_faxCoverSheet       = (_: Claim                                                                ) => fileName_faxCoverSheet_      ({ noi: _.noi                                                                                                                                                             });
  export const fileName_ttbApproval         = (_: Claim                                                                ) => fileName_ttbApproval_        ({ noi: _.noi                                                                                                                                                             });
  export const fileName_destructionApproval = (_: Claim                                                                ) => fileName_destructionApproval_({ claimNumber: _.claimNumber                                                                                                                                             });
  export const fileName_aod                 = (_: Claim, options?: { index: number                                    }) => fileName_aod_                ({ claimNumber: _.claimNumber, index: options?.index ?? error(`Claim ${_.claimNumber} is missing AOD index value.`)                                                       });
  export const fileName_extraFile           = (_: Claim                                                                ) => fileName_extraFile_          ({                                                                                                                                                                        });

  export const fileName_testClaims    = (_: TestClaimsFileInfo)   => `Test Claims ${_.collection.data.prefix}`;
  export const fileName_products      = (_: ProductsFileInfo  )   => `Products`;

  export const distributorLocation_OR_wholesaler_name_FOR_user = (_: Claim, user: User): string => {
    if(_.distributorLocation) {
      return _.distributorLocation?.name + (user.settings.persona !== CLP.ActorType.DISTRIBUTOR ? " - " + _.distributorLocation?.sapId : "");
    }
    else {
      return _.wholesaler_name + (user.settings.persona !== CLP.ActorType.DISTRIBUTOR ? " - " + _.wholesaler_sapId : "");
    }
  }
  export const claimForm = (_: Claim): FileInfo | undefined => {
    return fileInfo({
      parentId:   _.id.asInt,
      childId:    _.claimForm_id,
      name:       _.claimForm_fileName,
      uploadedAt: optionalDateTime(_.claimForm_uploadedAt),
    })
  }
  export const wholesalerName_for_destructionIntent = (_: Claim) =>
    _.wholesaler_name !== undefined ? `${_.wholesaler_name} - ${_.wholesaler_sapId}` : _.wholesaler_sapId;

  export const ttb_noticeOfIntent = (_: Claim): FileInfo | undefined => {
    return fileInfo({
      parentId:   _.id.asInt,
      childId:    _.ttb_noticeOfIntent_id,
      name:       _.ttb_noticeOfIntent_fileName,
      uploadedAt: optionalDateTime(_.ttb_noticeOfIntent_uploadedAt),
    })
  }
  export const ttb_faxCoverSheet = (_: Claim): FileInfo | undefined => {
    return fileInfo({
      parentId:   _.id.asInt,
      childId:    _.ttb_faxCoverSheet_id,
      name:       _.ttb_faxCoverSheet_fileName,
      uploadedAt: optionalDateTime(_.ttb_faxCoverSheet_uploadedAt),
    })
  }
  export const ttb_ttbApproval = (_: Claim): FileInfo | undefined => {
    return fileInfo({
      parentId:   _.id.asInt,
      childId:    _.ttb_ttbApproval_id,
      name:       _.ttb_ttbApproval_fileName,
      uploadedAt: optionalDateTime(_.ttb_ttbApproval_uploadedAt),
    })
  }
  export const bbc_destructionApproval = (_: Claim): FileInfo | undefined => {
    return fileInfo({
      parentId:   _.id.asInt,
      childId:    _.bbc_destructionApproval_id,
      name:       _.bbc_destructionApproval_fileName,
      uploadedAt: optionalDateTime(_.bbc_destructionApproval_uploadedAt),
    })
  }
  export const aod_file = (_: Claim) => (index: number): FileInfo | undefined => {
    const childId_fieldName    = `aod_${index}_id`;
    const fileName_fieldName   = `aod_${index}_fileName`;
    const uploadedAt_fieldName = `aod_${index}_uploadedAt`;

    assert(childId_fieldName    in _);
    assert(fileName_fieldName   in _);
    assert(uploadedAt_fieldName in _);

    const x: any = _;

    const childId    = x[childId_fieldName   ] as bigint | undefined;
    const name       = x[fileName_fieldName  ] as string | undefined;
    const uploadedAt = x[uploadedAt_fieldName] as Date   | undefined;

    return fileInfo({
      parentId:   _.id.asInt,
      childId:    childId,
      name:       name,
      uploadedAt: optionalDateTime(uploadedAt),
    });
  }

  export const extra_file = (_: Claim) => (index: number): FileInfo | undefined => {
    const childId_fieldName    = `extraFile_${index}_id`;
    const fileName_fieldName   = `extraFile_${index}_fileName`;
    const uploadedAt_fieldName = `extraFile_${index}_uploadedAt`;

    assert(childId_fieldName    in _);
    assert(fileName_fieldName   in _);
    assert(uploadedAt_fieldName in _);

    const x: any = _;

    const childId    = x[childId_fieldName   ] as bigint | undefined;
    const name       = x[fileName_fieldName  ] as string | undefined;
    const uploadedAt = x[uploadedAt_fieldName] as Date   | undefined;

    return fileInfo({
      parentId:   _.id.asInt,
      childId:    childId,
      name:       name,
      uploadedAt: optionalDateTime(uploadedAt),
    });
  }

  export const testClaims_file = (_?: TestClaimsFileInfo): FileInfo | undefined => {
    assert(_ !== undefined, "Test Claims file info is required to create a file info object.");

    return fileInfo({
      parentId:   _.id.asInt,
      childId:    _.uploadedAt ? _.id.asInt                       : undefined,
      name:       _.uploadedAt ? _.name ?? fileName_testClaims(_) : undefined,
      uploadedAt: optionalDateTime(_.uploadedAt),
    });
  }

  export const products_file = (_?: ProductsFileInfo): FileInfo | undefined => {
    assert(_ !== undefined, "Products file info is required to create a file info object.");

    return fileInfo({
      parentId:   _.id.asInt,
      childId:    _.uploadedAt ? _.id.asInt                       : undefined,
      name:       _.uploadedAt ? _.name ?? fileName_products(_)   : undefined,
      uploadedAt: optionalDateTime(_.uploadedAt),
    });
  }

  export type Activity<TAction extends CLP.Action> = {
    persona?:  CLP.ActorType;
    action:    TAction;
    // startDate: Date;
    dueDate?:  Date;
    dueDays?:  number;
    dueSoon?:  boolean;
    overdue:   boolean;
  }
  export type Claim = {
    id:                         ClaimId;

    schemaVersion:              CLP.SchemaVersion;

    claimType:                  CLM.ClaimType;
    claimReason:                CLM.ClaimReason;
    itemized:                   boolean;
    involvesTaxCredit:          boolean;
    sapOrderTiming:             CLM.SapOrderTiming;

    stage:                      CLP.Stage;
    phase:                      CLP.Phase;
    deliverable?:               CLP.Deliverable;

    bbcStatus:                  CLP.BbcStatus;
    distributorStatus?:         CLP.DistributorStatus;
    cancelationReason?:         string;
    denialReason?:              string;

    claimNumber:                string;
    testClaimNumber?:           number;
    sapOrderNumber?:            string;
    beverageType?:              BeverageType;
    casesRequested?:            number;

    distributor?:               Distributor;
    distributorLocation?:       DistributorLocation;

    wholesaler_sapId:           string;     // persisted
    wholesaler_nameOverride?:   string;     // persisted
    wholesaler?:                Wholesaler; // Calculated based on stored wholesaler_sapId + MUST match wholesalerSapId
    wholesaler_name?:           string;     // Calculated based on wholesaler and wholesaler_nameOverride
    wholesaler_contactName?:    string;     // persisted
    wholesaler_contactEmail?:   string;     // persisted

    registeredOn:               Date;
    noi?:                       string;

    flca_claimApprover_level?:  string;
    flca_claimApprover_name?:   string;
    flca_approvedAt?:           Date;
    ulca_claimApprover_levels?: string[];

    claimForm_id?:              bigint;
    claimForm_fileName?:        string;
    claimForm_uploadedAt?:      Date;

    retailPercentage?:          number;

    // Destruction Intent - Move to separate object
    di_destructionCompany_type?:          DestructionCompanyType,
    di_destructionCompany_name?:          string,
    di_destructionCompany_streetAddress?: string,
    di_destructionCompany_city?:          string,
    di_destructionCompany_state?:         CLM.State,
    di_destructionCompany_zipCode?:       string,
    di_destructionMethod?:                string,

    di_distributorDestructionLocation?:   DistributorLocation,

    // Should TTB submission be skipped? - Move to separate object
    skipTtb:                    boolean,
    skipTtb_skipReason?:        string,
    skipTtb_deferredTaxCredit?: number,

    // TTB Submission & Approval - Move to separate object
    ttb_alcoholicCases?:            number,
    ttb_barrels?:                   number,
    ttb_tax?:                       number,
    ttb_productionLocations?:       ProductionLocation[],

    ttb_dateFaxedToTTB?:            Date,
    ttb_noticeOfIntent_id?:         bigint,
    ttb_noticeOfIntent_fileName?:   string,
    ttb_noticeOfIntent_uploadedAt?: Date,
    ttb_faxCoverSheet_id?:          bigint,
    ttb_faxCoverSheet_fileName?:    string,
    ttb_faxCoverSheet_uploadedAt?:  Date,

    ttb_ttbApprovalConfirmed:           boolean,
    ttb_skipTtbFollowUp:                boolean,
    ttb_ttbFollowUpConfirmedAt?:        Date,
    ttb_overdueTtbApprovalConfirmedAt?: Date,

    ttb_ttbApprovalDeadline?:        Date,
    ttb_ttbApprovalDate?:            Date,
    ttb_ttbApproval_id?:             bigint,
    ttb_ttbApproval_fileName?:       string,
    ttb_ttbApproval_uploadedAt?:     Date,

    // Destruction Approval - Move to separate object
    bbc_destructionApprovalDate?:        Date,
    bbc_destructionApproval_id?:         bigint,
    bbc_destructionApproval_fileName?:   string,
    bbc_destructionApproval_uploadedAt?: Date,

    // Destruction - Move to separate object
    aod_earliestDestructionDate?: Date,
    aod_casesDestroyed?:          number,
    aod_hasIssues?:               boolean,
    aod_issues?:                  AodIssue[],
    aod_reviewNotes?:             string,
    // des_affidavits:            [],
    aod_1_id?:                    bigint,
    aod_1_fileName?:              string,
    aod_1_uploadedAt?:            Date,
    aod_2_id?:                    bigint,
    aod_2_fileName?:              string,
    aod_2_uploadedAt?:            Date,
    aod_3_id?:                    bigint,
    aod_3_fileName?:              string,
    aod_3_uploadedAt?:            Date,
    aod_4_id?:                    bigint,
    aod_4_fileName?:              string,
    aod_4_uploadedAt?:            Date,
    aod_5_id?:                    bigint,
    aod_5_fileName?:              string,
    aod_5_uploadedAt?:            Date,
    aod_6_id?:                    bigint,
    aod_6_fileName?:              string,
    aod_6_uploadedAt?:            Date,
    aod_7_id?:                    bigint,
    aod_7_fileName?:              string,
    aod_7_uploadedAt?:            Date,
    aod_8_id?:                    bigint,
    aod_8_fileName?:              string,
    aod_8_uploadedAt?:            Date,
    aod_9_id?:                    bigint,
    aod_9_fileName?:              string,
    aod_9_uploadedAt?:            Date,
    aod_10_id?:                   bigint,
    aod_10_fileName?:             string,
    aod_10_uploadedAt?:           Date,

    // extra_files:               [],
    extraFile_1_id?:              bigint,
    extraFile_1_fileName?:        string,
    extraFile_1_uploadedAt?:      Date,
    extraFile_2_id?:              bigint,
    extraFile_2_fileName?:        string,
    extraFile_2_uploadedAt?:      Date,
    extraFile_3_id?:              bigint,
    extraFile_3_fileName?:        string,
    extraFile_3_uploadedAt?:      Date,
    extraFile_4_id?:              bigint,
    extraFile_4_fileName?:        string,
    extraFile_4_uploadedAt?:      Date,
    extraFile_5_id?:              bigint,
    extraFile_5_fileName?:        string,
    extraFile_5_uploadedAt?:      Date,
    extraFile_6_id?:              bigint,
    extraFile_6_fileName?:        string,
    extraFile_6_uploadedAt?:      Date,
    extraFile_7_id?:              bigint,
    extraFile_7_fileName?:        string,
    extraFile_7_uploadedAt?:      Date,
    extraFile_8_id?:              bigint,
    extraFile_8_fileName?:        string,
    extraFile_8_uploadedAt?:      Date,
    extraFile_9_id?:              bigint,
    extraFile_9_fileName?:        string,
    extraFile_9_uploadedAt?:      Date,
    extraFile_10_id?:             bigint,
    extraFile_10_fileName?:       string,
    extraFile_10_uploadedAt?:     Date,

    // Accounting Approval - Move to separate object
    acc_casesCredited?:          number,
    acc_creditAmount?:           number,
    acc_accountingApprovalDate?: Date,
    acc_creditProcessingNotes?:  string,
    acc_creditMemoDate?:         Date,

    baseline?:                  Activity<CLP.BaselineAction>;
    assigned?:                  Activity<CLP.AssignableAction>;
    current?:                   Activity<CLP.AssignableAction>;

    sysInfo:                    SYS.Info;
    keyMoments:                 ClaimKeyMoments;

    incompleteProductLineItemCount?:      number;
    completeProductLineItemCount?:        number;

    signature_submitterName?:             string;
    signature_distributorAcknowledgment?: boolean;

    claimNotes?:                string;
    distributorNotes?:          string;

    claimVerificationMethod?:   CLM.ClaimVerificationMethod;
    bbcApproverNotes?:          string;

    issuesAssignedTo?:          UserGroup[];
    openIssueCount?:            number;
    resolvedIssueCount?:        number;
    currentIssueType?:          CLM.IssueType;

    extensionOne:               ClaimExtensionOne;

    testingTags?:               SYS.TestingTag[];
  }
  export type ClaimHelpers = {
    baseline_persona?: CLP.ActorType,
    baseline_action?:  CLP.BaselineAction,
    baseline_dueDate?: Date,
    assigned_persona?: CLP.ActorType,
    assigned_action?:  CLP.AssignableAction,
    assigned_dueDate?: Date,
  };
  export type RawClaim = {
    id:                         string;

    schemaVersion:              string;

    claimType:                  string;
    claimReason:                string;
    itemized:                   boolean;
    involvesTaxCredit:          boolean;
    sapOrderTiming:             string;

    stage:                      string;
    phase:                      string;
    deliverable?:               string;

    bbcStatus:                  string;
    distributorStatus?:         string;
    cancelationReason?:         string;
    denialReason?:              string;

    claimNumber:                string;
    testClaimNumber?:           number;
    sapOrderNumber?:            string;
    beverageType?:              string;
    casesRequested?:            number;

    distributor_id?:            string;
    distributor_sapId?:         string;
    distributor_name?:          string;

    distributorLocation_id?:            string,
    distributorLocation_sapId?:         string,
    distributorLocation_name?:          string,
    distributorLocation_streetAddress?: string,
    distributorLocation_city?:          string,
    distributorLocation_state?:         string,
    distributorLocation_zipCode?:       string,

    wholesaler_sapId:           string;
    wholesaler_nameOverride?:   string;
    wholesaler_contactName?:    string;
    wholesaler_contactEmail?:   string;

    registeredOn:               string;
    noi?:                       string;

    flca_claimApprover_level?:  string;
    flca_claimApprover_name?:   string;
    flca_approvedAt?:           string;
    ulca_claimApprover_levels?: string;

    claimForm_id?:              string;
    claimForm_fileName?:        string;
    claimForm_uploadedAt?:      string;

    retailPercentage?:          number;

    // Destruction Intent - Move to separate object
    di_destructionCompany_type?:          string,
    di_destructionCompany_name?:          string,
    di_destructionCompany_streetAddress?: string,
    di_destructionCompany_city?:          string,
    di_destructionCompany_state?:         string,
    di_destructionCompany_zipCode?:       string,
    di_destructionMethod?:                string,

    di_distributorDestructionLocation_id?:            string,
    di_distributorDestructionLocation_sapId?:         string,
    di_distributorDestructionLocation_name?:          string,
    di_distributorDestructionLocation_streetAddress?: string,
    di_distributorDestructionLocation_city?:          string,
    di_distributorDestructionLocation_state?:         string,
    di_distributorDestructionLocation_zipCode?:       string,

    // Should TTB submission be skipped? - Move to separate object
    skipTtb:                    boolean,
    skipTtb_skipReason?:        string,
    skipTtb_deferredTaxCredit?: number,

    // TTB Submission & Approval - Move to separate object
    ttb_alcoholicCases?:         number,
    ttb_barrels?:                number,
    ttb_tax?:                    number,
    ttb_productionLocations?:    string,

    ttb_dateFaxedToTTB?:            string,
    ttb_noticeOfIntent_id?:         string,
    ttb_noticeOfIntent_fileName?:   string,
    ttb_noticeOfIntent_uploadedAt?: string,
    ttb_faxCoverSheet_id?:          string,
    ttb_faxCoverSheet_fileName?:    string,
    ttb_faxCoverSheet_uploadedAt?:  string,

    ttb_ttbApprovalConfirmed:           boolean,
    ttb_skipTtbFollowUp:                boolean,
    ttb_ttbFollowUpConfirmedAt?:        string,
    ttb_overdueTtbApprovalConfirmedAt?: string,

    ttb_ttbApprovalDeadline?:        string,
    ttb_ttbApprovalDate?:            string,
    ttb_ttbApproval_id?:             string,
    ttb_ttbApproval_fileName?:       string,
    ttb_ttbApproval_uploadedAt?:     string,

    // Destruction Approval - Move to separate object
    bbc_destructionApprovalDate?:        string,
    bbc_destructionApproval_id?:         string,
    bbc_destructionApproval_fileName?:   string,
    bbc_destructionApproval_uploadedAt?: string,

    // Destruction - Move to separate object
    aod_earliestDestructionDate?: string,
    aod_casesDestroyed?:          number,
    aod_hasIssues?:               boolean,
    aod_issues?:                  string,
    aod_reviewNotes?:             string,
    // des_affidavits:            [],
    aod_1_id?:                    string,
    aod_1_fileName?:              string,
    aod_1_uploadedAt?:            string,
    aod_2_id?:                    string,
    aod_2_fileName?:              string,
    aod_2_uploadedAt?:            string,
    aod_3_id?:                    string,
    aod_3_fileName?:              string,
    aod_3_uploadedAt?:            string,
    aod_4_id?:                    string,
    aod_4_fileName?:              string,
    aod_4_uploadedAt?:            string,
    aod_5_id?:                    string,
    aod_5_fileName?:              string,
    aod_5_uploadedAt?:            string,
    aod_6_id?:                    string,
    aod_6_fileName?:              string,
    aod_6_uploadedAt?:            string,
    aod_7_id?:                    string,
    aod_7_fileName?:              string,
    aod_7_uploadedAt?:            string,
    aod_8_id?:                    string,
    aod_8_fileName?:              string,
    aod_8_uploadedAt?:            string,
    aod_9_id?:                    string,
    aod_9_fileName?:              string,
    aod_9_uploadedAt?:            string,
    aod_10_id?:                   string,
    aod_10_fileName?:             string,
    aod_10_uploadedAt?:           string,

    // extra_files:               [],
    extraFile_1_id?:              string,
    extraFile_1_fileName?:        string,
    extraFile_1_uploadedAt?:      string,
    extraFile_2_id?:              string,
    extraFile_2_fileName?:        string,
    extraFile_2_uploadedAt?:      string,
    extraFile_3_id?:              string,
    extraFile_3_fileName?:        string,
    extraFile_3_uploadedAt?:      string,
    extraFile_4_id?:              string,
    extraFile_4_fileName?:        string,
    extraFile_4_uploadedAt?:      string,
    extraFile_5_id?:              string,
    extraFile_5_fileName?:        string,
    extraFile_5_uploadedAt?:      string,
    extraFile_6_id?:              string,
    extraFile_6_fileName?:        string,
    extraFile_6_uploadedAt?:      string,
    extraFile_7_id?:              string,
    extraFile_7_fileName?:        string,
    extraFile_7_uploadedAt?:      string,
    extraFile_8_id?:              string,
    extraFile_8_fileName?:        string,
    extraFile_8_uploadedAt?:      string,
    extraFile_9_id?:              string,
    extraFile_9_fileName?:        string,
    extraFile_9_uploadedAt?:      string,
    extraFile_10_id?:             string,
    extraFile_10_fileName?:       string,
    extraFile_10_uploadedAt?:     string,

    // Accounting Approval - Move to separate object
    acc_casesCredited?:          number,
    acc_creditAmount?:           number,
    acc_accountingApprovalDate?: string,
    acc_creditProcessingNotes?:  string,
    acc_creditMemoDate?:         string,

    baseline_persona?:          string;
    baseline_action?:           string;
    baseline_dueDate?:          string;

    sysInfo_createdAt:                    string;
    sysInfo_createdBy_id:                 string;
    sysInfo_createdBy_name:               string;
    sysInfo_created_impersonatedBy_id?:   string;
    sysInfo_created_impersonatedBy_name?: string;
    sysInfo_updatedAt:                    string;
    sysInfo_updatedBy_id:                 string;
    sysInfo_updatedBy_name:               string;
    sysInfo_updated_impersonatedBy_id?:   string;
    sysInfo_updated_impersonatedBy_name?: string;
    sysInfo_deletedAt?:                   string;
    sysInfo_deletedBy_id?:                string;
    sysInfo_deletedBy_name?:              string;
    sysInfo_deleted_impersonatedBy_id?:   string;
    sysInfo_deleted_impersonatedBy_name?: string;

    keyMoment_submittedAt?:                                 string;
    keyMoment_submittedBy_id?:                              string;
    keyMoment_submittedBy_name?:                            string;
    keyMoment_submittedBy_managerLevel?:                    string;
    keyMoment_sentBackAt?:                                  string;
    keyMoment_sentBackBy_id?:                               string;
    keyMoment_sentBackBy_name?:                             string;
    keyMoment_sentBackBy_managerLevel?:                     string;
    keyMoment_acceptedAt?:                                  string;
    keyMoment_acceptedBy_id?:                               string;
    keyMoment_acceptedBy_name?:                             string;
    keyMoment_acceptedBy_managerLevel?:                     string;
    keyMoment_claimApprovalConfirmedAt?:                    string;
    keyMoment_claimApprovalConfirmedBy_id?:                 string;
    keyMoment_claimApprovalConfirmedBy_name?:               string;
    keyMoment_claimApprovalConfirmedBy_managerLevel?:       string;
    keyMoment_firstLevelApprovedAt?:                        string;
    keyMoment_firstLevelApprovedBy_id?:                     string;
    keyMoment_firstLevelApprovedBy_name?:                   string;
    keyMoment_firstLevelApprovedBy_managerLevel?:           string;
    keyMoment_ttbSubmissionConfirmedAt?:                    string;
    keyMoment_sapOrderCreatedAt?:                           string;
    keyMoment_sapOrderCreatedBy_id?:                        string;
    keyMoment_sapOrderCreatedBy_name?:                      string;
    keyMoment_sapOrderCreatedBy_managerLevel?:              string;
    keyMoment_ttbSubmissionConfirmedBy_id?:                 string;
    keyMoment_ttbSubmissionConfirmedBy_name?:               string;
    keyMoment_ttbSubmissionConfirmedBy_managerLevel?:       string;
    keyMoment_destructionApprovalConfirmedAt?:              string;
    keyMoment_destructionApprovalConfirmedBy_id?:           string;
    keyMoment_destructionApprovalConfirmedBy_name?:         string;
    keyMoment_destructionApprovalConfirmedBy_managerLevel?: string;
    keyMoment_destructionConfirmedAt?:                      string;
    keyMoment_destructionConfirmedBy_id?:                   string;
    keyMoment_destructionConfirmedBy_name?:                 string;
    keyMoment_destructionConfirmedBy_managerLevel?:         string;
    keyMoment_sapEntryConfirmedAt?:                         string;
    keyMoment_sapEntryConfirmedBy_id?:                      string;
    keyMoment_sapEntryConfirmedBy_name?:                    string;
    keyMoment_sapEntryConfirmedBy_managerLevel?:            string;
    keyMoment_closedAt?:                                    string;
    keyMoment_closedBy_id?:                                 string;
    keyMoment_closedBy_name?:                               string;
    keyMoment_closedBy_managerLevel?:                       string;

    incompleteProductLineItemCount?:      number;
    completeProductLineItemCount?:        number;

    signature_submitterName?:             string;
    signature_distributorAcknowledgment?: boolean;

    claimNotes?:                string;
    distributorNotes?:          string;

    claimVerificationMethod?:   string;
    bbcApproverNotes?:          string;

    issuesAssignedTo?:          string;
    openIssueCount?:            number;
    resolvedIssueCount?:        number;
    currentIssueType?:          string;

    extensionOne:               ClaimExtensionOneRaw;

    testingTags?:              string;
  }
  export type ClaimKeyMoments = {
    submitted?:                    SYS.KeyMoment;
    sentBack?:                     SYS.KeyMoment;
    accepted?:                     SYS.KeyMoment;
    claimApprovalConfirmed?:       SYS.KeyMoment;
    firstLevelApproved?:           SYS.KeyMoment;
    sapOrderCreated?:              SYS.KeyMoment;
    ttb_submissionConfirmed?:      SYS.KeyMoment;
    destructionApprovalConfirmed?: SYS.KeyMoment;
    destructionConfirmed?:         SYS.KeyMoment;
    sapEntryConfirmed?:            SYS.KeyMoment;
    closed?:                       SYS.KeyMoment;
  }
  export type DataContext = {
    now:                      Date,
    currentUser:              User,
    globalSettings:           GlobalSettings,
    wholesalerBySapIdIfExist: (sapId: string) => Wholesaler | undefined,
  };
  export type ClaimCleanupContext = DataContext & {
    dueDays: (dueDate?: Date                         ) => number  | undefined,
    dueSoon: (dueDate?: Date, persona?: CLP.ActorType) => boolean | undefined,
    overdue: (dueDate?: Date                         ) => boolean | undefined,
  }
  export function claimCleanupContext(dataContext: () => DataContext): () => ClaimCleanupContext {
    return () => {

      const baseContext = dataContext();
      const { now, currentUser } = baseContext;

      const dueDateAlert = dueDateAlert_inBusinessDays(currentUser)

      function dueDays(dueDate?: Date) {
        if (dueDate === undefined) return undefined;
        if (overdue(dueDate)     ) return undefined;
        return differenceInBusinessDays(dueDate, now);
      }
      function dueSoon(dueDate?: Date, persona?: CLP.ActorType) {
        if (dueDate === undefined) return undefined;
        if (overdue(dueDate)     ) return undefined;

        // Due soon is relevant only when action is assigned to the current user's persona
        // because due soon alert is tailored to the current user's preferences
        if (persona === undefined           ) return undefined;
        if (!matchesCurrentUser({ persona })) return undefined;

        return dueDateAlert ? dueDays(dueDate)! < dueDateAlert : undefined;
      }
      function overdue(dueDate?: Date) {
        if (dueDate === undefined) return undefined;
        return dueDate < now;
      }

      function matchesCurrentUser({ persona }: {
        persona?: CLP.ActorType
      }) {
        return persona !== undefined && areEqualEnums(persona, currentUser.settings.persona);
      }

      return { ...baseContext, dueDays, dueSoon, overdue };
    }
  }

  export const deriveProcessFields = (cleanupContext: () => ClaimCleanupContext) => (
    claim: Claim & ClaimHelpers
  ): {
    wholesaler?:       Wholesaler,
    wholesaler_name?:  string,
    bbcStatus:         CLP.BbcStatus,
    assigned_action?:  CLP.AssignableAction,
    assigned_dueDate?: Date,
    assigned_persona?: CLP.ActorType,
    baseline?:         Activity<CLP.BaselineAction>,
    assigned?:         Activity<CLP.AssignableAction>,
    current?:          Activity<CLP.AssignableAction>,
  } => {
    const { now, wholesalerBySapIdIfExist, dueDays, dueSoon, overdue, currentUser } = cleanupContext();

    const _ = claim;

    const implicitProcessFields = (): undefined |{
        implicit_action:   CLP.AssignableAction,
        implicit_dueDate?: Date,
        implicit_persona?:  CLP.ActorType,
      } => {

        const isProcessingBlocked = CLM.isProcessingBlocked_FOR_user({ claim, currentUser });
        const currentUserPersona = currentUser.settings.persona;

        if (isProcessingBlocked) {
          const issuePersonas = CLM.issuePersonas({ claim });

          return ({
            implicit_action:  CLP.Action.CONFIRM_ISSUE_RESOLVED,
            implicit_dueDate: undefined,
            implicit_persona: issuePersonas.includes(currentUserPersona) ? currentUserPersona : issuePersonas.length ? issuePersonas[0] : undefined,
          });
        }

        const hasIncompleteProductLineItems = CLM.displayHasIncompleteProductLineItems({ claim, currentUser });

        if(hasIncompleteProductLineItems) {
          return ({
            implicit_action:  CLP.Action.FINISH_PRODUCT_DETAILS,
            implicit_dueDate: undefined,
            implicit_persona: CLP.ActorType.DISTRIBUTOR,
          });
        }

        if(claim.deliverable === CLP.Deliverable.FIRST_LEVEL_CLAIM_APPROVAL && currentUserPersona === CLP.ActorType.DISTRIBUTOR) {
          return ({
            implicit_action:  CLP.Action.GET_CLAIM_APPROVAL,
            implicit_dueDate: undefined,
            implicit_persona: CLP.ActorType.DISTRIBUTOR,
          });
        }

        const dueDate = _.baseline_dueDate;

        const assignableAction  = _.baseline_action && CLP.isAssignableAction(_.baseline_action) ? _.baseline_action : undefined;
        if (assignableAction && assignableAction.assignedTo) {
          assert(assignableAction.assignedTo === _.baseline_persona, "Baseline persona must match assignable action's assignedTo.");
          return ({
            implicit_action:  assignableAction,
            implicit_dueDate: dueDate,
            implicit_persona: assignableAction.assignedTo,
          });
        }

        const delegatedAction = _.baseline_action && CLP.isDelegatedAction(_.baseline_action) ? _.baseline_action : undefined;
        if (delegatedAction) {
          const overdueCorrectionAction = delegatedAction.overdueCorrectedBy;
          if (overdue(dueDate)) return ({
            implicit_action:  overdueCorrectionAction,
            implicit_dueDate: undefined,
            implicit_persona: overdueCorrectionAction.assignedTo,
          });

          const timeExpectations = {
            CLAIM_APPROVALS:  2,
            TTB_FOLLOW_UP:    7,
            TTB_APPROVAL:    14,
          }

          const DAYS = (days: number) => ({ dueDate, now }: { dueDate: Date, now: Date }) =>
            (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);

          const supportingActions = delegatedAction.supportedBy;
          if (supportingActions) {
            const isActive = (action: CLP.AssignableAction) => {
              if (action === CLP.Action.FOLLOW_UP_WITH_TTB) return !claim.ttb_skipTtbFollowUp && claim.ttb_ttbFollowUpConfirmedAt === undefined;
              unexpectedCodePath(`Unsupported action: ${action.identifier}`);
            }
            const candidateAction = supportingActions.find(_ => dueDate && _.assignable({ dueDate, now }) && isActive(_));
            if (candidateAction) return ({
              implicit_action:  candidateAction,
              implicit_dueDate: undefined,
              implicit_persona: candidateAction.assignedTo,
            });
          }
        }

        return undefined;
      }

      const { implicit_action, implicit_dueDate, implicit_persona } = implicitProcessFields() ?? {};

      assert(_.assigned_action  === undefined || _.assigned_action  === implicit_action,  `Assigned action must match implicit action. ${_.assigned_action?.identifier} !== ${implicit_action?.identifier}`);
      assert(_.assigned_dueDate === undefined || _.assigned_dueDate === implicit_dueDate, `Assigned dueDate must match implicit dueDate. ${_.assigned_dueDate} !== ${implicit_dueDate}`);
      assert(_.assigned_persona === undefined || _.assigned_persona === implicit_persona, `Assigned persona must match implicit persona. ${_.assigned_persona?.identifier} !== ${implicit_persona?.identifier}`);

      const bbcStatus_override = implicit_action === CLP.Action.FOLLOW_UP_ON_CLAIM_APPROVALS  ? CLP.BbcStatus.CLAIM_APPROVAL_OVERDUE
                               : implicit_action === CLP.Action.REGISTER_TTB_APPROVAL_OVERDUE ? CLP.BbcStatus.TTB_APPROVAL_OVERDUE
                               : undefined;

    const assigned_action  = implicit_action;
    const assigned_dueDate = implicit_dueDate;
    const assigned_persona = implicit_persona;

    const wholesaler      = wholesalerBySapIdIfExist(_.wholesaler_sapId);
    const wholesaler_name = _.wholesaler_nameOverride ?? wholesaler?.name;
    const bbcStatus       = bbcStatus_override ?? _.bbcStatus;

    const baselineActivity: Activity<CLP.BaselineAction> | undefined =
      _.baseline_action || _.baseline_dueDate || _.baseline_persona ? {
        persona: _.baseline_persona,
        action:  _.baseline_action,
        dueDate: _.baseline_dueDate,
        dueDays: dueDays(_.baseline_dueDate),
        overdue: overdue(_.baseline_dueDate),
        dueSoon: dueSoon(_.baseline_dueDate, _.baseline_persona),
      } as Activity<CLP.BaselineAction> : undefined;

    const assigned: Activity<CLP.AssignableAction> | undefined =
      assigned_action || assigned_dueDate || assigned_persona ? {
        persona: assigned_persona,
        action:  assigned_action,
        dueDate: assigned_dueDate,
        dueDays: dueDays(assigned_dueDate),
        overdue: overdue(assigned_dueDate),
        dueSoon: dueSoon(assigned_dueDate, assigned_persona),
      } as Activity<CLP.AssignableAction> : undefined;

    const baseline: Activity<CLP.BaselineAction> | undefined =
      baselineActivity ?? (
        assigned && CLP.isBaselineAction(assigned.action) ? assigned as unknown as Activity<CLP.BaselineAction> : undefined
      );

    const current: Activity<CLP.AssignableAction> | undefined =
      isActionableBy(currentUser)(assigned?.action, assigned?.persona) ? assigned : undefined;

    return {
      wholesaler,
      wholesaler_name,
      bbcStatus,
      assigned_action,
      assigned_dueDate,
      assigned_persona,
      baseline,
      assigned,
      current,
    };
  }

  export const claim = (cleanupContext: () => ClaimCleanupContext) => (
    claim: Claim & ClaimHelpers
  ): Claim => ({
    ...claim,
    ...deriveProcessFields(cleanupContext)(claim),
  } as Claim);

  export const claimValidator = ({ currentUser }: {
    currentUser:       () => User,
  }) => {
    if (currentUser === undefined) throw new Error("Parameter currentUser must be defined.");
    if (currentUser === null     ) throw new Error("Parameter currentUser must be defined.");

    return validator((_: Claim) => ({

      a1: _.id !== undefined,
      a2: areEqualEnums(_.claimType, _.claimReason.claimType),

      b1: _.stage       !== undefined,
      b2: _.phase       !== undefined,
      b3: _.deliverable !== undefined || areEqualEnums(_.phase, CLP.Phase.ARCHIVED),

      b4: areEqualEnums(_.stage, _.phase.stage),
      b5: areEqualEnums(_.phase, _.deliverable?.phase) || _.deliverable === undefined,

      b6: _.bbcStatus !== undefined,

      b7: _.deliverable !== undefined && _.bbcStatus.deliverables.some(equalsToEnum(_.deliverable))
       || _.deliverable === undefined && _.bbcStatus.deliverables.length === 0,

      //TODO Fix validation for claims with issues b8: _.baseline === undefined || _.baseline.action === undefined || _.status.availableActions.some(equalsToEnum<CLP.Action>(_.baseline.action)) || CLP.isDelegatedAction(_.baseline.action),
      // b9: _.assigned === undefined || _.assigned.action === undefined || _.status.availableActions.some(equalsTo<CLP.Action>(_.assigned.action)),
      // ba: _.current  === undefined || _.current .action === undefined || _.status.availableActions.some(equalsTo<CLP.Action>(_.current .action)),

      bb:  _.bbcStatus.overdue && _.baseline?.action && CLP.isDelegatedAction(_.baseline.action)
      || !_.bbcStatus.overdue,

      bc: _.bbcStatus.overdue &&   _.assigned?.action &&  CLP.isOverdueCorrectionAction(_.assigned.action)
      || !_.bbcStatus.overdue && (!_.baseline?.action || !CLP.isOverdueCorrectionAction(_.baseline.action)),

      bd: areEqualEnums(_.bbcStatus, CLP.BbcStatus.CANCELED) && _.cancelationReason !== undefined || !areEqualEnums(_.bbcStatus, CLP.BbcStatus.CANCELED) && _.cancelationReason === undefined,
      be: areEqualEnums(_.bbcStatus, CLP.BbcStatus.DENIED  ) && _.denialReason      !== undefined || !areEqualEnums(_.bbcStatus, CLP.BbcStatus.DENIED  ) && _.denialReason      === undefined,

      c1: _.claimNumber    !== undefined && CLAIM_NUMBER_FORMAT_PATTERN.test(_.claimNumber),
      c2: _.sapOrderNumber !== undefined && /^\d{8}$/.test(_.sapOrderNumber) || _.schemaVersion === CLP.SchemaVersion.V2,
      c3: !_.involvesTaxCredit || (_.beverageType !== undefined && beverageTypes.some(equalsToEnum(_.beverageType))),
      c4: ((_.casesRequested === undefined || _.casesRequested === 0) && areEqualEnums(_.schemaVersion, CLP.SchemaVersion.V2)) || (_.casesRequested !== undefined && _.casesRequested > 0),

      d1: _.wholesaler_sapId !== undefined,

      d2: _.wholesaler_sapId === _.wholesaler?.sapId       || _.wholesaler              === undefined,
      d3: _.wholesaler_name  === _.wholesaler_nameOverride || _.wholesaler_nameOverride === undefined,
      d4: _.wholesaler_name  === _.wholesaler?.name        || _.wholesaler_nameOverride !== undefined,
      d5: _.distributor !== undefined || (_.wholesaler_nameOverride !== undefined && _.distributorLocation === undefined),
      d6: _.distributorLocation !== undefined || _.wholesaler_sapId !== undefined,

      e1: _.registeredOn !== undefined,
      e2: _.noi          === undefined || _.beverageType === BeverageType.BEER_FMB,
      e3: _.noi          === undefined || /^\d{2}-\d{2,3}D$/.test(_.noi),

      f1: _.baseline === undefined || _.baseline.persona !== undefined || _.baseline.action !== undefined && CLP.isDelegatedAction(_.baseline.action),
      f2: _.baseline === undefined || _.baseline.action  !== undefined,
      // f3: _.baseline === undefined || _.baseline.dueDate !== undefined,
      // f4: _.baseline === undefined || _.baseline.dueDays !== undefined || _.baseline.overdue,
      // f5: _.baseline === undefined || _.baseline.dueSoon !== undefined || _.baseline.overdue || _.baseline.persona === undefined || equalEnums(_.baseline.persona, currentUser().settings.persona) || dueDateAlert_inBusinessDays(currentUser()) === undefined,
      // f6: _.baseline === undefined || _.baseline.overdue !== undefined,

      f7: _.baseline === undefined ||  CLP.action_assignedTo(_.baseline.action) === undefined || areEqualEnums(_.baseline.persona, _.baseline.action && CLP.action_assignedTo(_.baseline.action)),
      f8: _.baseline === undefined || _.baseline.action?.deliverable            === undefined || areEqualEnums( _.deliverable,     _.baseline.action?.deliverable),
      // f9: _.baseline === undefined || _.baseline.dueDate >=  _.registeredOn,

      //g1: _.assigned === undefined || _.assigned.persona !== undefined,
      g2: _.assigned === undefined || _.assigned.action  !== undefined,
      // g3: _.assigned === undefined || _.assigned.dueDate !== undefined,
      // g4: _.assigned === undefined || _.assigned.dueDays !== undefined || _.assigned.overdue,
      // g5: _.assigned === undefined || _.assigned.dueSoon !== undefined || _.assigned.overdue || _.assigned.persona === undefined || !equalEnums(_.assigned.persona, currentUser().settings.persona) || dueDateAlert_inBusinessDays(currentUser()) === undefined,
      // g6: _.assigned === undefined || _.assigned.overdue !== undefined,

      g7: _.assigned === undefined || _.assigned.action.assignedTo              === undefined ||                      areEqualEnums(_.assigned.persona, _.assigned.action && CLP.action_assignedTo (_.assigned.action)),
      g8: _.assigned === undefined || CLP.action_deliverable(_.assigned.action) === undefined || _.assigned.action && areEqualEnums(_.deliverable,                           CLP.action_deliverable(_.assigned.action)),

      // g9: _.assigned === undefined || _.assigned.dueDate >= _.registeredOn,

      h1: _.baseline === undefined || _.assigned === undefined || _.assigned.action.assignedTo === undefined || JSON.stringify(_.baseline) === JSON.stringify(_.assigned) || !areEqualEnums(_.baseline.persona, _.assigned.persona),
      // h2: _.baseline === undefined || _.assigned === undefined || _.baseline.dueDate <= _.assigned.dueDate,

      i1: _.current === undefined || _.current === _.assigned,
      //i2: _.assigned !== undefined && _.assigned.action.assignedTo === undefined || _.current === undefined || areEqualEnums(_.current.persona, currentUser().settings.persona),

      j1: _.skipTtb === false || isStandardText    (_.skipTtb_skipReason       ),
      j2: _.skipTtb === false || (_.skipTtb_deferredTaxCredit === undefined && CLM.isSubmissionToTTBConfirmed({ claim: _ })) || isNonNegativeMoney(_.skipTtb_deferredTaxCredit),

      k1: _.retailPercentage === undefined || _.retailPercentage >= 0 && _.retailPercentage <= 100,

      x1: _.extensionOne !== undefined,
    }));
  }

  export const toRawClaim = (_: Claim): RawClaim => ({

    id:                                  _.id.asInt.toString(),

    schemaVersion:                         _.schemaVersion.identifier,

    claimType:                           _.claimType.identifier,
    claimReason:                         _.claimReason.identifier,
    itemized:                            _.itemized,
    involvesTaxCredit:                   _.involvesTaxCredit,
    sapOrderTiming:                      _.sapOrderTiming.identifier,

    stage:                               _.stage        .identifier,
    phase:                               _.phase        .identifier,
    deliverable:                         _.deliverable ?.identifier,

    bbcStatus:                           _.bbcStatus         .identifier,
    distributorStatus:                   _.distributorStatus?.identifier,
    cancelationReason:                   _.cancelationReason,
    denialReason:                        _.denialReason,

    claimNumber:                         _.claimNumber,
    testClaimNumber:                     _.testClaimNumber,
    sapOrderNumber:                      _.sapOrderNumber,
    beverageType:                        _.beverageType?.identifier,
    casesRequested:                      _.casesRequested,

    distributor_id:                        _.distributor?.id.asInt.toString(),
    distributor_sapId:                     _.distributor?.sapId,
    distributor_name:                      _.distributor?.name,

    distributorLocation_id:                _.distributorLocation?.id.asInt.toString(),
    distributorLocation_sapId:             _.distributorLocation?.sapId,
    distributorLocation_name:              _.distributorLocation?.name,
    distributorLocation_streetAddress:     _.distributorLocation?.streetAddress,
    distributorLocation_city:              _.distributorLocation?.city,
    distributorLocation_state:             _.distributorLocation?.state?.identifier,
    distributorLocation_zipCode:           _.distributorLocation?.zipCode,

    wholesaler_sapId:                    _.wholesaler_sapId,
    wholesaler_nameOverride:             _.wholesaler_nameOverride,
    wholesaler_contactName:              _.wholesaler_contactName,
    wholesaler_contactEmail:             _.wholesaler_contactEmail,

    registeredOn:                        _.registeredOn.toISOString(),
    noi:                                 _.noi,

    flca_claimApprover_level:            _.flca_claimApprover_level,
    flca_claimApprover_name:             _.flca_claimApprover_name,
    flca_approvedAt:                     _.flca_approvedAt?.toISOString(),
    ulca_claimApprover_levels:           _.ulca_claimApprover_levels?.join(","),

    claimForm_id:                        _.claimForm_id?.toString(),
    claimForm_fileName:                  _.claimForm_fileName,
    claimForm_uploadedAt:                _.claimForm_uploadedAt?.toISOString(),

    retailPercentage:                    _.retailPercentage,

    di_destructionCompany_type:          _.di_destructionCompany_type?.identifier,
    di_destructionCompany_name:          _.di_destructionCompany_name,
    di_destructionCompany_streetAddress: _.di_destructionCompany_streetAddress,
    di_destructionCompany_city:          _.di_destructionCompany_city,
    di_destructionCompany_state:         _.di_destructionCompany_state?.identifier,
    di_destructionCompany_zipCode:       _.di_destructionCompany_zipCode,
    di_destructionMethod:                _.di_destructionMethod,

    di_distributorDestructionLocation_id:            _.di_distributorDestructionLocation?.id.asInt.toString(),
    di_distributorDestructionLocation_sapId:         _.di_distributorDestructionLocation?.sapId,
    di_distributorDestructionLocation_name:          _.di_distributorDestructionLocation?.name,
    di_distributorDestructionLocation_streetAddress: _.di_distributorDestructionLocation?.streetAddress,
    di_distributorDestructionLocation_city:          _.di_distributorDestructionLocation?.city,
    di_distributorDestructionLocation_state:         _.di_distributorDestructionLocation?.state?.identifier,
    di_distributorDestructionLocation_zipCode:       _.di_distributorDestructionLocation?.zipCode,

    skipTtb:                             _.skipTtb,
    skipTtb_skipReason:                  _.skipTtb_skipReason,
    skipTtb_deferredTaxCredit:           _.skipTtb_deferredTaxCredit,

    ttb_alcoholicCases:                  _.ttb_alcoholicCases,
    ttb_barrels:                         _.ttb_barrels,
    ttb_tax:                             _.ttb_tax,
    ttb_productionLocations:             _.ttb_productionLocations?.map(_ => _.identifier).join(","),

    ttb_dateFaxedToTTB:                  _.ttb_dateFaxedToTTB           ?.toISOString(),
    ttb_noticeOfIntent_id:               _.ttb_noticeOfIntent_id        ?.toString(),
    ttb_noticeOfIntent_fileName:         _.ttb_noticeOfIntent_fileName,
    ttb_noticeOfIntent_uploadedAt:       _.ttb_noticeOfIntent_uploadedAt?.toISOString(),
    ttb_faxCoverSheet_id:                _.ttb_faxCoverSheet_id         ?.toString(),
    ttb_faxCoverSheet_fileName:          _.ttb_faxCoverSheet_fileName,
    ttb_faxCoverSheet_uploadedAt:        _.ttb_faxCoverSheet_uploadedAt ?.toISOString(),

    ttb_ttbApprovalConfirmed:            _.ttb_ttbApprovalConfirmed,
    ttb_skipTtbFollowUp:                 _.ttb_skipTtbFollowUp,
    ttb_ttbFollowUpConfirmedAt:          _.ttb_ttbFollowUpConfirmedAt       ?.toISOString(),
    ttb_overdueTtbApprovalConfirmedAt:   _.ttb_overdueTtbApprovalConfirmedAt?.toISOString(),

    ttb_ttbApprovalDeadline:             _.ttb_ttbApprovalDeadline   ?.toISOString(),
    ttb_ttbApprovalDate:                 _.ttb_ttbApprovalDate       ?.toISOString(),
    ttb_ttbApproval_id:                  _.ttb_ttbApproval_id        ?.toString(),
    ttb_ttbApproval_fileName:            _.ttb_ttbApproval_fileName,
    ttb_ttbApproval_uploadedAt:          _.ttb_ttbApproval_uploadedAt?.toISOString(),

    bbc_destructionApprovalDate:         _.bbc_destructionApprovalDate       ?.toISOString(),
    bbc_destructionApproval_id:          _.bbc_destructionApproval_id        ?.toString(),
    bbc_destructionApproval_fileName:    _.bbc_destructionApproval_fileName,
    bbc_destructionApproval_uploadedAt:  _.bbc_destructionApproval_uploadedAt?.toISOString(),

    aod_earliestDestructionDate:         _.aod_earliestDestructionDate?.toISOString(),
    aod_casesDestroyed:                  _.aod_casesDestroyed,
    aod_hasIssues:                       _.aod_hasIssues,
    aod_issues:                          _.aod_issues?.map(_ => _.identifier).join(","),
    aod_reviewNotes:                     _.aod_reviewNotes,
    // des_affidavits:                  [],
    aod_1_id:                            _.aod_1_id?.toString(),
    aod_1_fileName:                      _.aod_1_fileName,
    aod_1_uploadedAt:                    _.aod_1_uploadedAt?.toISOString(),
    aod_2_id:                            _.aod_2_id?.toString(),
    aod_2_fileName:                      _.aod_2_fileName,
    aod_2_uploadedAt:                    _.aod_2_uploadedAt?.toISOString(),
    aod_3_id:                            _.aod_3_id?.toString(),
    aod_3_fileName:                      _.aod_3_fileName,
    aod_3_uploadedAt:                    _.aod_3_uploadedAt?.toISOString(),
    aod_4_id:                            _.aod_4_id?.toString(),
    aod_4_fileName:                      _.aod_4_fileName,
    aod_4_uploadedAt:                    _.aod_4_uploadedAt?.toISOString(),
    aod_5_id:                            _.aod_5_id?.toString(),
    aod_5_fileName:                      _.aod_5_fileName,
    aod_5_uploadedAt:                    _.aod_5_uploadedAt?.toISOString(),
    aod_6_id:                            _.aod_6_id?.toString(),
    aod_6_fileName:                      _.aod_6_fileName,
    aod_6_uploadedAt:                    _.aod_6_uploadedAt?.toISOString(),
    aod_7_id:                            _.aod_7_id?.toString(),
    aod_7_fileName:                      _.aod_7_fileName,
    aod_7_uploadedAt:                    _.aod_7_uploadedAt?.toISOString(),
    aod_8_id:                            _.aod_8_id?.toString(),
    aod_8_fileName:                      _.aod_8_fileName,
    aod_8_uploadedAt:                    _.aod_8_uploadedAt?.toISOString(),
    aod_9_id:                            _.aod_9_id?.toString(),
    aod_9_fileName:                      _.aod_9_fileName,
    aod_9_uploadedAt:                    _.aod_9_uploadedAt?.toISOString(),
    aod_10_id:                           _.aod_10_id?.toString(),
    aod_10_fileName:                     _.aod_10_fileName,
    aod_10_uploadedAt:                   _.aod_10_uploadedAt?.toISOString(),

    // extra_files:                      [],
    extraFile_1_id:                      _.extraFile_1_id?.toString(),
    extraFile_1_fileName:                _.extraFile_1_fileName,
    extraFile_1_uploadedAt:              _.extraFile_1_uploadedAt?.toISOString(),
    extraFile_2_id:                      _.extraFile_2_id?.toString(),
    extraFile_2_fileName:                _.extraFile_2_fileName,
    extraFile_2_uploadedAt:              _.extraFile_2_uploadedAt?.toISOString(),
    extraFile_3_id:                      _.extraFile_3_id?.toString(),
    extraFile_3_fileName:                _.extraFile_3_fileName,
    extraFile_3_uploadedAt:              _.extraFile_3_uploadedAt?.toISOString(),
    extraFile_4_id:                      _.extraFile_4_id?.toString(),
    extraFile_4_fileName:                _.extraFile_4_fileName,
    extraFile_4_uploadedAt:              _.extraFile_4_uploadedAt?.toISOString(),
    extraFile_5_id:                      _.extraFile_5_id?.toString(),
    extraFile_5_fileName:                _.extraFile_5_fileName,
    extraFile_5_uploadedAt:              _.extraFile_5_uploadedAt?.toISOString(),
    extraFile_6_id:                      _.extraFile_6_id?.toString(),
    extraFile_6_fileName:                _.extraFile_6_fileName,
    extraFile_6_uploadedAt:              _.extraFile_6_uploadedAt?.toISOString(),
    extraFile_7_id:                      _.extraFile_7_id?.toString(),
    extraFile_7_fileName:                _.extraFile_7_fileName,
    extraFile_7_uploadedAt:              _.extraFile_7_uploadedAt?.toISOString(),
    extraFile_8_id:                      _.extraFile_8_id?.toString(),
    extraFile_8_fileName:                _.extraFile_8_fileName,
    extraFile_8_uploadedAt:              _.extraFile_8_uploadedAt?.toISOString(),
    extraFile_9_id:                      _.extraFile_9_id?.toString(),
    extraFile_9_fileName:                _.extraFile_9_fileName,
    extraFile_9_uploadedAt:              _.extraFile_9_uploadedAt?.toISOString(),
    extraFile_10_id:                     _.extraFile_10_id?.toString(),
    extraFile_10_fileName:               _.extraFile_10_fileName,
    extraFile_10_uploadedAt:             _.extraFile_10_uploadedAt?.toISOString(),

    // Accounting Approval - Move to separate object
    acc_casesCredited:                   _.acc_casesCredited,
    acc_creditAmount:                    _.acc_creditAmount,
    acc_accountingApprovalDate:          _.acc_accountingApprovalDate?.toISOString(),
    acc_creditProcessingNotes:           _.acc_creditProcessingNotes,
    acc_creditMemoDate:                  _.acc_creditMemoDate?.toISOString(),

    baseline_persona:                    _.baseline?.persona?.identifier,
    baseline_action:                     _.baseline?.action  .identifier,
    baseline_dueDate:                    _.baseline?.dueDate?.toISOString(),

                                         ...SYS.toRawSysInfo(_.sysInfo),

    keyMoment_submittedAt:                                  _.keyMoments?.submitted?.at?.toISOString(),
    keyMoment_submittedBy_id:                               _.keyMoments?.submitted?.by?.id.asInt.toString(),
    keyMoment_submittedBy_name:                             _.keyMoments?.submitted?.by?.name,
    keyMoment_submittedBy_managerLevel:                     _.keyMoments?.submitted?.by?.managerLevel?.identifier,
    keyMoment_sentBackAt:                                   _.keyMoments?.sentBack?.at?.toISOString(),
    keyMoment_sentBackBy_id:                                _.keyMoments?.sentBack?.by?.id.asInt.toString(),
    keyMoment_sentBackBy_name:                              _.keyMoments?.sentBack?.by?.name,
    keyMoment_sentBackBy_managerLevel:                      _.keyMoments?.sentBack?.by?.managerLevel?.identifier,
    keyMoment_acceptedAt:                                   _.keyMoments?.accepted?.at?.toISOString(),
    keyMoment_acceptedBy_id:                                _.keyMoments?.accepted?.by?.id.asInt.toString(),
    keyMoment_acceptedBy_name:                              _.keyMoments?.accepted?.by?.name,
    keyMoment_acceptedBy_managerLevel:                      _.keyMoments?.accepted?.by?.managerLevel?.identifier,
    keyMoment_firstLevelApprovedAt:                         _.keyMoments?.firstLevelApproved?.at?.toISOString(),
    keyMoment_firstLevelApprovedBy_id:                      _.keyMoments?.firstLevelApproved?.by?.id.asInt.toString(),
    keyMoment_firstLevelApprovedBy_name:                    _.keyMoments?.firstLevelApproved?.by?.name,
    keyMoment_firstLevelApprovedBy_managerLevel:            _.keyMoments?.firstLevelApproved?.by?.managerLevel?.identifier,
    keyMoment_sapOrderCreatedAt:                            _.keyMoments?.sapOrderCreated?.at?.toISOString(),
    keyMoment_sapOrderCreatedBy_id:                         _.keyMoments?.sapOrderCreated?.by?.id.asInt.toString(),
    keyMoment_sapOrderCreatedBy_name:                       _.keyMoments?.sapOrderCreated?.by?.name,
    keyMoment_sapOrderCreatedBy_managerLevel:               _.keyMoments?.sapOrderCreated?.by?.managerLevel?.identifier,
    keyMoment_claimApprovalConfirmedAt:                     _.keyMoments?.claimApprovalConfirmed?.at?.toISOString(),
    keyMoment_claimApprovalConfirmedBy_id:                  _.keyMoments?.claimApprovalConfirmed?.by?.id.asInt.toString(),
    keyMoment_claimApprovalConfirmedBy_name:                _.keyMoments?.claimApprovalConfirmed?.by?.name,
    keyMoment_claimApprovalConfirmedBy_managerLevel:        _.keyMoments?.claimApprovalConfirmed?.by?.managerLevel?.identifier,
    keyMoment_ttbSubmissionConfirmedAt:                     _.keyMoments?.ttb_submissionConfirmed?.at?.toISOString(),
    keyMoment_ttbSubmissionConfirmedBy_id:                  _.keyMoments?.ttb_submissionConfirmed?.by?.id.asInt.toString(),
    keyMoment_ttbSubmissionConfirmedBy_name:                _.keyMoments?.ttb_submissionConfirmed?.by?.name,
    keyMoment_ttbSubmissionConfirmedBy_managerLevel:        _.keyMoments?.ttb_submissionConfirmed?.by?.managerLevel?.identifier,
    keyMoment_destructionApprovalConfirmedAt:               _.keyMoments?.destructionApprovalConfirmed?.at?.toISOString(),
    keyMoment_destructionApprovalConfirmedBy_id:            _.keyMoments?.destructionApprovalConfirmed?.by?.id.asInt.toString(),
    keyMoment_destructionApprovalConfirmedBy_name:          _.keyMoments?.destructionApprovalConfirmed?.by?.name,
    keyMoment_destructionApprovalConfirmedBy_managerLevel:  _.keyMoments?.destructionApprovalConfirmed?.by?.managerLevel?.identifier,
    keyMoment_destructionConfirmedAt:                       _.keyMoments?.destructionConfirmed?.at?.toISOString(),
    keyMoment_destructionConfirmedBy_id:                    _.keyMoments?.destructionConfirmed?.by?.id.asInt.toString(),
    keyMoment_destructionConfirmedBy_name:                  _.keyMoments?.destructionConfirmed?.by?.name,
    keyMoment_destructionConfirmedBy_managerLevel:          _.keyMoments?.destructionConfirmed?.by?.managerLevel?.identifier,
    keyMoment_sapEntryConfirmedAt:                          _.keyMoments?.sapEntryConfirmed?.at?.toISOString(),
    keyMoment_sapEntryConfirmedBy_id:                       _.keyMoments?.sapEntryConfirmed?.by?.id.asInt.toString(),
    keyMoment_sapEntryConfirmedBy_name:                     _.keyMoments?.sapEntryConfirmed?.by?.name,
    keyMoment_sapEntryConfirmedBy_managerLevel:             _.keyMoments?.sapEntryConfirmed?.by?.managerLevel?.identifier,
    keyMoment_closedAt:                                     _.keyMoments?.closed?.at?.toISOString(),
    keyMoment_closedBy_id:                                  _.keyMoments?.closed?.by?.id.asInt.toString(),
    keyMoment_closedBy_name:                                _.keyMoments?.closed?.by?.name,
    keyMoment_closedBy_managerLevel:                        _.keyMoments?.closed?.by?.managerLevel?.identifier,

    completeProductLineItemCount:          _.completeProductLineItemCount,
    incompleteProductLineItemCount:        _.incompleteProductLineItemCount,

    signature_submitterName:               _.signature_submitterName,
    signature_distributorAcknowledgment:   _.signature_distributorAcknowledgment,

    claimNotes:                            _.claimNotes,
    distributorNotes:                      _.distributorNotes,

    claimVerificationMethod:              _.claimVerificationMethod?.identifier,
    bbcApproverNotes:                      _.bbcApproverNotes,

    issuesAssignedTo:                      _.issuesAssignedTo?.map(_ => _.identifier).join(","),
    openIssueCount:                        _.openIssueCount,
    resolvedIssueCount:                    _.resolvedIssueCount,
    currentIssueType:                      _.currentIssueType?.identifier,

    testingTags:                           _.testingTags?.map(_ => _.identifier).join(","),

    extensionOne:                          ce1_toRaw(_.extensionOne),
  });

  export const distributorFromRaw = (_: {
    distributor_id?:    string,
    distributor_sapId?: string,
    distributor_name?:  string,
  }): Distributor | undefined => (
    _.distributor_id ? {
      __type: "Distributor",
      id:     Id.from(BigInt(_.distributor_id)) as DistributorId,
      sapId:  _.distributor_sapId!,
      name:   _.distributor_name!,
    } : undefined
  );

  export const fromRawClaim = (cleanupContext: () => ClaimCleanupContext) => (_: RawClaim): Claim => claim(cleanupContext)({

    id:                                                                      Id.from(BigInt(_.id)) as ClaimId,

    schemaVersion:                                                           CLP.schemaVersion(_.schemaVersion),

    claimType:                                                               CLM.claimType(_.claimType),
    claimReason:                                                             CLM.claimReason(_.claimReason),
    itemized:                                                                _.itemized,
    involvesTaxCredit:                                                       _.involvesTaxCredit,
    sapOrderTiming:                                                          CLM.sapOrderTiming(_.sapOrderTiming),

    stage:                                                                   CLP.stage       (_.stage),
    phase:                                                                   CLP.phase       (_.phase),
    deliverable:                         _.deliverable                    && CLP.deliverable (_.deliverable),

    bbcStatus:                                                               CLP.bbcStatus        (_.bbcStatus),
    distributorStatus:                   _.distributorStatus              && CLP.distributorStatus(_.distributorStatus),
    cancelationReason:                                                       _.cancelationReason,
    denialReason:                                                            _.denialReason,

    claimNumber:                                                             _.claimNumber,
    testClaimNumber:                                                         _.testClaimNumber,
    sapOrderNumber:                                                          _.sapOrderNumber,
    beverageType:                                                            _.beverageType ? beverageType(_.beverageType) : undefined,
    casesRequested:                                                          _.casesRequested,

    distributor:                                                             distributorFromRaw(_),

    distributorLocation:                                                     _.distributorLocation_id && _.distributorLocation_state && {
                                                                               id:            Id.from(BigInt(_.distributorLocation_id)) as DistributorLocationId,
                                                                               sapId:         _.distributorLocation_sapId,
                                                                               name:          _.distributorLocation_name,
                                                                               streetAddress: _.distributorLocation_streetAddress,
                                                                               city:          _.distributorLocation_city,
                                                                               state:         CLM.state(_.distributorLocation_state),
                                                                               zipCode:       _.distributorLocation_zipCode,
                                                                             },

    wholesaler_sapId:                                                        _.wholesaler_sapId,
    wholesaler_nameOverride:                                                 _.wholesaler_nameOverride,
    wholesaler_contactName:                                                  _.wholesaler_contactName,
    wholesaler_contactEmail:                                                 _.wholesaler_contactEmail,

    registeredOn:                                                            new Date(_.registeredOn),
    noi:                                                                     _.noi,

    flca_claimApprover_level:                                                _.flca_claimApprover_level,
    flca_claimApprover_name:                                                 _.flca_claimApprover_name,
    flca_approvedAt:                     _.flca_approvedAt                && new Date(_.flca_approvedAt),
    ulca_claimApprover_levels:                                               _.ulca_claimApprover_levels?.split(","),

    claimForm_id:                        _.claimForm_id                   && BigInt(_.claimForm_id),
    claimForm_fileName:                                                      _.claimForm_fileName,
    claimForm_uploadedAt:                _.claimForm_uploadedAt           && new Date(_.claimForm_uploadedAt),

    retailPercentage:                                                        _.retailPercentage,

    di_destructionCompany_type:          _.di_destructionCompany_type     && CLM.destructionCompanyType(_.di_destructionCompany_type),
    di_destructionCompany_name:                                              _.di_destructionCompany_name,
    di_destructionCompany_streetAddress:                                     _.di_destructionCompany_streetAddress,
    di_destructionCompany_city:                                              _.di_destructionCompany_city,
    di_destructionCompany_state:         _.di_destructionCompany_state    && CLM.state(_.di_destructionCompany_state),
    di_destructionCompany_zipCode:                                           _.di_destructionCompany_zipCode,
    di_destructionMethod:                                                    _.di_destructionMethod,

    di_distributorDestructionLocation:             tryDistributorLocation({
                                                                            id:            _.di_distributorDestructionLocation_id,
                                                                            distributorId: _.distributor_id,
                                                                            sapId:         _.di_distributorDestructionLocation_sapId,
                                                                            name:          _.di_distributorDestructionLocation_name,
                                                                            streetAddress: _.di_distributorDestructionLocation_streetAddress,
                                                                            city:          _.di_distributorDestructionLocation_city,
                                                                            state:         _.di_distributorDestructionLocation_state,
                                                                            zipCode:       _.di_distributorDestructionLocation_zipCode
                                                                          }),

    skipTtb:                                                                 _.skipTtb,
    skipTtb_skipReason:                                                      _.skipTtb_skipReason,
    skipTtb_deferredTaxCredit:                                               _.skipTtb_deferredTaxCredit,

    ttb_alcoholicCases:                                                      _.ttb_alcoholicCases,
    ttb_barrels:                                                             _.ttb_barrels,
    ttb_tax:                                                                 _.ttb_tax,
    ttb_productionLocations:                                                 _.ttb_productionLocations?.split(",")?.map(CLM.productionLocation),

    ttb_dateFaxedToTTB:                  _.ttb_dateFaxedToTTB             && new Date(_.ttb_dateFaxedToTTB),
    ttb_noticeOfIntent_id:               _.ttb_noticeOfIntent_id          && BigInt(_.ttb_noticeOfIntent_id),
    ttb_noticeOfIntent_fileName:                                             _.ttb_noticeOfIntent_fileName,
    ttb_noticeOfIntent_uploadedAt:       _.ttb_noticeOfIntent_uploadedAt  && new Date(_.ttb_noticeOfIntent_uploadedAt),
    ttb_faxCoverSheet_id:                _.ttb_faxCoverSheet_id           && BigInt(_.ttb_faxCoverSheet_id),
    ttb_faxCoverSheet_fileName:                                              _.ttb_faxCoverSheet_fileName,
    ttb_faxCoverSheet_uploadedAt:        _.ttb_faxCoverSheet_uploadedAt   && new Date(_.ttb_faxCoverSheet_uploadedAt),

    ttb_ttbApprovalConfirmed:                                                _.ttb_ttbApprovalConfirmed,
    ttb_skipTtbFollowUp:                                                    _.ttb_skipTtbFollowUp,
    ttb_ttbFollowUpConfirmedAt:          _.ttb_ttbFollowUpConfirmedAt     && new Date(_.ttb_ttbFollowUpConfirmedAt),
    ttb_overdueTtbApprovalConfirmedAt:   _.ttb_overdueTtbApprovalConfirmedAt && new Date(_.ttb_overdueTtbApprovalConfirmedAt),

    ttb_ttbApprovalDeadline:             _.ttb_ttbApprovalDeadline        && new Date(_.ttb_ttbApprovalDeadline),
    ttb_ttbApprovalDate:                 _.ttb_ttbApprovalDate            && new Date(_.ttb_ttbApprovalDate),
    ttb_ttbApproval_id:                  _.ttb_ttbApproval_id             && BigInt(_.ttb_ttbApproval_id),
    ttb_ttbApproval_fileName:                                                _.ttb_ttbApproval_fileName,
    ttb_ttbApproval_uploadedAt:          _.ttb_ttbApproval_uploadedAt     && new Date(_.ttb_ttbApproval_uploadedAt),

    bbc_destructionApprovalDate:         _.bbc_destructionApprovalDate        && new Date(_.bbc_destructionApprovalDate),
    bbc_destructionApproval_id:          _.bbc_destructionApproval_id         && BigInt(_.bbc_destructionApproval_id),
    bbc_destructionApproval_fileName:    _.bbc_destructionApproval_fileName,
    bbc_destructionApproval_uploadedAt:  _.bbc_destructionApproval_uploadedAt && new Date(_.bbc_destructionApproval_uploadedAt),

    aod_earliestDestructionDate:         _.aod_earliestDestructionDate    && new Date(_.aod_earliestDestructionDate),
    aod_casesDestroyed:                                                      _.aod_casesDestroyed,
    aod_hasIssues:                                                           _.aod_hasIssues,
    aod_issues:                                                              _.aod_issues?.split(",")?.map(CLM.aodIssue),
    aod_reviewNotes:                                                         _.aod_reviewNotes,
    // des_affidavits:                  [],
    aod_1_id:                            _.aod_1_id                       && BigInt(_.aod_1_id),
    aod_1_fileName:                                                          _.aod_1_fileName,
    aod_1_uploadedAt:                    _.aod_1_uploadedAt               && new Date(_.aod_1_uploadedAt),
    aod_2_id:                            _.aod_2_id                       && BigInt(_.aod_2_id),
    aod_2_fileName:                                                          _.aod_2_fileName,
    aod_2_uploadedAt:                    _.aod_2_uploadedAt               && new Date(_.aod_2_uploadedAt),
    aod_3_id:                            _.aod_3_id                       && BigInt(_.aod_3_id),
    aod_3_fileName:                                                          _.aod_3_fileName,
    aod_3_uploadedAt:                    _.aod_3_uploadedAt               && new Date(_.aod_3_uploadedAt),
    aod_4_id:                            _.aod_4_id                       && BigInt(_.aod_4_id),
    aod_4_fileName:                                                          _.aod_4_fileName,
    aod_4_uploadedAt:                    _.aod_4_uploadedAt               && new Date(_.aod_4_uploadedAt),
    aod_5_id:                            _.aod_5_id                       && BigInt(_.aod_5_id),
    aod_5_fileName:                                                          _.aod_5_fileName,
    aod_5_uploadedAt:                    _.aod_5_uploadedAt               && new Date(_.aod_5_uploadedAt),
    aod_6_id:                            _.aod_6_id                       && BigInt(_.aod_6_id),
    aod_6_fileName:                                                          _.aod_6_fileName,
    aod_6_uploadedAt:                    _.aod_6_uploadedAt               && new Date(_.aod_6_uploadedAt),
    aod_7_id:                            _.aod_7_id                       && BigInt(_.aod_7_id),
    aod_7_fileName:                                                          _.aod_7_fileName,
    aod_7_uploadedAt:                    _.aod_7_uploadedAt               && new Date(_.aod_7_uploadedAt),
    aod_8_id:                            _.aod_8_id                       && BigInt(_.aod_8_id),
    aod_8_fileName:                                                          _.aod_8_fileName,
    aod_8_uploadedAt:                    _.aod_8_uploadedAt               && new Date(_.aod_8_uploadedAt),
    aod_9_id:                            _.aod_9_id                       && BigInt(_.aod_9_id),
    aod_9_fileName:                                                          _.aod_9_fileName,
    aod_9_uploadedAt:                    _.aod_9_uploadedAt               && new Date(_.aod_9_uploadedAt),
    aod_10_id:                           _.aod_10_id                      && BigInt(_.aod_10_id),
    aod_10_fileName:                                                         _.aod_10_fileName,
    aod_10_uploadedAt:                   _.aod_10_uploadedAt              && new Date(_.aod_10_uploadedAt),

    // extra_files:                      [],
    extraFile_1_id:                      _.extraFile_1_id                 && BigInt(_.extraFile_1_id),
    extraFile_1_fileName:                                                    _.extraFile_1_fileName,
    extraFile_1_uploadedAt:              _.extraFile_1_uploadedAt         && new Date(_.extraFile_1_uploadedAt),
    extraFile_2_id:                      _.extraFile_2_id                 && BigInt(_.extraFile_2_id),
    extraFile_2_fileName:                                                    _.extraFile_2_fileName,
    extraFile_2_uploadedAt:              _.extraFile_2_uploadedAt         && new Date(_.extraFile_2_uploadedAt),
    extraFile_3_id:                      _.extraFile_3_id                 && BigInt(_.extraFile_3_id),
    extraFile_3_fileName:                                                    _.extraFile_3_fileName,
    extraFile_3_uploadedAt:              _.extraFile_3_uploadedAt         && new Date(_.extraFile_3_uploadedAt),
    extraFile_4_id:                      _.extraFile_4_id                 && BigInt(_.extraFile_4_id),
    extraFile_4_fileName:                                                    _.extraFile_4_fileName,
    extraFile_4_uploadedAt:              _.extraFile_4_uploadedAt         && new Date(_.extraFile_4_uploadedAt),
    extraFile_5_id:                      _.extraFile_5_id                 && BigInt(_.extraFile_5_id),
    extraFile_5_fileName:                                                    _.extraFile_5_fileName,
    extraFile_5_uploadedAt:              _.extraFile_5_uploadedAt         && new Date(_.extraFile_5_uploadedAt),
    extraFile_6_id:                      _.extraFile_6_id                 && BigInt(_.extraFile_6_id),
    extraFile_6_fileName:                                                    _.extraFile_6_fileName,
    extraFile_6_uploadedAt:              _.extraFile_6_uploadedAt         && new Date(_.extraFile_6_uploadedAt),
    extraFile_7_id:                      _.extraFile_7_id                 && BigInt(_.extraFile_7_id),
    extraFile_7_fileName:                                                    _.extraFile_7_fileName,
    extraFile_7_uploadedAt:              _.extraFile_7_uploadedAt         && new Date(_.extraFile_7_uploadedAt),
    extraFile_8_id:                      _.extraFile_8_id                 && BigInt(_.extraFile_8_id),
    extraFile_8_fileName:                                                    _.extraFile_8_fileName,
    extraFile_8_uploadedAt:              _.extraFile_8_uploadedAt         && new Date(_.extraFile_8_uploadedAt),
    extraFile_9_id:                      _.extraFile_9_id                 && BigInt(_.extraFile_9_id),
    extraFile_9_fileName:                                                    _.extraFile_9_fileName,
    extraFile_9_uploadedAt:              _.extraFile_9_uploadedAt         && new Date(_.extraFile_9_uploadedAt),
    extraFile_10_id:                     _.extraFile_10_id                && BigInt(_.extraFile_10_id),
    extraFile_10_fileName:                                                   _.extraFile_10_fileName,
    extraFile_10_uploadedAt:             _.extraFile_10_uploadedAt        && new Date(_.extraFile_10_uploadedAt),

    // Accounting Approval - Move to separate object
    acc_casesCredited:                   _.acc_casesCredited,
    acc_creditAmount:                    _.acc_creditAmount,
    acc_accountingApprovalDate:          _.acc_accountingApprovalDate     && new Date(_.acc_accountingApprovalDate),
    acc_creditProcessingNotes:                                              _.acc_creditProcessingNotes,
    acc_creditMemoDate:                  _.acc_creditMemoDate             && new Date(_.acc_creditMemoDate),

    baseline_persona:                    _.baseline_persona               && CLP.actorType(_.baseline_persona),
    baseline_action:                     _.baseline_action                && CLP.action   (_.baseline_action ),
    baseline_dueDate:                    _.baseline_dueDate               && new Date     (_.baseline_dueDate),

    sysInfo:                                                              SYS.fromRawSysInfo(_),

    keyMoments:                          {
                                            submitted:                    SYS.optionalKeyMoment({
                                                                                            at:              _.keyMoment_submittedAt,
                                                                                            by_id:           _.keyMoment_submittedBy_id,
                                                                                            by_name:         _.keyMoment_submittedBy_name,
                                                                                            by_managerLevel: _.keyMoment_submittedBy_managerLevel,
                                                                                          }),
                                            sentBack:                     SYS.optionalKeyMoment({
                                                                                            at:              _.keyMoment_sentBackAt,
                                                                                            by_id:           _.keyMoment_sentBackBy_id,
                                                                                            by_name:         _.keyMoment_sentBackBy_name,
                                                                                            by_managerLevel: _.keyMoment_sentBackBy_managerLevel,
                                                                                          }),
                                            accepted:                     SYS.optionalKeyMoment({
                                                                                            at:              _.keyMoment_acceptedAt,
                                                                                            by_id:           _.keyMoment_acceptedBy_id,
                                                                                            by_name:         _.keyMoment_acceptedBy_name,
                                                                                            by_managerLevel: _.keyMoment_acceptedBy_managerLevel,
                                                                                          }),
                                            claimApprovalConfirmed:       SYS.optionalKeyMoment({
                                                                                            at:      _.keyMoment_claimApprovalConfirmedAt,
                                                                                            by_id:   _.keyMoment_claimApprovalConfirmedBy_id,
                                                                                            by_name: _.keyMoment_claimApprovalConfirmedBy_name,
                                                                                          }),
                                            firstLevelApproved:           SYS.optionalKeyMoment({
                                                                                            at:              _.keyMoment_firstLevelApprovedAt,
                                                                                            by_id:           _.keyMoment_firstLevelApprovedBy_id,
                                                                                            by_name:         _.keyMoment_firstLevelApprovedBy_name,
                                                                                            by_managerLevel: _.keyMoment_firstLevelApprovedBy_managerLevel,
                                                                                          }),
                                            sapOrderCreated:              SYS.optionalKeyMoment({
                                                                                            at:              _.keyMoment_sapOrderCreatedAt,
                                                                                            by_id:           _.keyMoment_sapOrderCreatedBy_id,
                                                                                            by_name:         _.keyMoment_sapOrderCreatedBy_name,
                                                                                            by_managerLevel: _.keyMoment_sapOrderCreatedBy_managerLevel,
                                                                                          }),
                                            ttb_submissionConfirmed:      SYS.optionalKeyMoment({
                                                                                            at:              _.keyMoment_ttbSubmissionConfirmedAt,
                                                                                            by_id:           _.keyMoment_ttbSubmissionConfirmedBy_id,
                                                                                            by_name:         _.keyMoment_ttbSubmissionConfirmedBy_name,
                                                                                            by_managerLevel: _.keyMoment_ttbSubmissionConfirmedBy_managerLevel,
                                                                                          }),
                                            destructionApprovalConfirmed: SYS.optionalKeyMoment({
                                                                                            at:              _.keyMoment_destructionApprovalConfirmedAt,
                                                                                            by_id:           _.keyMoment_destructionApprovalConfirmedBy_id,
                                                                                            by_name:         _.keyMoment_destructionApprovalConfirmedBy_name,
                                                                                            by_managerLevel: _.keyMoment_destructionApprovalConfirmedBy_managerLevel,
                                                                                          }),
                                            destructionConfirmed:         SYS.optionalKeyMoment({
                                                                                            at:              _.keyMoment_destructionConfirmedAt,
                                                                                            by_id:           _.keyMoment_destructionConfirmedBy_id,
                                                                                            by_name:         _.keyMoment_destructionConfirmedBy_name,
                                                                                            by_managerLevel: _.keyMoment_destructionConfirmedBy_managerLevel,
                                                                                          }),
                                            sapEntryConfirmed:            SYS.optionalKeyMoment({
                                                                                            at:              _.keyMoment_sapEntryConfirmedAt,
                                                                                            by_id:           _.keyMoment_sapEntryConfirmedBy_id,
                                                                                            by_name:         _.keyMoment_sapEntryConfirmedBy_name,
                                                                                            by_managerLevel: _.keyMoment_sapEntryConfirmedBy_managerLevel,
                                                                                          }),
                                            closed:                       SYS.optionalKeyMoment({
                                                                                            at:              _.keyMoment_closedAt,
                                                                                            by_id:           _.keyMoment_closedBy_id,
                                                                                            by_name:         _.keyMoment_closedBy_name,
                                                                                            by_managerLevel: _.keyMoment_closedBy_managerLevel,
                                                                                          }),
                                         },

    completeProductLineItemCount:                                            _.completeProductLineItemCount,
    incompleteProductLineItemCount:                                          _.incompleteProductLineItemCount,

    signature_submitterName:                                                 _.signature_submitterName,
    signature_distributorAcknowledgment:                                     _.signature_distributorAcknowledgment,

    claimNotes:                                                              _.claimNotes,
    distributorNotes:                                                        _.distributorNotes,

    claimVerificationMethod:                                                 _.claimVerificationMethod && CLM.claimVerificationMethod(_.claimVerificationMethod),
    bbcApproverNotes:                                                        _.bbcApproverNotes,

    issuesAssignedTo:                                                        _.issuesAssignedTo?.split(",")?.map(CLM.userGroup),
    openIssueCount:                                                          _.openIssueCount,
    resolvedIssueCount:                                                      _.resolvedIssueCount,
    currentIssueType:                                                        _.currentIssueType && CLM.issueType(_.currentIssueType),

    testingTags:                                                             _.testingTags?.split(",")?.map(SYS.testingTag),

    extensionOne:                                                            ce1_fromRaw(_.extensionOne),
  } as Claim & ClaimHelpers);

  export const claimErrors = ({ currentUser }: { currentUser: () => User }) => (
    (_: Claim) => claimValidator({ currentUser }).validationErrors(_)
  );

  export const isValidClaim = ({ currentUser }: { currentUser: () => User }) => (
   (_: Claim) => claimValidator({ currentUser }).isValid(_)
  );
  export const validateClaims = ({ currentUser }: { currentUser: () => User }) => (

    (claims: Claim[]) => {

      const validator = claimValidator({ currentUser });

      const validationErrors = claims
        .filter(_ => !validator.isValid(_))
        .map(_ => ({ claim: _, errors: validator.validationErrors(_) }));

      assert(
        validationErrors.length === 0,
        `Errors found in defined Claims with following error codes. ${JSON.stringify(validationErrors, stringifyReplacer, 2)}`
      );
    }
  );
  export const validateRawClaims = (claimCleanupContext: () => ClaimCleanupContext) => (

    (rawClaims: RawClaim[]) : Claim[] => {

      const ccc = claimCleanupContext();
      const { currentUser } = ccc;

      const claims = rawClaims.map(fromRawClaim(() => ccc));

      validateClaims({ currentUser: () => currentUser })(claims);

      return claims;
    }
  );

  // #region Claim Summary
  export type RawClaimSummary = {
    id:                              string;
    schemaVersion:                   string;
    claimType:                       string;
    stage:                           string;
    phase:                           string;
    deliverable?:                    string;
    bbcStatus:                       string;
    distributorStatus?:              string;
    claimNumber:                     string;
    testClaimNumber?:                number;
    sapOrderNumber?:                 string;
    beverageType?:                   string;
    casesRequested?:                 number;
    noi?:                            string;
    registeredOn:                    string;

    distributor_id?:                 string;
    distributor_sapId?:              string;
    distributor_name?:               string;

    distributorLocation_id?:         string;
    distributorLocation_sapId?:      string;
    distributorLocation_name?:       string;
    distributorLocation_state?:      string;

    wholesaler_sapId:                string;
    wholesaler_nameOverride?:        string;

    baseline_persona?:               string;
    baseline_action?:                string;
    baseline_dueDate?:               string;

    ttb_skipTtbFollowUp:             boolean;
    ttb_ttbFollowUpConfirmedAt?:     string;

    openIssueCount?:                 number;
    issuesAssignedTo?:               string;
    currentIssueType?:               string;

    incompleteProductLineItemCount?: number;
    completeProductLineItemCount?:   number;

    keyMoment_submittedAt?:          string;
    keyMoment_submittedBy_id?:       string;
    keyMoment_submittedBy_name?:     string;
    keyMoment_closedAt?:             string;
    keyMoment_closedBy_id?:          string;
    keyMoment_closedBy_name?:        string;
  };

  export type ClaimSummary = {
    id:                              ClaimId;
    schemaVersion:                   CLP.SchemaVersion;
    claimType:                       ClaimType;
    stage:                           CLP.Stage;
    phase:                           CLP.Phase;
    deliverable?:                    CLP.Deliverable;
    claimNumber:                     string;
    testClaimNumber?:                number;
    sapOrderNumber?:                 string;
    casesRequested?:                 number;
    noi?:                            string;
    beverageType?:                   BeverageType;
    registeredOn:                    Date;
    distributor?:                    Distributor;
    distributorLocation?:            { id: DistributorLocationId; name: string; sapId: string };
    wholesaler_sapId:                string;
    wholesaler_name?:                string;
    bbcStatus:                       CLP.BbcStatus;
    distributorStatus?:              CLP.DistributorStatus;
    openIssueCount?:                 number;
    currentIssueType?:               IssueType;
    incompleteProductLineItemCount?: number;
    baseline?:                       Activity<CLP.BaselineAction>;
    assigned?:                       Activity<CLP.AssignableAction>;
    current?:                        Activity<CLP.AssignableAction>;
    keyMoments:                      Pick<ClaimKeyMoments, "submitted" | "closed">;
  };

  // Compile-time guarantee that a full Claim can be passed anywhere a ClaimSummary is
  // expected. ClaimSummary must remain a structural supertype of Claim — if a future edit
  // adds a required ClaimSummary field absent on Claim, narrows a field below Claim's, or
  // changes a Claim field incompatibly, this line fails to compile. No runtime cost.
  // TODO: Replace this by rewritting ClaimSummary to based on Claim using Pick<...>
  type _ClaimIsAssignableToClaimSummary = Claim extends ClaimSummary ? true : never;
  const _claimIsAssignableToClaimSummary: _ClaimIsAssignableToClaimSummary = true;

  export const fromRawClaimSummary = (cleanupContext: () => ClaimCleanupContext) => (_: RawClaimSummary): ClaimSummary => {

    const intermediate = {
      stage:                          CLP.stage(_.stage),
      phase:                          CLP.phase(_.phase),
      deliverable:                    _.deliverable                && CLP.deliverable(_.deliverable),
      bbcStatus:                      CLP.bbcStatus(_.bbcStatus),
      distributorStatus:              _.distributorStatus          && CLP.distributorStatus(_.distributorStatus),
      wholesaler_sapId:               _.wholesaler_sapId,
      wholesaler_nameOverride:        _.wholesaler_nameOverride,
      baseline_persona:               _.baseline_persona           && CLP.actorType(_.baseline_persona),
      baseline_action:                _.baseline_action            && CLP.action(_.baseline_action),
      baseline_dueDate:               _.baseline_dueDate           && new Date(_.baseline_dueDate),
      ttb_skipTtbFollowUp:            _.ttb_skipTtbFollowUp,
      ttb_ttbFollowUpConfirmedAt:     _.ttb_ttbFollowUpConfirmedAt  && new Date(_.ttb_ttbFollowUpConfirmedAt),
      openIssueCount:                 _.openIssueCount,
      issuesAssignedTo:               _.issuesAssignedTo?.split(",")?.map(userGroup),
      incompleteProductLineItemCount: _.incompleteProductLineItemCount,
    } as unknown as Claim & ClaimHelpers;

    const derived = deriveProcessFields(cleanupContext)(intermediate);

    return {
      id:                             Id.from(BigInt(_.id)) as ClaimId,
      schemaVersion:                  CLP.schemaVersion(_.schemaVersion),
      claimType:                      claimType(_.claimType),
      stage:                          intermediate.stage,
      phase:                          intermediate.phase,
      deliverable:                    intermediate.deliverable,
      claimNumber:                    _.claimNumber,
      testClaimNumber:                _.testClaimNumber,
      sapOrderNumber:                 _.sapOrderNumber,
      casesRequested:                 _.casesRequested,
      noi:                            _.noi,
      beverageType:                   _.beverageType ? beverageType(_.beverageType) : undefined,
      registeredOn:                   new Date(_.registeredOn),
      distributor:                    distributorFromRaw(_),
      distributorLocation:            _.distributorLocation_id && _.distributorLocation_state ? {
                                        id:    Id.from(BigInt(_.distributorLocation_id)) as DistributorLocationId,
                                        name:  _.distributorLocation_name!,
                                        sapId: _.distributorLocation_sapId!,
                                      } : undefined,
      wholesaler_sapId:               _.wholesaler_sapId,
      wholesaler_name:                derived.wholesaler_name,
      bbcStatus:                      derived.bbcStatus,
      distributorStatus:              intermediate.distributorStatus,
      openIssueCount:                 _.openIssueCount,
      currentIssueType:               _.currentIssueType ? issueType(_.currentIssueType) : undefined,
      incompleteProductLineItemCount: _.incompleteProductLineItemCount,
      baseline:                       derived.baseline,
      assigned:                       derived.assigned,
      current:                        derived.current,
      keyMoments: {
        submitted:                    SYS.optionalKeyMoment({ at: _.keyMoment_submittedAt, by_id: _.keyMoment_submittedBy_id, by_name: _.keyMoment_submittedBy_name }),
        closed:                       SYS.optionalKeyMoment({ at: _.keyMoment_closedAt,    by_id: _.keyMoment_closedBy_id,    by_name: _.keyMoment_closedBy_name }),
      },
    };
  };

  // #endregion

// #endregion

// #region BbcEmployee

export type BbcEmployee = {
  __type:       "BbcEmployee";
  id:            BbcEmployeeId;
  managerLevel:  CLM.ManagerLevel;
  name:          string;
  email:         string;
};

export type RawBbcEmployee = Omit<BbcEmployee, "id" | "managerLevel"> & {
  id:                    string;
  managerLevel:          string;
};

export const toRawBbcEmployee = (_: BbcEmployee): RawBbcEmployee => {
  return {
    ..._,
    id:           _.id.asInt.toString(),
    managerLevel: _.managerLevel.identifier,
} as RawBbcEmployee};

export const fromRawBbcEmployee = (_: RawBbcEmployee): BbcEmployee => {
  return {
    ..._,
    id:                   Id.from(BigInt(_.id)) as BbcEmployeeId,
    managerLevel:         CLM.managerLevel(_.managerLevel),
} as BbcEmployee};

const bbcEmployeeValidator = validator((bbcEmployee: BbcEmployee) => {
  const { FOR } = objectValidator(bbcEmployee);
  return ({
    a1: FOR(_ => _.id          ).ALWAYS_IS_DEFINED,
    a2: FOR(_ => _.name        ).ALWAYS_IS_DEFINED,
    a3: FOR(_ => _.email       ).ALWAYS_IS_DEFINED,
    a4: FOR(_ => _.managerLevel).ALWAYS_IS_DEFINED,
  });
});

export const bbcEmployeeErrors = (_: BbcEmployee) => bbcEmployeeValidator.validationErrors(_);

export const isValidBbcEmployee = (_: BbcEmployee) => bbcEmployeeValidator.isValid(_);

export const validateBbcEmployees = (_: BbcEmployee[]) => {
  const validationErrors = _
    .filter(_ => !isValidBbcEmployee(_))
    .map(_ => ({ bbcEmployee: _, errors: bbcEmployeeErrors(_) }));

  assert(
    validationErrors.length === 0,
    `Errors found in defined BbcEmployees with following error codes. ${JSON.stringify(validationErrors, stringifyReplacer, 2)}`
  );
};

export const validateRawBbcEmployees = (rawBbcEmployees: RawBbcEmployee[]) => {
  const bbcEmployees = rawBbcEmployees.map(fromRawBbcEmployee);
  validateBbcEmployees(bbcEmployees);
};

export const IS_bbcEmployee = ({ user }: { user: User }) => {
  return user.settings.persona === CLP.ActorType.BBC_APPROVER;
}

export type BbcEmployeeReference = Omit<BbcEmployee, "__type" | "managerLevel" | "email"> & {
  __type: "BbcEmployeeReference";
};

export const bbcEmployeeReference_FROM_user = ({ user }: { user: User }): BbcEmployeeReference => {
  assert(IS_bbcEmployee({ user }),"User must be a BBC employee");
  return {
    __type: "BbcEmployeeReference",
    id:     user.id as Id as BbcEmployeeId,
    name:   user.name,
  };
}

// #endregion BbcEmployee

// #region Distributor

export type Distributor = {
  __type: "Distributor";
  id:     DistributorId;
  sapId:  string;
  name:   string;
};

export type RawDistributor = Omit<Distributor, "id"> & {
  id: string;
};

export const toRawDistributor = (_: Distributor): RawDistributor => {
  return {
    ..._,
    id: _.id.asInt.toString(),
} as RawDistributor};

export const fromRawDistributor = (_: RawDistributor): Distributor => {
  return {
    ..._,
    id:                   Id.from(BigInt(_.id)) as DistributorId,
} as Distributor};

export const distributorValidator = validator((distributor: Distributor) => {
  const { FOR } = objectValidator(distributor);
  return ({
    a1: FOR(_ => _.id   ).ALWAYS_IS_DEFINED,
    a2: FOR(_ => _.sapId).ALWAYS_IS_DEFINED,
    a3: FOR(_ => _.name ).ALWAYS_IS_DEFINED,
  });
});

export const distributorErrors = (_: Distributor) => distributorValidator.validationErrors(_);

export const isValidDistributor = (_: Distributor) => distributorValidator.isValid(_);

export const validateDistributors = (_: Distributor[]) => {
  const validationErrors = _
    .filter(_ => !isValidDistributor(_))
    .map(_ => ({ distributor: _, errors: distributorErrors(_) }));

  assert(
    validationErrors.length === 0,
    `Errors found in defined Distributors with following error codes. ${JSON.stringify(validationErrors, stringifyReplacer, 2)}`
  );
}

export const validateRawDistributors = (rawDistributors: RawDistributor[]) => {
  const distributors = rawDistributors.map(fromRawDistributor);
  validateDistributors(distributors);
}

export type DistributorReference = {
  id: DistributorId;
};

export const distributorReference = (id: string): DistributorReference => {
  return {
    id: Id.from(BigInt(id)) as DistributorId,
  };
}

// #endregion Distributor

// #region DistributorLocation

export type DistributorLocation = {
  __type:        "DistributorLocation";
  id:            DistributorLocationId;
  distributorId: string;
  sapId:         string;
  name:          string;
  streetAddress: string;
  city:          string;
  state:         State;
  zipCode:       string;
};

export type RawDistributorLocation = Omit<DistributorLocation, "id" | "state"> & {
  id:                    string;
  state:                 string;
};

export const toRawDistributorLocation = (_: DistributorLocation): RawDistributorLocation => {
  return {
    ..._,
    id:    _.id.asInt.toString(),
    state: _.state.identifier,
} as RawDistributorLocation};

export const fromRawDistributorLocation = (_: RawDistributorLocation): DistributorLocation => {
  return {
    ..._,
    id:    Id.from(BigInt(_.id)) as DistributorLocationId,
    state: CLM.state(_.state),
} as DistributorLocation};

export const tryDistributorLocation = ({ id, distributorId, sapId, name, streetAddress, city, state, zipCode }: { id?: string | undefined, distributorId?: string | undefined, sapId?: string | undefined, name?: string | undefined, streetAddress?: string | undefined, city?: string | undefined, state?: string | undefined, zipCode?: string | undefined }) => {
  if (!id || !distributorId || !sapId || !name || !streetAddress || !city || !state || !zipCode) {
    return undefined;
  }
  return fromRawDistributorLocation({
    __type: "DistributorLocation",
    id, distributorId, sapId, name, streetAddress, city, state, zipCode
  });
}

export function distributorLocation_fullAddress ({ distributorLocation }: { distributorLocation: DistributorLocation }) {
  return `${distributorLocation.streetAddress}, ${distributorLocation.city}, ${distributorLocation.state.identifier} ${distributorLocation.zipCode}`;
}

export function distributorLocation_fullName ({ distributorLocation, delimiter = " • " }: { distributorLocation: DistributorLocation, delimiter?: string }) {
  return `${distributorLocation.name}${delimiter}${distributorLocation_fullAddress({ distributorLocation })}`;
}

export function distributorLocation_fullName_FOR_user ({ distributorLocation, currentUser }: { distributorLocation: DistributorLocation, currentUser: User }) {
  return `${distributorLocation_fullName({ distributorLocation })}${currentUser.settings.persona !== CLP.ActorType.DISTRIBUTOR ? ` • ${distributorLocation.sapId}` : ""}`;
}

export const distributorLocationValidator = validator((distributorLocation: DistributorLocation) => {
  const { FOR } = objectValidator(distributorLocation);
  return ({
    a1: FOR(_ => _.id           ).ALWAYS_IS_DEFINED,
    a2: FOR(_ => _.sapId        ).ALWAYS_IS_DEFINED,
    a3: FOR(_ => _.name         ).ALWAYS_IS_DEFINED,
    a4: FOR(_ => _.streetAddress).ALWAYS_IS_DEFINED,
    a5: FOR(_ => _.city         ).ALWAYS_IS_DEFINED,
    a6: FOR(_ => _.state        ).ALWAYS_IS_DEFINED,
    a7: FOR(_ => _.zipCode      ).ALWAYS_IS_DEFINED,
  });
});

export const distributorLocationErrors = (_: DistributorLocation) => distributorLocationValidator.validationErrors(_);

export const isValidDistributorLocation = (_: DistributorLocation) => distributorLocationValidator.isValid(_);

export const validateDistributorLocations = (_: DistributorLocation[]) => {
  const validationErrors = _
    .filter(_ => !isValidDistributorLocation(_))
    .map(_ => ({ distributorLocation: _, errors: distributorLocationErrors(_) }));

  assert(
    validationErrors.length === 0,
    `Errors found in defined DistributorLocations with following error codes. ${JSON.stringify(validationErrors, stringifyReplacer, 2)}`
  );
}

export const validateRawDistributorLocations = (rawDistributorLocations: RawDistributorLocation[]) => {
  const distributorLocations = rawDistributorLocations.map(fromRawDistributorLocation);
  validateDistributorLocations(distributorLocations);
}

export type DistributorLocationReference = {
  id:     DistributorLocationId;
  sapId?: string;
}

export const distributorLocationReference = (id: string, sapId?: string): DistributorLocationReference => {
  return {
    id: Id.from(BigInt(id)) as DistributorLocationId,
    sapId
  };
}

// #endregion DistributorLocation

// #region DistributorEmployee

export type DistributorEmployee = {
  __type:                "DistributorEmployee";
  id:                    DistributorEmployeeId;
  name:                  string;
  email:                 string;
};

export type RawDistributorEmployee = Omit<DistributorEmployee, "id"> & {
  id: string;
};

export const toRawDistributorEmployee = (_: DistributorEmployee): RawDistributorEmployee => {
  return {
    ..._,
    id:                   _.id.asInt.toString(),
} as RawDistributorEmployee};

export const fromRawDistributorEmployee = (_: RawDistributorEmployee): DistributorEmployee => {
  return {
    ..._,
    id:                   Id.from(BigInt(_.id)) as DistributorEmployeeId,
} as DistributorEmployee};

export const distributorEmployeeValidator = validator((distributorEmployee: DistributorEmployee) => {
  const { FOR } = objectValidator(distributorEmployee);
  return ({
    a1: FOR(_ => _.id          ).ALWAYS_IS_DEFINED,
    a3: FOR(_ => _.name        ).ALWAYS_IS_DEFINED,
    a4: FOR(_ => _.email       ).ALWAYS_IS_DEFINED,
  });
});

export const distributorEmployeeErrors = (_: DistributorEmployee) => distributorEmployeeValidator.validationErrors(_);

export const isValidDistributorEmployee = (_: DistributorEmployee) => distributorEmployeeValidator.isValid(_);

export const validateDistributorEmployees = (_: DistributorEmployee[]) => {
  const validationErrors = _
    .filter(_ => !isValidDistributorEmployee(_))
    .map(_ => ({ distributorEmployee: _, errors: distributorEmployeeErrors(_) }));

  assert(
    validationErrors.length === 0,
    `Errors found in defined DistributorEmployees with following error codes. ${JSON.stringify(validationErrors, stringifyReplacer, 2)}`
  );
}

export const validateRawDistributorEmployees = (rawDistributorEmployees: RawDistributorEmployee[]) => {
  const distributorEmployees = rawDistributorEmployees.map(fromRawDistributorEmployee);
  validateDistributorEmployees(distributorEmployees);
}

export const IS_distributorEmployee = ({ user }: { user: User }) => {
  return user.settings.persona === CLP.ActorType.DISTRIBUTOR;
}

export type DistributorEmployeeReference = Omit<DistributorEmployee, "__type" | "email"> & {
  __type: "DistributorEmployeeReference";
}

export const distributorEmployeeReference_FROM_user = ({ user }: { user: User }): DistributorEmployeeReference => {
  assert(IS_distributorEmployee({ user }), "User must be a distributor employee");
  return {
    __type: "DistributorEmployeeReference",
    id:     user.id as Id as DistributorEmployeeId,
    name:   user.name,
  };
}

// #endregion DistributorEmployee

// #region Employee

export type Employee = BbcEmployee | DistributorEmployee

export function isBbcEmployee(employee: Employee): employee is BbcEmployee {
  return employee.__type === "BbcEmployee";
}

export function isDistributorEmployee(employee: Employee): employee is DistributorEmployee {
  return employee.__type === "DistributorEmployee";
}

// #endregion Employee

// #region Product

export type Product = {
  __type:                        "Product";
  id:                            ProductId;
  brandFamily:                   string;
  brandStyle:                    string;
  beverageType:                  CLM.BeverageType;
  packaging_container:           CLM.Container;
  packaging_skinny:              boolean;
  packaging_airline:             boolean;
  packaging_single:              boolean;
  packaging_ptep:                boolean;
  packaging_type:                string;
  sku6:                          string;
  flaggedBySapDeletionIndicator: boolean;
  featured:                      boolean;
  archived:                      boolean;
  data?:                         string;
  sysInfo:                       SYS.Info;
}

export type RawProduct = Omit<Product, "id" | "beverageType" | "packaging_container" | "sysInfo"> & {
  id:                     string;
  beverageType:           string;
  packaging_container:    string;
  sysInfo_createdAt:      string;
  sysInfo_createdBy_id:   string;
  sysInfo_createdBy_name: string;
  sysInfo_updatedAt:      string;
  sysInfo_updatedBy_id:   string;
  sysInfo_updatedBy_name: string;
};

export const toRawProduct = (_: Product): RawProduct => {
  return {
    ..._,
    id:                     _.id.asInt.toString(),
    beverageType:           _.beverageType.identifier,
    packaging_container:    _.packaging_container.identifier,
    sysInfo_createdAt:      _.sysInfo.created.at.toISOString(),
    sysInfo_createdBy_id:   _.sysInfo.created.by.id.asInt.toString(),
    sysInfo_createdBy_name: _.sysInfo.created.by.name,
    sysInfo_updatedAt:      _.sysInfo.updated.at.toISOString(),
    sysInfo_updatedBy_id:   _.sysInfo.updated.by.id.asInt.toString(),
    sysInfo_updatedBy_name: _.sysInfo.updated.by.name,
    sysInfo:                undefined,
} as RawProduct};

export const fromRawProduct = (_: RawProduct): Product => {
  return {
    ..._,
    id:                  Id.from(BigInt(_.id)) as ProductId,
    beverageType:        CLM.beverageType(_.beverageType),
    packaging_container: CLM.container(_.packaging_container),
    sysInfo:             SYS.fromRawSysInfo(_),
} as Product};

export const productValidator = validator((product: Product) => {
  const { FOR } = objectValidator(product);
  return ({
    a1: FOR(_ => _.id                           ).ALWAYS_IS_DEFINED,
    a2: FOR(_ => _.brandFamily                  ).ALWAYS_IS_DEFINED,
    a3: FOR(_ => _.brandStyle                   ).ALWAYS_IS_DEFINED,
    a4: FOR(_ => _.beverageType                 ).ALWAYS_IS_DEFINED,
    a5: FOR(_ => _.packaging_container          ).ALWAYS_IS_DEFINED,
    a6: FOR(_ => _.packaging_skinny             ).ALWAYS_IS_DEFINED,
    a7: FOR(_ => _.packaging_airline            ).ALWAYS_IS_DEFINED,
    a8: FOR(_ => _.packaging_single             ).ALWAYS_IS_DEFINED,
    a9: FOR(_ => _.packaging_ptep               ).ALWAYS_IS_DEFINED,
    a10:FOR(_ => _.packaging_type               ).ALWAYS_IS_DEFINED,
    a11:FOR(_ => _.sku6                         ).ALWAYS_IS_DEFINED,
    a12:FOR(_ => _.flaggedBySapDeletionIndicator).ALWAYS_IS_DEFINED,
    a13:FOR(_ => _.featured                     ).ALWAYS_IS_DEFINED,
    a14:FOR(_ => _.archived                     ).ALWAYS_IS_DEFINED,
  });
});

export const productErrors = (_: Product) => productValidator.validationErrors(_);

export const isValidProduct = (_: Product) => productValidator.isValid(_);

export const validateProducts = (_: Product[]) => {
  const validationErrors = _
    .filter(_ => !isValidProduct(_))
    .map(_ => ({ product: _, errors: productErrors(_) }));

  assert(
    validationErrors.length === 0,
    `Errors found in defined Products with following error codes. ${JSON.stringify(validationErrors, stringifyReplacer, 2)}`
  );
}

export const validateRawProducts = (rawProducts: RawProduct[]) => {
  const products = rawProducts.map(fromRawProduct);
  validateProducts(products);
}

export type ProductReference = {
  id:           ProductId;
  beverageType: CLM.BeverageType;
};
export type RawProductReference = Omit<ProductReference, "id" | "beverageType"> & {
  id:           string;
  beverageType: string;
}
export const toRawProductReference = (_: ProductReference): RawProductReference => {
  return {
    id:           _.id.asInt.toString(),
    beverageType: _.beverageType.identifier,
} as RawProductReference}
export const fromRawProductReference = (_: RawProductReference): ProductReference => {
  return {
    id:           Id.from(BigInt(_.id)) as ProductId,
    beverageType: CLM.beverageType(_.beverageType),
} as ProductReference};

export const productReference = (id: string, beverageType: string): ProductReference => {
  return fromRawProductReference({
    id,
    beverageType,
  });
}

// #endregion Product

// #region ProductLineItem

export type ProductLineItem = {
  id?:               ProductLineItemId;
  productId:         ProductId;
  claimId:           ClaimId;
  reason:            CLM.DestructionReason;
  quantity?:         number;
  creditPercentage?: number;
}
export type RawProductLineItem = Omit<ProductLineItem, "id" | "productId" | "claimId" | "reason"> & {
  id?:                string;
  productId:         string;
  claimId:           string;
  reason:            string;
}
export const toRawProductLineItem = (_: ProductLineItem): RawProductLineItem => {
  return {
    ..._,
    id:                _.id?.asInt.toString(),
    productId:         _.productId.asInt.toString(),
    claimId:           _.claimId.asInt.toString(),
    reason:            _.reason.identifier,
} as RawProductLineItem}
export const fromRawProductLineItem = (_: RawProductLineItem): ProductLineItem => {
  return {
    ..._,
    id:                _.id ? Id.from(BigInt(_.id)) as ProductLineItemId : undefined,
    productId:         Id.from(BigInt(_.productId)) as ProductId,
    claimId:           Id.from(BigInt(_.claimId)) as ClaimId,
    reason:            CLM.destructionReason(_.reason),
} as ProductLineItem}

// #endregion ProductLineItem

}

// #region Validation Helpers - Mudroom

// TODO: Cleanup and Move to validations

type Predicate<T> = (value: T) => boolean;
type Extractor<TObject, TValue> = (_: TObject) => TValue;

type Provider<T> = T | (() => T);
type Condition<T> = Provider<boolean> | Predicate<T>;

const condition_isPredicate = <T>(_: Condition<T>): _ is Predicate<T>      => _ instanceof Function && _.length === 1;
const condition_isProvider  = <T>(_: Condition<T>): _ is Provider<boolean> => !condition_isPredicate(_);

const resolveProvider  = <T>(provider:  Provider<T>): T => {
  assert(!(provider instanceof Function) || provider.length === 0, "Provider function must not have any arguments.");

  return provider instanceof Function ? provider() : provider;
}

const resolveCondition = <T>(value: Provider<T>) => (condition: Condition<T>): boolean => {
  assert(condition !== undefined, "Condition is required.");
  assert(!(condition instanceof Function) || condition.length <= 1, "Condition function must have at most one argument.");

  return condition_isPredicate(condition) ? condition(resolveProvider(value)) : resolveProvider(condition);
}

const objectValidator = <TObject>(object: Provider<TObject>) => {
  const object_ = resolveProvider(object);
  return ({
    OBJ: object_,
    NOW: new Date(),
    FOR: <TValue>(value: Extractor<TObject, TValue>) => {
      const value_   = value(object_);
      const isTrue = resolveCondition(value_);
      const isTrue_WHEN_defined = isDefined(value_) ? resolveCondition(value_ as NonNullable<TValue>) : () => unexpectedCodePath("Value is not defined.");
      return ({
        ALWAYS_IS_DEFINED:                                                 isTrue(isDefined),
        ALWAYS:            (condition: Condition<TValue>             ) =>  isTrue(condition),
        WHEN_DEFINED_THEN: (condition: Condition<NonNullable<TValue>>) => !isTrue(isDefined) || isTrue_WHEN_defined(condition),
        WHEN:              (condition: boolean | Predicate<TValue>) => ({
          THEN: (_: boolean | (() => boolean)) => condition ? _ instanceof Function ? _() : _ : false
        }),
        IS_NON_EMPTY_ARRAY: () => Array.isArray(value_) && value_.length > 0,
      });
    },
  });
};

const arrayValidator = <TItem>(array: Provider<TItem[]>, ...keys: Extractor<TItem, unknown>[]) => {
  const array_ = resolveProvider(array);
  assert(keys.length > 0, "arrayValidator requires at least one key extractor.");

  const inSameGroup = (a: TItem, b: TItem) => keys.every(key => key(a) === key(b));

  return ({
    KEYS_ARE_UNIQUE:  () => array_.every((item, index) => !array_.slice(0, index).some(other => inSameGroup(other, item))),
    FOR: <TValue>(value: Extractor<TItem, TValue>) => {
      const elementsOf  = (item: TItem) => {
        const elements = value(item);
        return Array.isArray(elements) ? elements : [];
      };
      const seenInGroup = (item: TItem, index: number) => array_.slice(0, index).filter(other => inSameGroup(other, item)).flatMap(elementsOf);
      return ({
        IS_UNIQUE:  () => array_.every((_, index) =>
          !array_.slice(0, index).some(other => inSameGroup(other, _) && value(other) === value(_))
        ),
        HAS_UNIQUE: () => array_.every((item, index) => {
          const seen = seenInGroup(item, index);
          return elementsOf(item).every((element, i, elements) =>
            !seen.includes(element) && !elements.slice(0, i).includes(element)
          );
        }),
      });
    },
  });
}

const isText   = (_?: string)              => isDefined(_) && typeof _ === "string";
const isNumber = (_?: number): _ is number => isDefined(_) && typeof _ === "number" && !isNaN(_);

const isNotEmpty     = (_?: string) => isDefined(_) && isText(_) && _ !== "";
const isTrimmed      = (_?: string) => isDefined(_) && isText(_) && _!.trim() === _;
const isStandardText = (_?: string) => isDefined(_) && isText(_) && isNotEmpty(_) && isTrimmed(_);

const isIncludedIn = <TValue>(values: TValue[]) => (_: TValue) => values.includes(_);

const isDecimal            = (decimalPlaces: number) => (_?: number): _ is number => isNumber(_) && numberOfDecimalPlaces(_) <= decimalPlaces;
const isPositiveDecimal    = (decimalPlaces: number) => (_?: number) => isDecimal(decimalPlaces)(_) && _! > 0;
const isNonNegativeDecimal = (decimalPlaces: number) => (_?: number) => isDecimal(decimalPlaces)(_) && _! >= 0;

export const numberOfDecimalPlaces = function (_: number) {
  if (Math.floor(_.valueOf()) === _.valueOf()) return 0;

  var str = _.toString();

  assert(str.indexOf("e") <  0, "Number must be representable in non-exponential notation.");
  assert(str.indexOf(".") >= 0, "Non-integer number must have a decimal point.");

  return str.split(".")[1].length || 0;
}

export const isMoney            = isDecimal(2);
export const isPositiveMoney    = isPositiveDecimal(2);
export const isNonNegativeMoney = isNonNegativeDecimal(2);

const isBefore          = (date: Date            ) => (_: Date) =>                    _ < date;
const isBefore_OPTIONAL = (date: Date | undefined) => (_: Date) => isDefined(date) && _ < date;
const isAfter           = (date: Date            ) => (_: Date) =>                    _ > date;
const isAfter_OPTIONAL  = (date: Date | undefined) => (_: Date) => isDefined(date) && _ > date;

const isNotBefore          = (date: Date            ) => (_: Date) =>                    _ >= date;
const isNotBefore_OPTIONAL = (date: Date | undefined) => (_: Date) => isDefined(date) && _ >= date;
const isNotAfter           = (date: Date            ) => (_: Date) =>                    _ <= date;
const isNotAfter_OPTIONAL  = (date: Date | undefined) => (_: Date) => isDefined(date) && _ <= date;

const isFileName = (_: string) => FILE_NAME_FORMAT.test(_);

const isId = (_: bigint) => _ > 0n;

// #endregion
